import { createInvoicePaymentCheckout, resumeInvoicePaymentCheckout } from './invoice-payment-checkout';
import type { TypedSupabaseClient } from '../client';
import type {
  Json,
  TablesUpdate,
} from '../../../supabase-client/src/database.types';
import { orContainsAnyColumn, orEqualsAnyColumn } from './postgrest-search';
import {
  chunkIds,
  compareDocumentsNewestFirst,
  completeCappedRows,
  fetchAllRows,
  withStableOrder,
} from './fetch-all-rows';
import type { DocumentBalanceRow } from '../../../interfaces/src';
import type {
  ISalesRepository,
  SalesDocument,
  SalesDocumentLine,
  SalesDocumentFilters,
  SalesDocumentSummary,
  DocumentWindowSignature,
  SalesDocType,
  ClientArticleDiscount,
  ClientArticleDiscountQuery,
  CreateSalesDocumentInput,
  CreateSalesCreditApplicationInput,
  SalesCreditApplication,
  SalesCreditApplicationLine,
  SalesCreditCompanions,
  SalesCreditCompanionReference,
  SalesCreditApplicationStatusSnapshot,
  CancelSalesCreditNoteInput,
  SalesCreditCancellation,
  UpdateSalesDocumentInput,
  DocumentNumberConflictDetails,
  ConflictingInvoiceSummary,
} from '../../../interfaces/src';
import {
  DocumentNumberConflictError,
  ParentAlreadyInvoicedError,
  ConcurrentEditError,
  OrderDeletionBlockedError,
  CreditNoteApplicationConflictError,
  CreditNoteSourceInvoicedError,
  CreditNoteQuantityUnavailableError,
  CreditNoteStockHistoryUnavailableError,
  SalesCreditCompanionsUnavailableError,
  SalesDocumentCreditLockedError,
  CreditNoteCancellationConsumedError,
  CreditNoteCancellationConflictError,
  AtomicResourceDeletedError,
  ConfirmedMutationRejectedError,
} from '../../../interfaces/src';
import {
  findParentInvoicingClashes,
  clashingInvoices,
  isConcurrentlyEdited,
} from '@erp/domain/sync';
import {
  findNextNumber,
  parseDocumentNumber,
  buildDocumentNumber,
} from '@erp/domain/numbering';
import {
  computePaymentStatus,
  settlementToleranceFor,
  planPaymentCarryOver,
  type CarryOverAllocation,
} from '@erp/domain/payments';
import {
  netUnitCostHt,
  calculateProCommission,
  computeProAmountDue,
  canDeleteSalesDocument,
  deliveryRemovalBlocker,
  DEFAULT_SALES_CHANNEL,
  inheritSalesChannel,
  inheritSalesOriginStore,
  buildSalesDeliveryStockEffects,
  canMirrorStorefrontDocumentLines,
  salesDeliveryRequiresWarehouse,
  maxRequestedDiscountPercent,
  type DeliveryRemovalBlocker,
  type SalesBenefitLineInput,
} from '@erp/domain/sales';
import { storefrontOrderStatusRemovesLinkedDelivery } from '@erp/domain/storefront';
import {
  SALES_DOC_NUMBERING,
  SALES_DOC_DEFAULT_PADDING,
} from './sales-numbering';
import { mirrorStorefrontStockForArticles } from './stock-repository';
// Recalcul des compteurs du client en ligne après suppression d'une de ses
// commandes. Sens unique : storefront-orders-repository n'importe pas ce
// fichier, donc pas de cycle.
import { SupabaseOnlineCustomerRepository } from './storefront-orders-repository';
import { resolveStorageImageUrl } from './storage-image';
import { sortByLineNumber } from './document-line-order';

// `image_url, images` ne sont joints QUE sur ce select — celui du document
// unitaire (`getDocumentById`). Les lectures de listes et de documents enfants
// gardent l'embed à 4 colonnes : elles n'affichent aucune vignette et fanent sur
// des centaines de parents à la fois.
// PostgREST uses the relationship name as the response key. The domain mapper
// deliberately keeps the stable `sales_document_lines` shape used by both
// backend adapters, so every authorized embed must alias the relationship.
// Without the alias the server returns valid rows under
// `authorized_sales_document_lines`, which the mapper correctly ignores as an
// unknown transport detail and the UI silently renders an empty document.
const SALES_DOCUMENT_LINES_EMBED =
  'sales_document_lines:authorized_sales_document_lines(*, article:authorized_articles(code, name, is_invoiceable, unit))';
const SALES_DOCUMENT_LINES_WITH_IMAGES_EMBED =
  'sales_document_lines:authorized_sales_document_lines(*, article:authorized_articles(code, name, is_invoiceable, unit, image_url, images))';
const SALES_DOCUMENT_SELECT = `*, ${SALES_DOCUMENT_LINES_WITH_IMAGES_EMBED}`;
// Mutation planning needs persisted line values, not catalog display fields or
// customer enrichment. Keep the same authorized, column-masked line relation.
const SALES_DOCUMENT_WRITE_SNAPSHOT_SELECT =
  '*, sales_document_lines:authorized_sales_document_lines(*)';

const SALES_DOCUMENT_LIST_SELECT = '*';

/** Colonnes de `SalesDocumentSummary`. Toute colonne ajoutée au type DOIT être
 *  ajoutée ici — sinon le champ arrive `undefined` et fausse une somme. */
export const SALES_DOCUMENT_SUMMARY_SELECT =
  'id, number, doc_type, doc_date, client_id, origin_store_id, channel, is_invoiced, parent_doc_id, parent_doc_ids, total_ttc, created_at';

const PAYMENT_ALLOCATION_CHUNK_SIZE = 500;
// `.in()` filters travel in the GET query string: past a few hundred UUIDs the
// URL blows the gateway's ~32KB cap, so every id fan-out must be chunked.
const IN_FILTER_CHUNK_SIZE = 500;
// La filiation multi-parents interroge deux colonnes d'un coup (`in` + `ov`)
// dans un même `or` : chaque id paraît DEUX fois dans l'URL, d'où un lot plus
// petit que IN_FILTER_CHUNK_SIZE pour rester sous le même plafond.
const PARENT_FILTER_CHUNK_SIZE = 200;
// Contrat SQL de list_sales_credit_companions : 100 ids maximum par appel.
const CREDIT_COMPANION_CHUNK_SIZE = 100;
// PostgREST caps responses at max_rows (1000) — page past it explicitly.
const ROW_PAGE_SIZE = 1000;
// Types de documents qui témoignent d'une remise réellement consentie à un
// client. L'avoir est exclu : il reprend les remises du document corrigé, ce
// n'est pas une négociation de plus.
const DISCOUNT_HISTORY_DOC_TYPES = [
  'quote',
  'order',
  'delivery',
  'invoice',
] as const;
const AUTHORIZED_SALES_DOCUMENTS =
  'authorized_sales_documents' as 'sales_documents';
const AUTHORIZED_SALES_DOCUMENT_LINES =
  'authorized_sales_document_lines' as 'sales_document_lines';
const AUTHORIZED_CLIENTS = 'authorized_clients' as 'clients';
const AUTHORIZED_ARTICLES = 'authorized_articles' as 'articles';
const AUTHORIZED_STOCK_MOVEMENTS =
  'authorized_stock_movements' as 'stock_movements';
const SALES_DOCUMENTS_WRITE = 'sales_documents' as const;
const SALES_DOCUMENT_LINES_WRITE = 'sales_document_lines' as const;

/**
 * Compatibilite temporaire: les deux tables du ledger d'avoirs vivent dans la
 * migration de ce lot mais pas encore dans `database.types.ts`. Centraliser le
 * cast ici evite de rendre tout le repository non type. A supprimer au prochain
 * `supabase gen types typescript`.
 */
type PendingSalesCreditSchemaClient = {
  // `any` est volontairement borne au builder PostgREST de relations absentes
  // des types generes; les donnees retournees sont remappees en unknown.
  from(
    relation: 'sales_credit_applications' | 'sales_credit_application_lines',
  ): any;
};

function pendingSalesCreditSchema(
  client: TypedSupabaseClient,
): PendingSalesCreditSchemaClient {
  return client as unknown as PendingSalesCreditSchemaClient;
}

interface AtomicInvoicePaymentTransfer extends Record<
  string,
  Json | undefined
> {
  allocation_id: string;
  payment_id: string;
  source_document_id: string;
  moved_amount: number;
  remaining_on_source: number;
  target_allocation_id: string | null;
}

function normalizeCreditNoteAtomicError(error: unknown): unknown {
  const message =
    error && typeof error === 'object' && 'message' in error
      ? String((error as { message?: unknown }).message ?? '')
      : String(error ?? '');
  if (
    /sales_credit_source_quantity_exceeded|credit_note_return_quantity_exceeded/i.test(
      message,
    )
  ) {
    return new CreditNoteQuantityUnavailableError();
  }
  if (
    /credit_note_original_warehouse_unresolved|credit_note_stock_return_plan_incomplete|legacy_credit_stock_return_requires_reconciliation|legacy_credit_source_requires_reconciliation|credit_note_canonical_source_unresolved/i.test(
      message,
    )
  ) {
    return new CreditNoteStockHistoryUnavailableError();
  }
  return error;
}

function normalizeInvoiceAtomicError(error: unknown): unknown {
  const record =
    error && typeof error === 'object'
      ? (error as { code?: string; message?: unknown })
      : null;
  const message = String(record?.message ?? error ?? '');
  if (
    /invoice_delivery_credit_application_plan_incomplete|sales_credit_companions_unavailable/i.test(
      message,
    )
  ) {
    return new SalesCreditCompanionsUnavailableError();
  }
  if (record?.code === '23P01' && /credit/i.test(message)) {
    return new CreditNoteApplicationConflictError(null);
  }
  return error;
}

function normalizeCreditCancellationError(error: unknown): unknown {
  const message =
    error && typeof error === 'object' && 'message' in error
      ? String((error as { message?: unknown }).message ?? '')
      : String(error ?? '');
  if (/sales_credit_cancellation_consumed/i.test(message)) {
    return new CreditNoteCancellationConsumedError();
  }
  if (/sales_credit_cancellation_conflict/i.test(message)) {
    return new CreditNoteCancellationConflictError();
  }
  return error;
}

export class SupabaseSalesRepository implements ISalesRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  private async authorizeDiscount(input: {
    docType: SalesDocType;
    action: 'create' | 'edit';
    documentId?: string;
    clientRequestId?: string;
    requestedPercent: number;
    supervisorToken?: string;
  }): Promise<void> {
    const { data, error } = await this.supabase.rpc(
      'authorize_sales_discount' as never,
      {
        p_doc_type: input.docType,
        p_action: input.action,
        p_document_id: input.documentId ?? null,
        p_client_request_id: input.clientRequestId ?? null,
        p_requested_percent: input.requestedPercent,
        p_supervisor_token: input.supervisorToken ?? null,
      } as never,
    );
    if (error) throw error;
    const decision = data as Record<string, unknown> | null;
    if (decision?.['allowed'] !== true) {
      throw new Error(
        `discount_limit_exceeded:${Number(decision?.['roleLimit'] ?? 0)}`,
      );
    }
  }

  async listDocuments(
    filters?: SalesDocumentFilters,
  ): Promise<SalesDocument[]> {
    if (filters?.lineArticleId) return this.listDocumentsByLineArticle(filters);
    if (filters?.paymentStatus)
      return this.listDocumentsByPaymentStatus(filters);

    const buildQuery = () => {
      let query = (this.supabase as any)
        .from(AUTHORIZED_SALES_DOCUMENTS)
        .select(SALES_DOCUMENT_LIST_SELECT);
      query = applySalesDocumentFilters(query, filters);
      return applySalesDocumentSort(query, filters?.sort);
    };
    let query = buildQuery();
    const hasExplicitLimit = Boolean(filters?.limit && filters.limit > 0);
    if (filters?.limit && filters.limit > 0) {
      const offset = Math.max(0, filters.offset ?? 0);
      query = query.range(offset, offset + filters.limit - 1);
    }

    const { data, error } = await query;
    if (error) throw error;
    const firstRows = (data ?? []) as Array<Record<string, unknown>>;
    const rows = hasExplicitLimit
      ? firstRows
      : await completeCappedRows(firstRows, (from, to) =>
          buildQuery().range(from, to),
        );
    const docs = rows.map((row) => mapSalesDocument(row));
    return this.enrichDocumentsBatched(
      await this.attachLinesIfRequested(docs, filters),
      {
        includeCreditApplications: filters?.includeLines === true,
        includeCustomers: filters?.includeCustomerEnrichment !== false,
      },
    );
  }

  /**
   * Liste projetée pour le tableau de bord — voir `SalesDocumentSummary`.
   *
   * Deux différences avec `listDocuments`, et elles comptent toutes les deux :
   * la projection est explicite (10 colonnes au lieu de `*`), et on NE passe
   * PAS par `enrichDocumentsBatched`, qui relit `clients`/`online_customers`/
   * `pro_customers` de chaque page — un enrichissement dont aucun widget de
   * somme ne lit quoi que ce soit.
   *
   * Même tri et même pagination que `listDocuments` : `withStableOrder` rend
   * l'ordre TOTAL, sans quoi paginer 24 mois de documents ferait apparaître ou
   * disparaître des lignes d'une page à l'autre — et fausserait un total.
   */
  async listDocumentSummaries(
    filters?: SalesDocumentFilters,
  ): Promise<SalesDocumentSummary[]> {
    if (filters?.paymentStatus || filters?.lineArticleId) {
      throw new Error(
        'listDocumentSummaries: paymentStatus/lineArticleId need the resolution paths of listDocuments',
      );
    }
    const buildQuery = () => {
      let query = (this.supabase as any)
        .from(AUTHORIZED_SALES_DOCUMENTS)
        .select(SALES_DOCUMENT_SUMMARY_SELECT);
      query = applySalesDocumentFilters(query, filters);
      return applySalesDocumentSort(query, filters?.sort);
    };
    let query = buildQuery();
    const hasExplicitLimit = Boolean(filters?.limit && filters.limit > 0);
    if (filters?.limit && filters.limit > 0) {
      const offset = Math.max(0, filters.offset ?? 0);
      query = query.range(offset, offset + filters.limit - 1);
    }
    const { data, error } = await query;
    if (error) throw error;
    const firstRows = (data ?? []) as Array<Record<string, unknown>>;
    const rows = hasExplicitLimit
      ? firstRows
      : await completeCappedRows(firstRows, (from, to) =>
          buildQuery().range(from, to),
        );
    return rows.map(mapSalesDocumentSummary);
  }

  /**
   * Empreinte de l'ensemble — voir `DocumentWindowSignature`.
   *
   * UNE requête pour les deux mesures : `count: 'exact'` fait voyager le nombre
   * dans l'en-tête `Content-Range`, et `order(updated_at desc).limit(1)` rend la
   * ligne la plus récemment touchée. Le corps fait une ligne d'une colonne.
   *
   * ⚠ Volontairement PAS `head: true` : on a besoin du corps pour lire
   * `updated_at`. Le compte seul ne suffit pas — une suppression suivie d'une
   * création dans la même minute le laisserait identique.
   */
  async listDocumentsSignature(
    filters?: SalesDocumentFilters,
  ): Promise<DocumentWindowSignature> {
    let query = (this.supabase as any)
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select('updated_at', { count: 'exact' });
    query = applySalesDocumentFilters(query, filters);
    query = query
      .order('updated_at', { ascending: false, nullsFirst: false })
      .limit(1);
    const { data, count, error } = await query;
    if (error) throw error;
    const rows = (data ?? []) as Array<Record<string, unknown>>;
    return {
      count: count ?? 0,
      maxUpdatedAt: (rows[0]?.['updated_at'] as string | undefined) ?? null,
    };
  }

  async countDocuments(filters?: SalesDocumentFilters): Promise<number> {
    if (filters?.lineArticleId)
      return this.countDocumentsByLineArticle(filters);
    if (filters?.paymentStatus)
      return (await this.getPaymentStatusFilteredDocumentIds(filters)).length;

    let query = (this.supabase as any)
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select('id', { count: 'exact', head: true });
    query = applySalesDocumentFilters(query, filters);
    const { count, error } = await query;
    if (error) throw error;
    return count ?? 0;
  }

  /**
   * Documents containing at least one line for `filters.lineArticleId`.
   *
   * Resolved lines-first: the article index (`idx_sales_lines_article`) gives
   * the document ids, then the documents themselves come back in PARALLEL
   * chunks. Walking the documents instead — with an `!inner` embed filtered on
   * the article — reads more direct but is far slower: PostgREST compiles the
   * embed into a LATERAL join evaluated document by document, so the planner
   * scans the whole table (measured on the busiest article of the biggest
   * tenant: 2.3 s, against ~450 ms for the index scan on the lines).
   *
   * ⚠ `includeLines` is expensive HERE and nowhere else: it re-reads every line
   * of every matching document, so an article sold in 1 262 documents pulls
   * ~8 000 lines of which only 1 561 mention it. The article page asks for
   * `listLinesByArticle` instead. `paymentStatus` is not combinable with this
   * path (no caller does).
   */
  private async listDocumentsByLineArticle(
    filters: SalesDocumentFilters,
  ): Promise<SalesDocument[]> {
    const docIds = await this.getDocumentIdsForLineArticle(
      filters.lineArticleId!,
    );
    if (docIds.length === 0) return [];

    const pages = await Promise.all(
      chunkIds(docIds, IN_FILTER_CHUNK_SIZE).map((chunk) =>
        applySalesDocumentFilters(
          (this.supabase as any)
            .from(AUTHORIZED_SALES_DOCUMENTS)
            .select(SALES_DOCUMENT_LIST_SELECT)
            .in('id', chunk),
          filters,
        ),
      ),
    );

    const docs: SalesDocument[] = [];
    for (const { data, error } of pages as Array<{
      data: unknown;
      error: unknown;
    }>) {
      if (error) throw error;
      for (const row of (data ?? []) as Array<Record<string, unknown>>)
        docs.push(mapSalesDocument(row));
    }

    // Chunked fetches lose server ordering — restore the default newest-first.
    docs.sort(compareDocumentsNewestFirst);
    const offset = Math.max(0, filters.offset ?? 0);
    const page =
      filters.limit && filters.limit > 0
        ? docs.slice(offset, offset + filters.limit)
        : docs.slice(offset);
    return this.enrichDocumentsBatched(
      await this.attachLinesIfRequested(page, filters),
      {
        includeCreditApplications: filters.includeLines === true,
        includeCustomers: filters.includeCustomerEnrichment !== false,
      },
    );
  }

  private async countDocumentsByLineArticle(
    filters: SalesDocumentFilters,
  ): Promise<number> {
    const docIds = await this.getDocumentIdsForLineArticle(
      filters.lineArticleId!,
    );
    if (docIds.length === 0) return 0;

    const counts = await Promise.all(
      chunkIds(docIds, IN_FILTER_CHUNK_SIZE).map((chunk) =>
        applySalesDocumentFilters(
          (this.supabase as any)
            .from(AUTHORIZED_SALES_DOCUMENTS)
            .select('id', { count: 'exact', head: true })
            .in('id', chunk),
          filters,
        ),
      ),
    );

    let total = 0;
    for (const { count, error } of counts as Array<{
      count: number | null;
      error: unknown;
    }>) {
      if (error) throw error;
      total += count ?? 0;
    }
    return total;
  }

  private async getDocumentIdsForLineArticle(
    articleId: string,
  ): Promise<string[]> {
    const rows = await fetchAllRows<{ document_id: string }>((from, to) =>
      (this.supabase as any)
        .from(AUTHORIZED_SALES_DOCUMENT_LINES)
        .select('document_id')
        .eq('article_id', articleId)
        .order('id', { ascending: true })
        .range(from, to),
    );
    return Array.from(new Set(rows.map((row) => row.document_id)));
  }

  /** Toutes les lignes de cet article — voir `ISalesRepository.listLinesByArticle`. */
  async listLinesByArticle(articleId: string): Promise<SalesDocumentLine[]> {
    if (!articleId) return [];
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
      (this.supabase as any)
        .from(AUTHORIZED_SALES_DOCUMENT_LINES)
        .select(
          '*, article:authorized_articles(code, name, is_invoiceable, unit)',
        )
        .eq('article_id', articleId)
        // `id` en dernier rend l'ordre TOTAL : sans lui, deux lignes de même
        // `line_number` peuvent changer de place d'une page à l'autre, donc
        // apparaître deux fois ou pas du tout (voir withStableOrder).
        .order('line_number', { ascending: true })
        .order('id', { ascending: true })
        .range(from, to),
    );
    return rows.map((row) => mapSalesLine(row));
  }

  private async listDocumentsByPaymentStatus(
    filters: SalesDocumentFilters,
  ): Promise<SalesDocument[]> {
    const matchingIds = await this.getPaymentStatusFilteredDocumentIds(filters);
    const offset = Math.max(0, filters.offset ?? 0);
    const pageIds =
      filters.limit && filters.limit > 0
        ? matchingIds.slice(offset, offset + filters.limit)
        : matchingIds.slice(offset);
    if (pageIds.length === 0) return [];

    const { data, error } = await (this.supabase as any)
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select(SALES_DOCUMENT_LIST_SELECT)
      .in('id', pageIds);
    if (error) throw error;

    const rank = new Map(pageIds.map((id, index) => [id, index]));
    const docs = ((data ?? []) as Array<Record<string, unknown>>)
      .map((row) => mapSalesDocument(row))
      .sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0));
    return this.enrichDocumentsBatched(
      await this.attachLinesIfRequested(docs, filters),
      {
        includeCreditApplications: filters.includeLines === true,
        includeCustomers: filters.includeCustomerEnrichment !== false,
      },
    );
  }

  private async getPaymentStatusFilteredDocumentIds(
    filters: SalesDocumentFilters,
  ): Promise<string[]> {
    const requestedStatus = filters.paymentStatus;
    if (!requestedStatus) return [];

    let query = (this.supabase as any)
      .from(AUTHORIZED_SALES_DOCUMENTS)
      // `stamp_amount` sert de tolérance de règlement : sans lui, le filtre
      // « Payé » du serveur écarterait des factures que la liste affiche payées.
      .select('id, doc_type, total_ttc, net_to_pay, stamp_amount');
    query = applySalesDocumentFilters(query, filters);
    query = applySalesDocumentSort(query, filters.sort);

    const { data, error } = await query;
    if (error) throw error;

    const candidates = (data ?? []) as Array<Record<string, unknown>>;
    if (candidates.length === 0) return [];

    const allocatedByDocumentId =
      await this.getPaymentAllocationSums(candidates);
    return candidates
      .filter((doc) => {
        const id = doc['id'] as string;
        const totalDue = Number(doc['net_to_pay'] ?? doc['total_ttc'] ?? 0);
        const tolerance = settlementToleranceFor({
          stampAmount: Number(doc['stamp_amount'] ?? 0),
        });
        return (
          computePaymentStatus(
            totalDue,
            allocatedByDocumentId.get(id) ?? 0,
            tolerance,
          ) === requestedStatus
        );
      })
      .map((doc) => doc['id'] as string);
  }

  private async getPaymentAllocationSums(
    documents: Array<Record<string, unknown>>,
  ): Promise<Map<string, number>> {
    const sums = new Map<string, number>();
    const documentTypes = Array.from(
      new Set(documents.map((doc) => doc['doc_type']).filter(Boolean)),
    ) as string[];
    const ids = documents.map((doc) => doc['id']).filter(Boolean) as string[];

    for (let i = 0; i < ids.length; i += PAYMENT_ALLOCATION_CHUNK_SIZE) {
      const chunk = ids.slice(i, i + PAYMENT_ALLOCATION_CHUNK_SIZE);
      let query = (this.supabase as any)
        .from('payment_allocations')
        .select('document_id, document_type, allocated_amount')
        .in('document_id', chunk);
      if (documentTypes.length > 0)
        query = query.in('document_type', documentTypes);

      const { data, error } = await query;
      if (error) throw error;

      for (const allocation of (data ?? []) as Array<Record<string, unknown>>) {
        const documentId = allocation['document_id'] as string;
        const amount = Number(allocation['allocated_amount'] ?? 0);
        sums.set(documentId, (sums.get(documentId) ?? 0) + amount);
      }
    }

    return sums;
  }

  private async attachLinesIfRequested(
    docs: SalesDocument[],
    filters?: SalesDocumentFilters,
  ): Promise<SalesDocument[]> {
    if (!filters?.includeLines || docs.length === 0) return docs;

    const ids = docs.map((doc) => doc.id);
    const linesByDocId = new Map<string, SalesDocumentLine[]>();
    for (let i = 0; i < ids.length; i += IN_FILTER_CHUNK_SIZE) {
      const chunk = ids.slice(i, i + IN_FILTER_CHUNK_SIZE);
      for (let from = 0; ; from += ROW_PAGE_SIZE) {
        const { data, error } = await (this.supabase as any)
          .from(AUTHORIZED_SALES_DOCUMENT_LINES)
          .select(
            '*, article:authorized_articles(code, name, is_invoiceable, unit)',
          )
          .in('document_id', chunk)
          // Secondary unique key keeps the pagination stable (line_number
          // repeats across documents).
          .order('line_number', { ascending: true })
          .order('id', { ascending: true })
          .range(from, from + ROW_PAGE_SIZE - 1);
        if (error) throw error;
        const rows = (data ?? []) as Array<Record<string, unknown>>;
        for (const row of rows) {
          const line = mapSalesLine(row);
          const lines = linesByDocId.get(line.documentId) ?? [];
          lines.push(line);
          linesByDocId.set(line.documentId, lines);
        }
        if (rows.length < ROW_PAGE_SIZE) break;
      }
    }

    return docs.map((doc) => ({
      ...doc,
      lines: linesByDocId.get(doc.id) ?? [],
    }));
  }

  /** Projection minimale factures / avoirs / BL NON facturés pour les soldes
   *  clients — 7 colonnes au lieu des lignes larges complètes, paginée au-delà
   *  du cap PostgREST.
   *
   *  Les BL non facturés en font partie : l'app autorise leur règlement direct
   *  et le portefeuille de la fiche client les compte comme créance. Sans eux,
   *  la liste des clients affichait « à jour » un client livré mais pas encore
   *  facturé — un chiffre différent de sa propre fiche. Le filtre est côté
   *  serveur : sur un tenant qui livre beaucoup, les BL facturés sont la
   *  majorité des pièces et n'ont rien à faire sur le fil. */
  async listBalanceRows(): Promise<DocumentBalanceRow[]> {
    type Row = {
      id: string;
      client_id: string;
      doc_type: 'invoice' | 'credit_note' | 'delivery';
      is_invoiced: boolean | null;
      total_ttc: number | null;
      net_to_pay: number | null;
      stamp_amount: number | null;
    };
    const rows = await fetchAllRows<Row>((from, to) =>
      (this.supabase as any)
        .from(AUTHORIZED_SALES_DOCUMENTS)
        .select('id, client_id, doc_type, is_invoiced, total_ttc, net_to_pay, stamp_amount')
        .or('doc_type.in.(invoice,credit_note),and(doc_type.eq.delivery,is_invoiced.eq.false)')
        .order('id', { ascending: true })
        .range(from, to),
    );
    const creditIds = rows
      .filter((row) => row.doc_type === 'credit_note')
      .map((row) => row.id);
    const cancelledCreditIds = new Set(
      creditIds.length === 0
        ? []
        : (await this.listCreditApplicationStatuses(creditIds))
            .filter((status) => status.status === 'cancelled')
            .map((status) => status.creditNoteId),
    );
    const invoiceAppliedByCredit = new Map<string, number>();
    for (const chunk of chunkIds(creditIds, IN_FILTER_CHUNK_SIZE)) {
      const { data, error } = await pendingSalesCreditSchema(this.supabase)
        .from('sales_credit_applications')
        .select('credit_note_id, applied_ttc, reversed_at')
        .in('credit_note_id', chunk);
      if (error) throw error;
      for (const application of (data ?? []) as Array<
        Record<string, unknown>
      >) {
        if (application['reversed_at']) continue;
        const creditId = application['credit_note_id'] as string;
        invoiceAppliedByCredit.set(
          creditId,
          Math.round(
            ((invoiceAppliedByCredit.get(creditId) ?? 0) +
              Number(application['applied_ttc'] ?? 0)) *
              1000,
          ) / 1000,
        );
      }
    }

    return rows
      .filter((r) => !cancelledCreditIds.has(r.id))
      .map((r) => ({
        id: r.id,
        thirdPartyId: r.client_id,
        docType: r.doc_type,
        isInvoiced: r.is_invoiced === true,
        totalTtc: Number(r.total_ttc) || 0,
        netToPay: Number(r.net_to_pay) || 0,
        stampAmount: Number(r.stamp_amount) || 0,
        invoiceAppliedTtc:
          r.doc_type === 'credit_note'
            ? (invoiceAppliedByCredit.get(r.id) ?? 0)
            : undefined,
      }));
  }

  async getDocumentById(
    id: string,
    allowedTypes?: readonly SalesDocType[],
  ): Promise<SalesDocument | null> {
    if (allowedTypes?.length === 0) return null;
    let query = (this.supabase as any)
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select(SALES_DOCUMENT_SELECT)
      .eq('id', id);
    if (allowedTypes) query = query.in('doc_type', allowedTypes);
    const { data, error } = await query.single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    const enriched = await this.enrichDocumentCustomer(
      mapSalesDocument(data, { supabase: this.supabase }),
    );
    return (await this.attachCreditApplications([enriched]))[0] ?? null;
  }

  async resolveOrCancelAtomicRequest(
    clientRequestId: string,
    requestKind:
      | 'sales_quote'
      | 'sales_order'
      | 'sales_delivery'
      | 'sales_invoice'
      | 'sales_credit_note'
      | 'pos_checkout',
  ): Promise<string | null> {
    const { data, error } = await this.supabase.rpc(
      'resolve_mobile_atomic_request',
      {
        p_client_request_id: clientRequestId,
        p_request_kind: requestKind,
        p_cancel_if_missing: true,
      },
    );
    if (error) {
      const databaseError = error as { code?: string; message?: string };
      if (
        databaseError.code === 'P0002' &&
        databaseError.message?.includes(
          'mobile_atomic_request_resource_deleted',
        )
      ) {
        throw new AtomicResourceDeletedError(error);
      }
      throw error;
    }
    return data ?? null;
  }

  async createDocument(
    input: CreateSalesDocumentInput,
  ): Promise<SalesDocument> {
    const requestId = input.clientRequestId ?? globalThis.crypto.randomUUID();
    if (input.invoicePayment) {
      if (input.docType !== 'invoice' || !input.clientRequestId || input.posCheckout || input.deliveryPayment || input.historicalImport) {
        throw new Error('invalid_invoice_payment_checkout');
      }
      const completed = await resumeInvoicePaymentCheckout(this.supabase, 'sales', input);
      if (completed) {
        const saved = await this.getDocumentById(completed);
        if (!saved) throw new Error('invoice_payment_checkout_resource_unavailable');
        return saved;
      }
    }

    if (
      input.posCheckout &&
      (input.docType !== 'delivery' || !input.clientRequestId)
    ) {
      throw new Error('invalid_pos_checkout_input');
    }
    if (
      input.deliveryPayment &&
      (input.docType !== 'delivery' ||
        !input.clientRequestId ||
        input.posCheckout !== undefined)
    ) {
      throw new Error('invalid_sales_delivery_payment_input');
    }
    const usesHistoricalImport = input.historicalImport === true;
    if (
      usesHistoricalImport &&
      ((input.docType !== 'invoice' && input.docType !== 'credit_note') ||
        !input.number?.trim() ||
        input.stockReturns?.length ||
        input.creditApplications?.length)
    ) {
      throw new Error('invalid_historical_sales_import');
    }

    // Préparer les lignes AVANT d'allouer le numéro. Pour un avoir avec
    // retour stock, leurs UUID sont référencés par le payload du RPC atomique.
    const netCosts =
      input.lines.length === 0
        ? []
        : input.preserveLineCosts
          ? input.lines.map((line) => {
              const provided = Number(line.purchasePrice);
              return Number.isFinite(provided) && provided > 0
                ? round3(provided)
                : 0;
            })
          : await resolveNetUnitCosts(this.supabase, input.lines);
    const preparedLines = input.lines.map((line, idx) => ({
      id: globalThis.crypto.randomUUID(),
      line_number: idx + 1,
      article_id: line.articleId,
      variant_id: line.variantId ?? null,
      variant_label: line.variantLabel ?? null,
      // Transmis dans p_lines pour (1) le garde d'effets de stock du BL
      // (create_sales_delivery_atomic attend quantité × facteur pièces) et
      // (2) l'INSERT de ligne du BL qui pose désormais unit_label/unit_factor
      // ATOMIQUEMENT (plus fiable que la RPC secondaire best-effort). Les autres
      // RPCs de création ignorent ces clés et s'appuient sur set_..._line_units.
      unit_factor: line.unitFactor ?? 1,
      unit_label: line.unitLabel ?? null,
      description: line.description,
      quantity: line.quantity,
      unit_price_ht: line.unitPriceHt,
      unit_price_ttc: line.unitPriceTtc ?? null,
      purchase_price: netCosts[idx] ?? 0,
      discount_pct: line.discountPct ?? 0,
      discount_mode: line.discountMode ?? 'pct',
      discount_amount: line.discountAmount ?? 0,
      vat_rate: line.vatRate,
      total_ht: line.totalHt ?? 0,
      total_ttc: line.totalTtc ?? 0,
      source_doc_id: line.sourceDocId ?? null,
      source_line_id: line.sourceLineId ?? null,
    }));
    if (input.stockReturns?.length && input.docType !== 'credit_note') {
      throw new Error('stock_returns_require_credit_note');
    }
    const creditLineIdBySourceLineId = new Map<string, string>();
    for (const line of preparedLines) {
      if (!line.source_line_id) continue;
      if (creditLineIdBySourceLineId.has(line.source_line_id)) {
        throw new Error('duplicate_credit_source_line');
      }
      creditLineIdBySourceLineId.set(line.source_line_id, line.id);
    }
    const sourcedCreditLineCount = preparedLines.filter(
      (line) => line.source_line_id,
    ).length;
    if (
      !usesHistoricalImport &&
      input.docType === 'credit_note' &&
      preparedLines.length > 0 &&
      sourcedCreditLineCount !== preparedLines.length
    ) {
      throw new Error('credit_note_source_line_required');
    }
    const preparedStockReturns = (input.stockReturns ?? []).map((movement) => ({
      article_id: movement.articleId,
      variant_id: movement.variantId ?? null,
      warehouse_id: movement.warehouseId,
      quantity: movement.quantity,
      movement_date: movement.movementDate,
      notes: movement.notes ?? null,
      source_returns: movement.sourceReturns.map((sourceReturn) => {
        const creditNoteLineId = creditLineIdBySourceLineId.get(
          sourceReturn.sourceLineId,
        );
        if (!creditNoteLineId)
          throw new Error('credit_stock_source_line_not_found');
        return {
          credit_note_line_id: creditNoteLineId,
          variant_id: movement.variantId ?? null,
          quantity: sourceReturn.quantity,
        };
      }),
    }));
    if (input.stockWarehouseId !== undefined && input.docType !== 'delivery') {
      throw new Error('sales_delivery_stock_target_requires_delivery');
    }
    const preparedDeliveryStockEffects =
      input.docType === 'delivery'
        ? buildSalesDeliveryStockEffects(input.lines).map((effect) => ({
            article_id: effect.articleId,
            variant_id: effect.variantId,
            quantity: effect.quantity,
          }))
        : [];
    if (
      input.docType === 'delivery' &&
      salesDeliveryRequiresWarehouse(input.lines) &&
      !input.stockWarehouseId
    ) {
      throw new Error('sales_delivery_warehouse_required');
    }
    const preparedCreditApplications = (input.creditApplications ?? []).map(
      (application) => ({
        id: globalThis.crypto.randomUUID(),
        credit_note_id: application.creditNoteId,
        source_delivery_id: application.sourceDeliveryId,
        credit_total_millimes: Math.round(application.creditTotalTtc * 1000),
        consumed_from_millimes: Math.round(application.consumedFromTtc * 1000),
        consumed_to_millimes: Math.round(
          (application.consumedFromTtc + application.appliedTtc) * 1000,
        ),
        applied_ht_before_discount: application.appliedHtBeforeDiscount,
        applied_discount: application.appliedDiscount,
        applied_ht: application.appliedHt,
        applied_document_discount: application.appliedDocumentDiscount,
        applied_net_ht: application.appliedNetHt,
        applied_vat: application.appliedVat,
        applied_ttc: application.appliedTtc,
        applied_benefit: application.appliedBenefit ?? null,
        lines: application.lines.map((line) => ({
          credit_note_line_id: line.creditNoteLineId,
          source_line_id: line.sourceLineId ?? null,
          line_number: line.lineNumber,
          article_id: line.articleId ?? null,
          description: line.description,
          applied_quantity: line.appliedQuantity,
          unit_price_ht: line.unitPriceHt,
          unit_price_ttc: line.unitPriceTtc ?? null,
          vat_rate: line.vatRate,
          applied_ht_before_discount: line.appliedHtBeforeDiscount,
          applied_discount: line.appliedDiscount,
          applied_ht: line.appliedHt,
          applied_document_discount: line.appliedDocumentDiscount,
          applied_net_ht: line.appliedNetHt,
          applied_vat: line.appliedVat,
          applied_ttc: line.appliedTtc,
          applied_benefit: line.appliedBenefit ?? null,
        })),
      }),
    );
    const invoiceParentIds = [
      ...new Set(
        [
          ...(input.parentDocIds ?? []),
          ...(input.parentDocId ? [input.parentDocId] : []),
        ].filter(Boolean),
      ),
    ];
    // Toute creation normale d'avoir source et toute facture avec lignes sont
    // transactionnelles. Les imports historiques totalement delies gardent le
    // chemin legacy explicite.
    const usesAtomicCreditCreate =
      !usesHistoricalImport &&
      input.docType === 'credit_note' &&
      sourcedCreditLineCount > 0;
    const usesAtomicInvoiceCreate =
      !usesHistoricalImport &&
      input.docType === 'invoice' &&
      preparedLines.length > 0;
    const usesAtomicDeliveryCreate =
      !usesHistoricalImport && input.docType === 'delivery';
    const usesAtomicPosCheckout =
      usesAtomicDeliveryCreate && input.posCheckout !== undefined;
    const usesAtomicDeliveryPayment =
      usesAtomicDeliveryCreate && input.deliveryPayment !== undefined;
    const usesAtomicQuoteOrderCreate =
      !usesHistoricalImport &&
      (input.docType === 'quote' || input.docType === 'order');
    const usesAtomicSalesCreate =
      usesAtomicQuoteOrderCreate ||
      usesAtomicCreditCreate ||
      usesAtomicInvoiceCreate ||
      usesAtomicDeliveryCreate;
    const customerSource =
      input.customerSource ??
      (input.proCustomerId
        ? 'pro'
        : input.onlineCustomerId
          ? 'online'
          : 'local');
    // Independent parent metadata reads must not add sequential network waits.
    const [channel, originStoreId] = await Promise.all([
      input.channel ?? this.inheritChannelFromParents(input),
      input.originStoreId !== undefined
        ? input.originStoreId
        : this.inheritOriginStoreFromParents(input),
    ]);
    const providedNumber = input.number?.trim();
    const proposedNumber = !providedNumber
      ? input.proposedNumber?.trim()
      : undefined;
    const atomicSalesNumberMode = providedNumber
      ? 'provided'
      : proposedNumber
        ? 'proposed'
        : 'auto';
    const documentPayload = {
      doc_type: input.docType,
      origin_store_id: originStoreId,
      channel,
      customer_source: customerSource,
      client_id: customerSource === 'local' ? input.clientId : null,
      online_customer_id:
        customerSource === 'online' ? (input.onlineCustomerId ?? null) : null,
      storefront_order_status:
        customerSource === 'online'
          ? (input.storefrontOrderStatus ?? null)
          : null,
      pro_customer_id:
        customerSource === 'pro' ? (input.proCustomerId ?? null) : null,
      pro_client_id:
        customerSource === 'pro' ? (input.proClientId ?? null) : null,
      representative_id: input.representativeId ?? null,
      car_registration_number: input.carRegistrationNumber ?? null,
      doc_date: input.docDate,
      validity_date: input.validityDate,
      parent_doc_id: input.parentDocId,
      parent_doc_ids:
        input.parentDocIds ?? (input.parentDocId ? [input.parentDocId] : []),
      pro_commande_code: input.proCommandeCode ?? null,
      notes: input.notes,
      custom_properties: input.customProperties ?? {},
      vat_exempt: input.vatExempt ?? false,
      total_ht: input.totalHt ?? 0,
      total_discount: input.totalDiscount ?? 0,
      document_discount_mode: input.documentDiscountMode ?? 'amount',
      document_discount_value: input.documentDiscountValue ?? 0,
      document_discount: input.documentDiscount ?? 0,
      total_vat: input.totalVat ?? 0,
      total_ttc: input.totalTtc ?? 0,
      withholding_amount: input.withholdingAmount ?? 0,
      stamp_amount: input.stampAmount ?? 0,
      ...fodecColumns(input),
      delivery_fee: input.deliveryFee ?? 0,
      net_to_pay: input.netToPay ?? 0,
      client_request_id: requestId,
      // Posée seulement quand elle existe (ticket POS) : les autres RPC de
      // création l'ignorent, et le rejeu d'une requête mise en file avant
      // l'ajout de la clé garde une empreinte identique.
      ...(input.cashRegisterId ? { cash_register_id: input.cashRegisterId } : {}),
      ...(usesAtomicSalesCreate
        ? {
            number: providedNumber ?? proposedNumber ?? null,
            number_mode: atomicSalesNumberMode,
          }
        : {}),
    };
    const posPaymentPayload = input.posCheckout
      ? {
          payment_type: input.posCheckout.payment.paymentType,
          payment_method: input.posCheckout.payment.paymentMethod,
          third_party_type: input.posCheckout.payment.thirdPartyType,
          third_party_id: input.posCheckout.payment.thirdPartyId,
          collection_store_id:
            input.posCheckout.payment.collectionStoreId ?? originStoreId,
          bank_account_id: input.posCheckout.payment.bankAccountId ?? null,
          amount: input.posCheckout.payment.amount,
          payment_date: input.posCheckout.payment.paymentDate,
          reference: input.posCheckout.payment.reference ?? null,
          notes: input.posCheckout.payment.notes ?? null,
          recurring_payment_id:
            input.posCheckout.payment.recurringPaymentId ?? null,
          recurring_occurrence_id:
            input.posCheckout.payment.recurringOccurrenceId ?? null,
        }
      : null;
    const deliveryPaymentPayload = input.deliveryPayment
      ? {
          payment_type: input.deliveryPayment.payment.paymentType,
          payment_method: input.deliveryPayment.payment.paymentMethod,
          third_party_type: input.deliveryPayment.payment.thirdPartyType,
          third_party_id: input.deliveryPayment.payment.thirdPartyId,
          collection_store_id:
            input.deliveryPayment.payment.collectionStoreId ?? originStoreId,
          bank_account_id:
            input.deliveryPayment.payment.bankAccountId ?? null,
          amount: input.deliveryPayment.payment.amount,
          payment_date: input.deliveryPayment.payment.paymentDate,
          reference: input.deliveryPayment.payment.reference ?? null,
          notes: input.deliveryPayment.payment.notes ?? null,
          recurring_payment_id:
            input.deliveryPayment.payment.recurringPaymentId ?? null,
          recurring_occurrence_id:
            input.deliveryPayment.payment.recurringOccurrenceId ?? null,
        }
      : null;
    const posDraft = input.posCheckout?.payment.linkedDraft;
    const posDraftPayload = posDraft
      ? {
          type: posDraft.type,
          direction: posDraft.direction,
          number: posDraft.number,
          bank_name: posDraft.bankName ?? '',
          bank_account_id: posDraft.bankAccountId ?? null,
          payer_bank_id: posDraft.payerBankId ?? null,
          payer_rib: posDraft.payerRib ?? null,
          beneficiary_name: posDraft.beneficiaryName ?? null,
          place_of_payment: posDraft.placeOfPayment ?? null,
          place_of_issue: posDraft.placeOfIssue ?? null,
          protestable: posDraft.protestable ?? null,
          amount: posDraft.amount,
          issue_date: posDraft.issueDate,
          due_date: posDraft.dueDate,
          acceptance_date: posDraft.acceptanceDate ?? null,
          third_party_type: posDraft.thirdPartyType,
          third_party_id: posDraft.thirdPartyId,
          current_state: posDraft.currentState ?? null,
          is_endorsed: posDraft.isEndorsed ?? false,
          endorsed_to: posDraft.endorsedTo ?? null,
          notes: posDraft.notes ?? null,
        }
      : null;
    if (usesHistoricalImport) {
      const { data, error } = await this.supabase.rpc(
        'import_historical_sales_document_atomic',
        {
          p_document: { ...documentPayload, number: input.number!.trim() },
          p_lines: preparedLines,
        },
      );
      if (error) throw error;
      if (!data) throw new Error('historical_sales_import_returned_no_id');
      const imported = await this.getDocumentById(data as string);
      if (!imported) throw new Error('historical_sales_import_unavailable');
      return imported;
    }
    const resumeDocumentPayload = {
      ...documentPayload,
      custom_properties: (input.customProperties ?? {}) as Json,
    };
    delete resumeDocumentPayload['number_mode'];
    const resumeAtomicDocument = async (): Promise<string | null> => {
      if (!usesAtomicSalesCreate) return null;
      if (usesAtomicPosCheckout || usesAtomicDeliveryPayment) {
        // A payload-free lookup cannot prove that the committed checkout was
        // created from this exact document/payment snapshot. First execution
        // and ordinary replay therefore always go through the canonical
        // checkout RPC below; the explicit UI recovery boundary owns
        // cancellation.
        return null;
      }
      if (usesAtomicDeliveryCreate) {
        const { data, error } = await this.supabase.rpc(
          'resume_sales_delivery_atomic',
          {
            p_document: documentPayload,
            p_lines: preparedLines,
            p_warehouse_id: input.stockWarehouseId ?? null,
            p_stock_effects: preparedDeliveryStockEffects,
          },
        );
        if (error) throw error;
        return (data as string | null) ?? null;
      }
      const { data, error } = await this.supabase.rpc(
        'resume_sales_document_atomic',
        {
          // The generic invoice/credit request ledger predates the transport
          // number mode. The atomic wrappers consume it, then deliberately
          // remove it before storing the canonical business payload. For an
          // invoice this is intentionally the restart-recovery boundary: once
          // a commit moved its source allocations, the transfer plan cannot
          // be reconstructed. Ordinary ambiguous replay uses the strict
          // transfer-aware function below instead.
          p_document: resumeDocumentPayload,
          p_lines: preparedLines,
          p_credit_applications: preparedCreditApplications,
          p_stock_returns: preparedStockReturns,
        },
      );
      if (error) throw error;
      return (data as string | null) ?? null;
    };

    // Verifie le snapshot canonique SOUS le meme advisory lock que create,
    // avant numero et avant relecture des paiements deja deplaces. Un simple
    // lookup local par client_request_id accepterait un payload different.
    const resumedDocumentId = usesAtomicPosCheckout || usesAtomicDeliveryPayment || !!input.invoicePayment
      ? null
      : await resumeAtomicDocument();
    if (resumedDocumentId) {
      const resumed = await this.getDocumentById(resumedDocumentId);
      if (!resumed)
        throw new Error('atomic_sales_document_request_unavailable');
      // Atomic numbering already consumes released numbers in its transaction.
      if (usesAtomicCreditCreate) {
        await mirrorStorefrontStockForArticles(
          this.supabase,
          preparedStockReturns.map((movement) => movement.article_id),
        );
      } else if (usesAtomicDeliveryCreate) {
        void mirrorStorefrontStockForArticles(
          this.supabase,
          preparedDeliveryStockEffects.map((effect) => effect.article_id),
        );
      }
      const resumedWithUnits = (!usesAtomicCreditCreate && !usesAtomicInvoiceCreate && !usesAtomicDeliveryCreate && await this.persistSalesLineUnits(
        resumed.id,
        input.lines,
      ))
        ? ((await this.getDocumentById(resumed.id)) ?? resumed)
        : resumed;
      // The source order is already canonical. Rewriting it after a committed
      // delivery both destroys partial-delivery quantities and can make a
      // successful creation appear rejected when the follow-up fails.
      return resumedWithUnits;
    }

    // Ces controles portent sur un etat mutable (le BL devient facture, une
    // autorisation superviseur peut expirer). Ils doivent donc venir APRES la
    // reprise canonique : un commit deja effectue reste recuperable sans
    // revalider les prerequis de sa premiere execution.
    await this.assertParentsNotAlreadyInvoiced(input);
    await this.assertCreditSourceMayBeCredited(input);
    await this.authorizeDiscount({
      docType: input.docType,
      action: 'create',
      clientRequestId: requestId,
      requestedPercent: maxRequestedDiscountPercent({
        documentTotalHt: input.totalHt,
        documentDiscount: input.documentDiscount,
        lines: input.lines,
      }),
      supervisorToken: input.supervisorToken,
    });

    const preparedPaymentTransfers =
      input.docType === 'invoice' && input.lines.length > 0
        ? await this.prepareInvoicePaymentTransfers(
            invoiceParentIds,
            Number(input.netToPay ?? input.totalTtc ?? 0),
          )
        : [];
    const resumeExactAtomicDocument = async (): Promise<string | null> => {
      if (!usesAtomicInvoiceCreate) return resumeAtomicDocument();
      const { data, error } = await this.supabase.rpc(
        'resume_sales_invoice_atomic',
        {
          p_document: resumeDocumentPayload,
          p_lines: preparedLines,
          p_credit_applications: preparedCreditApplications,
          p_payment_transfers: preparedPaymentTransfers,
        },
      );
      if (error) throw error;
      return data ?? null;
    };

    let number = providedNumber ?? proposedNumber ?? '';
    if (usesAtomicSalesCreate) {
      // Atomic delivery/invoice/credit RPCs allocate or claim the legal number
      // in the same transaction as all persistence effects. `number` is only
      // known here for an explicit/offline proposal and is read back after an
      // automatic allocation.
    } else if (providedNumber) {
      // Explicit numbers (migration imports, user-resolved conflicts) must not
      // silently duplicate an already-issued number of the same series.
      await this.assertNumberAvailable(input.docType, providedNumber);
      number = providedNumber;
    } else if (proposedNumber) {
      // Offline-first: the client minted this number while disconnected.
      // Claim it atomically; a lost claim throws DocumentNumberConflictError
      // and the sync layer asks the USER how to renumber.
      number = await this.claimProposedNumber(input.docType, proposedNumber);
    } else {
      number = await this.getNextNumber(input.docType);
    }
    const insertPayload = usesAtomicSalesCreate
      ? documentPayload
      : { ...documentPayload, number };
    const invokePosCheckout = () =>
      this.supabase.rpc('create_pos_checkout_atomic', {
        p_client_request_id: requestId,
        p_document: insertPayload,
        p_lines: preparedLines,
        p_warehouse_id: input.stockWarehouseId ?? null,
        p_stock_effects: preparedDeliveryStockEffects,
        p_payment: posPaymentPayload!,
        p_cash_session_id: input.posCheckout!.cashSessionId,
        p_draft: posDraftPayload,
      });
    const invokeDeliveryPaymentCheckout = () =>
      this.supabase.rpc('create_sales_delivery_checkout_atomic', {
        p_client_request_id: requestId,
        p_document: insertPayload,
        p_lines: preparedLines,
        p_warehouse_id: input.stockWarehouseId ?? null,
        p_stock_effects: preparedDeliveryStockEffects,
        p_payment: deliveryPaymentPayload!,
        p_cash_session_id: input.deliveryPayment?.cashSessionId ?? null,
      });
    const releaseAllocatedNumber = async () => {
      // Le numéro est consommé AVANT l'insert. Si l'insert échoue, on rend le
      // numéro pour ne pas gonfler le compteur sans document créé. Sans risque
      // de doublon : à la création suivante, getNextNumber re-synchronise le
      // compteur au-delà de tout numéro déjà émis.
      if (!usesAtomicSalesCreate && !providedNumber) {
        const parsed = parseDocumentNumber(number);
        if (parsed) {
          await this.supabase.rpc('reset_sequence_value', {
            seq_name: SALES_DOC_NUMBERING[input.docType].seq,
            new_value: parsed.value,
          });
        }
      }
    };
    const resolveAtomicOutcome = async (
      error: { code?: string; message?: string } | null,
      missingResultMessage: string,
    ): Promise<{ id: string }> => {
      if (usesAtomicPosCheckout || usesAtomicDeliveryPayment) {
        // Replay the exact canonical payload. A resolver lookup would accept a
        // resource committed under the same key but a different fingerprint.
        const replay = usesAtomicPosCheckout
          ? await invokePosCheckout()
          : await invokeDeliveryPaymentCheckout();
        if (replay.data) return { id: replay.data as string };
        if (replay.error?.code === '23505') throw replay.error;
        if (replay.error?.code && /^[0-9A-Z]{5}$/.test(replay.error.code)) {
          throw new ConfirmedMutationRejectedError(replay.error);
        }
        if (replay.error) throw replay.error;
        if (error) throw error;
        throw new Error(missingResultMessage);
      }
      // Un timeout peut survenir APRES commit. Revalider le snapshot canonique
      // sous advisory lock avant de rendre le numero ou d'accepter un document.
      let resumeConfirmedAbsent = false;
      try {
        const committedId = await resumeExactAtomicDocument();
        if (committedId) return { id: committedId };
        resumeConfirmedAbsent = true;
      } catch (resumeError) {
        const databaseError = resumeError as { code?: string };
        if (databaseError.code === '23505') {
          // La meme cle appartient deja a un autre payload. Notre tentative
          // n'a donc pas pu committer et son numero alloue est recuperable.
          await releaseAllocatedNumber();
          throw resumeError;
        }
        // Etat encore inconnu : ne jamais remettre le numero en circulation.
      }

      const confirmedDatabaseRejection =
        !!error?.code && /^[0-9A-Z]{5}$/.test(error.code);
      if (resumeConfirmedAbsent && (confirmedDatabaseRejection || !error)) {
        await releaseAllocatedNumber();
        throw new ConfirmedMutationRejectedError(
          error ?? new Error(missingResultMessage),
        );
      }
      if (error) throw error;
      throw new Error(missingResultMessage);
    };

    let doc: { id: string };
    if (usesAtomicQuoteOrderCreate) {
      const { data, error } = await this.supabase.rpc(
        'create_sales_quote_order_atomic',
        {
          p_document: insertPayload,
          p_lines: preparedLines,
        },
      );
      if (error || !data) {
        try {
          doc = await resolveAtomicOutcome(
            error,
            'sales_quote_order_atomic_create_returned_no_id',
          );
        } catch (atomicError) {
          throw await this.normalizeAtomicNumberError(
            input.docType,
            providedNumber ?? proposedNumber ?? '',
            atomicError,
          );
        }
      } else {
        doc = { id: data };
      }
    } else if (usesAtomicDeliveryCreate) {
      const { data, error } = usesAtomicPosCheckout
        ? await invokePosCheckout()
        : usesAtomicDeliveryPayment
          ? await invokeDeliveryPaymentCheckout()
        : await this.supabase.rpc('create_sales_delivery_atomic', {
            p_document: insertPayload,
            p_lines: preparedLines,
            p_warehouse_id: input.stockWarehouseId ?? null,
            p_stock_effects: preparedDeliveryStockEffects,
          });
      if (error || !data) {
        try {
          doc = await resolveAtomicOutcome(
            error,
            usesAtomicPosCheckout
              ? 'pos_checkout_atomic_create_returned_no_id'
              : usesAtomicDeliveryPayment
                ? 'sales_delivery_checkout_atomic_create_returned_no_id'
              : 'sales_delivery_atomic_create_returned_no_id',
          );
        } catch (atomicError) {
          throw await this.normalizeAtomicNumberError(
            input.docType,
            providedNumber ?? proposedNumber ?? '',
            atomicError,
          );
        }
      } else {
        doc = { id: data as string };
      }
      // Refresh the public catalogue in the background. The BL and ledger
      // have committed; a slow mirror must not turn that into a save timeout.
      void mirrorStorefrontStockForArticles(
        this.supabase,
        preparedDeliveryStockEffects.map((effect) => effect.article_id),
      );
    } else if (usesAtomicCreditCreate) {
      const { data, error } = await this.supabase.rpc(
        'create_sales_credit_note_atomic',
        {
          p_document: insertPayload,
          p_lines: preparedLines,
          p_stock_returns: preparedStockReturns,
        },
      );
      if (error || !data) {
        try {
          doc = await resolveAtomicOutcome(
            error,
            'credit_note_atomic_create_returned_no_id',
          );
        } catch (atomicError) {
          const normalizedError =
            atomicError instanceof ConfirmedMutationRejectedError
              ? new ConfirmedMutationRejectedError(
                  normalizeCreditNoteAtomicError(atomicError.cause),
                )
              : normalizeCreditNoteAtomicError(atomicError);
          throw await this.normalizeAtomicNumberError(
            input.docType,
            providedNumber ?? proposedNumber ?? '',
            normalizedError,
          );
        }
      } else {
        doc = { id: data as string };
      }
      // Le RPC a écrit le ledger sans passer par StockRepository : remettre à
      // jour le miroir storefront depuis le ledger autoritatif, sans recréer de
      // mouvement. Ce rafraîchissement reste best-effort comme tous les autres
      // miroirs de stock ; l'avoir atomique, lui, est déjà validé.
      await mirrorStorefrontStockForArticles(
        this.supabase,
        preparedStockReturns.map((movement) => movement.article_id),
      );
    } else if (usesAtomicInvoiceCreate && input.invoicePayment) {
      doc = { id: await createInvoicePaymentCheckout(this.supabase, 'sales', input, {
        document: insertPayload, lines: preparedLines, credits: preparedCreditApplications,
        transfers: preparedPaymentTransfers,
      }) };
    } else if (usesAtomicInvoiceCreate) {
      const { data, error } = await this.supabase.rpc(
        'create_sales_invoice_atomic',
        {
          p_document: insertPayload,
          p_lines: preparedLines,
          p_credit_applications: preparedCreditApplications,
          p_payment_transfers: preparedPaymentTransfers,
        },
      );
      if (error || !data) {
        try {
          doc = await resolveAtomicOutcome(
            error,
            'sales_invoice_atomic_create_returned_no_id',
          );
        } catch (atomicError) {
          const normalizedError =
            atomicError instanceof ConfirmedMutationRejectedError
              ? new ConfirmedMutationRejectedError(
                  normalizeInvoiceAtomicError(atomicError.cause),
                )
              : normalizeInvoiceAtomicError(atomicError);
          throw await this.normalizeAtomicNumberError(
            input.docType,
            providedNumber ?? proposedNumber ?? '',
            normalizedError,
          );
        }
      } else {
        doc = { id: data as string };
      }
    } else {
      const { data, error: docError } = await this.supabase
        .from(SALES_DOCUMENTS_WRITE)
        .insert(insertPayload as never)
        .select('id')
        .single();
      if (docError || !data) {
        await releaseAllocatedNumber();
        if (docError) throw docError;
        throw new Error('sales_document_create_returned_no_id');
      }
      doc = data;
    }

    // Le numéro est de nouveau porté par un document : sa ligne « libéré » n'a
    // plus lieu d'être. Purement hygiénique — findNextNumber écarte déjà les
    // lignes périmées en les croisant avec les numéros existants.
    if (!usesAtomicSalesCreate) await this.clearReleasedNumber(input.docType, number);

    if (!usesAtomicSalesCreate) {
      // Bind a create authorization to the new document before inserting its
      // lines. The line policies then validate the same server-side grant.
      const { error: finalizeDiscountError } = await this.supabase.rpc(
        'finalize_sales_discount_authorization' as never,
        {
          p_client_request_id: requestId,
          p_document_id: doc.id,
        } as never,
      );
      if (finalizeDiscountError) throw finalizeDiscountError;

      if (preparedLines.length > 0) {
        const lines = preparedLines.map((line) => ({
          ...line,
          document_id: doc.id,
        }));
        const { error: linesError } = await this.supabase
          .from(SALES_DOCUMENT_LINES_WRITE)
          .insert(lines as never);
        if (linesError) throw linesError;
      }
    }

    if (
      !usesAtomicInvoiceCreate &&
      input.docType === 'invoice' &&
      input.creditApplications?.length
    ) {
      try {
        await this.persistCreditApplications(
          doc.id,
          requestId,
          input.creditApplications,
        );
      } catch (error) {
        // La facture porte déjà ses totaux nets : si sa réservation perd une
        // course concurrente, elle ne doit pas survivre sans l'avoir qui les
        // justifie. Seul le document de CETTE tentative est supprimé.
        await this.supabase
          .from(SALES_DOCUMENTS_WRITE)
          .delete()
          .eq('id', doc.id);
        if (!providedNumber) {
          const parsed = parseDocumentNumber(number);
          if (parsed) {
            await this.supabase.rpc('reset_sequence_value', {
              seq_name: SALES_DOC_NUMBERING[input.docType].seq,
              new_value: parsed.value,
            });
          }
        }
        throw error;
      }
    }

    // Une facture hérite des encaissements de ses BL : sans ça, un BL réglé
    // donnait une facture « impayée » (l'affectation désignait encore le BL,
    // qui n'est plus une créance une fois facturé).
    if (!usesAtomicInvoiceCreate && input.docType === 'invoice') {
      await this.carryOverParentPayments(
        doc.id,
        (insertPayload.parent_doc_ids as string[]) ?? [],
        Number(input.netToPay ?? input.totalTtc ?? 0),
      );
    }

    if (!usesAtomicCreditCreate && !usesAtomicInvoiceCreate && !usesAtomicDeliveryCreate) {
      await this.persistSalesLineUnits(doc.id, input.lines);
    }
    const fresh = (await this.getDocumentById(doc.id))!;
    // Keep the source quantities intact and report the already committed BL.
    return fresh;
  }

  /**
   * Fige l'unité de vente choisie (« Carton »…) sur les lignes qui ne sont pas
   * dans l'unité de base, APRÈS la création/édition atomique.
   *
   * Pourquoi hors des RPCs atomiques : les tables de lignes sont verrouillées
   * en écriture (RLS base-table) et chaque RPC insère une liste de colonnes
   * fixe. Plutôt que de réécrire toutes ces fonctions, une RPC dédiée
   * (`set_sales_document_line_units`, SECURITY DEFINER bornée au tenant) pose
   * `unit_label`/`unit_factor` en rapprochant par `line_number` — stable que
   * les ids de ligne soient régénérés ou non. Idempotent : rejouer réapplique
   * les mêmes valeurs, ce qui rend l'opération auto-réparable au retry.
   *
   * Le prix effectif (déjà dans `unit_price_ht`) et le décompte de stock (déjà
   * multiplié par le facteur dans les effets de stock) ne dépendent PAS de cet
   * appel : seuls l'affichage et la reprise du facteur lors d'une
   * transformation en dépendent. Retourne `true` si au moins une ligne a été
   * marquée (pour rafraîchir la lecture).
   */
  private async persistSalesLineUnits(
    documentId: string,
    lines: readonly { unitFactor?: number; unitLabel?: string | null }[],
  ): Promise<boolean> {
    const units = lines
      .map((line, idx) => ({
        line_number: idx + 1,
        unit_factor: line.unitFactor ?? 1,
        unit_label: line.unitLabel ?? null,
      }))
      .filter((unit) => unit.unit_factor !== 1 || unit.unit_label != null);
    if (units.length === 0) return false;
    // Best-effort : le document et le stock sont DÉJÀ commités quand on arrive
    // ici. Poser les libellés/facteurs d'unité est secondaire — un échec ne doit
    // JAMAIS faire échouer une sauvegarde déjà validée (l'utilisateur verrait
    // « erreur » alors que sa vente est enregistrée). On journalise pour le
    // diagnostic et on continue ; l'unité se re-pose à la prochaine édition.
    try {
      const { error } = await this.supabase.rpc(
        'set_sales_document_line_units' as never,
        { p_document_id: documentId, p_units: units } as never,
      );
      if (error) throw error;
      return true;
    } catch (unitError) {
      console.error(
        '[persistSalesLineUnits] non-bloquant : échec du marquage des unités de ligne',
        unitError,
      );
      return false;
    }
  }

  /**
   * Persiste les snapshots préparés par @erp/domain/sales. L'exclusion GiST
   * en base tranche la concurrence : deux factures ne peuvent réserver la
   * même plage de millimes d'un avoir.
   */
  private async persistCreditApplications(
    invoiceId: string,
    requestId: string,
    inputs: CreateSalesCreditApplicationInput[],
  ): Promise<void> {
    if (inputs.length === 0) return;

    const { data: existingRows, error: existingError } =
      await pendingSalesCreditSchema(this.supabase)
        .from('sales_credit_applications')
        .select('credit_note_id')
        .eq('invoice_id', invoiceId)
        .eq('client_request_id', requestId);
    if (existingError) throw existingError;
    const existingCreditIds = new Set(
      ((existingRows ?? []) as Array<Record<string, unknown>>).map(
        (row) => row['credit_note_id'] as string,
      ),
    );
    const pending = inputs.filter(
      (input) => !existingCreditIds.has(input.creditNoteId),
    );
    if (pending.length === 0) return;

    const rows = pending.map((input) => {
      const id = globalThis.crypto.randomUUID();
      const consumedFromMillimes = Math.round(input.consumedFromTtc * 1000);
      const appliedMillimes = Math.round(input.appliedTtc * 1000);
      return {
        id,
        input,
        header: {
          id,
          credit_note_id: input.creditNoteId,
          credit_total_millimes: Math.round(input.creditTotalTtc * 1000),
          invoice_id: invoiceId,
          source_delivery_id: input.sourceDeliveryId,
          client_request_id: requestId,
          consumed_from_millimes: consumedFromMillimes,
          consumed_to_millimes: consumedFromMillimes + appliedMillimes,
          applied_ht_before_discount: input.appliedHtBeforeDiscount,
          applied_discount: input.appliedDiscount,
          applied_ht: input.appliedHt,
          applied_document_discount: input.appliedDocumentDiscount,
          applied_net_ht: input.appliedNetHt,
          applied_vat: input.appliedVat,
          applied_ttc: input.appliedTtc,
          applied_benefit: input.appliedBenefit ?? null,
        },
      };
    });

    const { error: headerError } = await pendingSalesCreditSchema(this.supabase)
      .from('sales_credit_applications')
      .insert(rows.map((row) => row.header));
    if (headerError) {
      if (headerError.code === '23P01' || headerError.code === '23505') {
        throw new CreditNoteApplicationConflictError(
          pending.length === 1 ? pending[0]!.creditNoteId : null,
        );
      }
      throw headerError;
    }

    const lineRows = rows.flatMap(({ id, input }) =>
      input.lines.map((line) => ({
        application_id: id,
        credit_note_line_id: line.creditNoteLineId,
        source_line_id: line.sourceLineId ?? null,
        line_number: line.lineNumber,
        article_id: line.articleId ?? null,
        description: line.description,
        applied_quantity: line.appliedQuantity,
        unit_price_ht: line.unitPriceHt,
        unit_price_ttc: line.unitPriceTtc ?? null,
        vat_rate: line.vatRate,
        applied_ht_before_discount: line.appliedHtBeforeDiscount,
        applied_discount: line.appliedDiscount,
        applied_ht: line.appliedHt,
        applied_document_discount: line.appliedDocumentDiscount,
        applied_net_ht: line.appliedNetHt,
        applied_vat: line.appliedVat,
        applied_ttc: line.appliedTtc,
        applied_benefit: line.appliedBenefit ?? null,
      })),
    );
    const { error: linesError } = await pendingSalesCreditSchema(this.supabase)
      .from('sales_credit_application_lines')
      .insert(lineRows);
    if (!linesError) return;

    // Un insert multi-row est atomique ; retirer les en-têtes de cette seule
    // tentative remet donc le registre à son état précédent.
    await pendingSalesCreditSchema(this.supabase)
      .from('sales_credit_applications')
      .delete()
      .in(
        'id',
        rows.map((row) => row.id),
      );
    throw linesError;
  }

  /**
   * Reporte sur la facture les affectations de règlement de ses documents
   * sources (BL, commandes).
   *
   * Appelé depuis `createDocument` : les DEUX chemins de transformation
   * (formulaire et « Transformer la sélection ») y passent, et un futur
   * troisième aussi. Le plan lui-même est du domaine pur
   * (planPaymentCarryOver) — ici on ne fait qu'écrire.
   *
   * Jamais fatal : une facture correctement créée ne doit pas être rejetée
   * parce que le report a échoué. Le lien reste réparable (l'affectation est
   * toujours sur le BL) et la migration de rattrapage sait le refaire.
   */
  private async carryOverParentPayments(
    invoiceId: string,
    parentIds: string[],
    netToPay: number,
  ): Promise<void> {
    const sources = [...new Set(parentIds.filter(Boolean))];
    if (sources.length === 0 || !(netToPay > 0)) return;

    try {
      const { data, error } = await this.supabase
        .from('payment_allocations')
        .select('id, payment_id, document_id, allocated_amount')
        .in('document_id', sources);
      if (error) throw error;

      const rows = (data ?? []) as Array<Record<string, unknown>>;
      if (rows.length === 0) return;

      // Ordre stable : le plan plafonne à la créance de la facture, donc
      // « qui est repris en premier » doit être reproductible d'un poste à
      // l'autre. L'id de la ligne d'affectation suffit et ne bouge jamais.
      const sourceAllocations: CarryOverAllocation[] = rows
        .map((row) => ({
          id: row['id'] as string,
          paymentId: row['payment_id'] as string,
          documentId: row['document_id'] as string,
          allocatedAmount: Number(row['allocated_amount'] ?? 0),
        }))
        .sort((a, b) => a.id.localeCompare(b.id));

      const { transfers } = planPaymentCarryOver({
        sourceAllocations,
        targetDue: netToPay,
      });

      for (const transfer of transfers) {
        if (transfer.remainingOnSource === 0) {
          // La ligne part entière : un simple déménagement, aucun montant touché.
          const { error: moveErr } = await this.supabase
            .from('payment_allocations')
            .update({
              document_id: invoiceId,
              document_type: 'invoice',
            } as never)
            .eq('id', transfer.allocationId);
          if (moveErr) throw moveErr;
          continue;
        }

        // Reprise partielle : le BL garde le surplus (marchandises non
        // facturables écartées de la facture), la facture reçoit sa part.
        const { error: shrinkErr } = await this.supabase
          .from('payment_allocations')
          .update({ allocated_amount: transfer.remainingOnSource } as never)
          .eq('id', transfer.allocationId);
        if (shrinkErr) throw shrinkErr;

        const { error: insertErr } = await this.supabase
          .from('payment_allocations')
          .insert({
            payment_id: transfer.paymentId,
            document_type: 'invoice',
            document_id: invoiceId,
            allocated_amount: transfer.movedAmount,
          } as never);
        if (insertErr) throw insertErr;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[payment-carry-over] failed for invoice', invoiceId, err);
    }
  }

  /**
   * Snapshot exhaustif des allocations positives des parents. Le RPC exige
   * aussi les lignes qui ne bougent pas (moved=0), afin de prouver qu'aucun
   * encaissement du BL n'a été silencieusement oublié par le client.
   */
  private async prepareInvoicePaymentTransfers(
    parentIds: string[],
    netToPay: number,
  ): Promise<AtomicInvoicePaymentTransfer[]> {
    const sources = [...new Set(parentIds.filter(Boolean))];
    if (sources.length === 0) return [];

    const rows: Array<Record<string, unknown>> = [];
    for (const ids of chunkIds(sources, IN_FILTER_CHUNK_SIZE)) {
      const { data, error } = await this.supabase
        .from('payment_allocations')
        .select('id, payment_id, document_id, allocated_amount')
        .in('document_id', ids)
        .gt('allocated_amount', 0);
      if (error) throw error;
      rows.push(...((data ?? []) as Array<Record<string, unknown>>));
    }

    const sourceAllocations: CarryOverAllocation[] = rows
      .map((row) => ({
        id: row['id'] as string,
        paymentId: row['payment_id'] as string,
        documentId: row['document_id'] as string,
        allocatedAmount: round3(Number(row['allocated_amount'] ?? 0)),
      }))
      .filter((allocation) => allocation.allocatedAmount > 0)
      .sort((a, b) => a.id.localeCompare(b.id));
    const planned = planPaymentCarryOver({
      sourceAllocations,
      targetDue: netToPay,
    });
    const byAllocationId = new Map(
      planned.transfers.map((transfer) => [transfer.allocationId, transfer]),
    );

    return sourceAllocations.map((allocation) => {
      const transfer = byAllocationId.get(allocation.id);
      const movedAmount = transfer?.movedAmount ?? 0;
      return {
        allocation_id: allocation.id,
        payment_id: allocation.paymentId,
        source_document_id: allocation.documentId,
        moved_amount: movedAmount,
        remaining_on_source:
          transfer?.remainingOnSource ?? allocation.allocatedAmount,
        target_allocation_id:
          movedAmount > 0 ? globalThis.crypto.randomUUID() : null,
      };
    });
  }

  /**
   * Rend aux documents sources les encaissements que la facture leur avait
   * repris, avant de la supprimer.
   *
   * Sans ce retour, supprimer une facture DÉTRUISAIT le lien : le BL
   * redevenait facturable ET impayé, alors que l'argent était bien rentré. Ce
   * qui ne trouve pas sa place (un BL déjà soldé par ailleurs) reste
   * supprimé — le paiement, lui, survit toujours en crédit non affecté.
   */
  private async returnPaymentsToParents(
    invoiceId: string,
    parentIds: string[],
  ): Promise<void> {
    const sources = [...new Set(parentIds.filter(Boolean))];
    if (sources.length === 0) return;

    const { data: allocData, error: allocError } = await this.supabase
      .from('payment_allocations')
      .select('id, payment_id, document_id, allocated_amount')
      .eq('document_id', invoiceId);
    if (allocError) throw allocError;

    const invoiceAllocations = (
      (allocData ?? []) as Array<Record<string, unknown>>
    )
      .map((row) => ({
        id: row['id'] as string,
        paymentId: row['payment_id'] as string,
        documentId: invoiceId,
        allocatedAmount: Number(row['allocated_amount'] ?? 0),
      }))
      .sort((a, b) => a.id.localeCompare(b.id));
    if (invoiceAllocations.length === 0) return;

    // Les BL reprennent dans l'ordre de leur numéro de document, et chacun
    // dans la limite de ce qu'il lui reste à devoir — l'exacte symétrie du
    // report, pour que faire/défaire/refaire retombe sur ses pieds.
    const { data: parentData, error: parentError } = await this.supabase
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select('id, number, doc_type, total_ttc, net_to_pay')
      .in('id', sources);
    if (parentError) throw parentError;

    const parents = ((parentData ?? []) as Array<Record<string, unknown>>)
      .map((row) => ({
        id: row['id'] as string,
        number: (row['number'] as string) ?? '',
        docType: (row['doc_type'] as string) ?? 'delivery',
        due: Number(row['net_to_pay'] ?? row['total_ttc'] ?? 0),
      }))
      .sort((a, b) => a.number.localeCompare(b.number));
    if (parents.length === 0) return;

    const { data: parentAllocData, error: parentAllocError } =
      await this.supabase
        .from('payment_allocations')
        .select('document_id, allocated_amount')
        .in(
          'document_id',
          parents.map((p) => p.id),
        );
    if (parentAllocError) throw parentAllocError;

    const alreadyOnParent = new Map<string, number>();
    for (const row of (parentAllocData ?? []) as Array<
      Record<string, unknown>
    >) {
      const id = row['document_id'] as string;
      alreadyOnParent.set(
        id,
        (alreadyOnParent.get(id) ?? 0) + Number(row['allocated_amount'] ?? 0),
      );
    }

    let pending = invoiceAllocations;
    for (const parent of parents) {
      if (pending.length === 0) break;

      const { transfers } = planPaymentCarryOver({
        sourceAllocations: pending,
        targetDue: parent.due,
        targetAlreadyAllocated: alreadyOnParent.get(parent.id) ?? 0,
      });
      if (transfers.length === 0) continue;

      const movedById = new Map(transfers.map((t) => [t.allocationId, t]));
      for (const transfer of transfers) {
        if (transfer.remainingOnSource === 0) {
          const { error } = await this.supabase
            .from('payment_allocations')
            .update({
              document_id: parent.id,
              document_type: parent.docType,
            } as never)
            .eq('id', transfer.allocationId);
          if (error) throw error;
          continue;
        }

        const { error: shrinkErr } = await this.supabase
          .from('payment_allocations')
          .update({ allocated_amount: transfer.remainingOnSource } as never)
          .eq('id', transfer.allocationId);
        if (shrinkErr) throw shrinkErr;

        const { error: insertErr } = await this.supabase
          .from('payment_allocations')
          .insert({
            payment_id: transfer.paymentId,
            document_type: parent.docType,
            document_id: parent.id,
            allocated_amount: transfer.movedAmount,
          } as never);
        if (insertErr) throw insertErr;
      }

      // Ce qui n'a pas été repris entièrement reste candidat pour le BL
      // suivant, à hauteur de son résidu.
      pending = pending
        .map((allocation) => {
          const moved = movedById.get(allocation.id);
          if (!moved) return allocation;
          return { ...allocation, allocatedAmount: moved.remainingOnSource };
        })
        .filter((allocation) => allocation.allocatedAmount > 0);
    }
  }

  /**
   * Refuse de facturer une source (BL / commande) déjà couverte par une AUTRE
   * facture vivante.
   *
   * Sans ce contrôle, deux postes pouvaient facturer le même BL : le poste
   * hors-ligne au retour du réseau, le poste connecté dans le même quart
   * d'heure. `parent_doc_ids` n'a aucune contrainte et `is_invoiced` n'est
   * qu'un drapeau posé après coup — d'où une détection fondée sur l'existence
   * d'une facture enfant, seule preuve fiable.
   */
  private async assertParentsNotAlreadyInvoiced(
    input: CreateSalesDocumentInput,
  ): Promise<void> {
    if (input.docType !== 'invoice' || input.allowDuplicateParentInvoice)
      return;
    const parentIds = [
      ...new Set(
        [
          ...(input.parentDocIds ?? []),
          ...(input.parentDocId ? [input.parentDocId] : []),
        ].filter(Boolean),
      ),
    ];
    if (parentIds.length === 0) return;

    const candidates = await this.findLiveInvoicesForParents(parentIds);
    const clashes = findParentInvoicingClashes({
      parentIds,
      existingInvoices: candidates,
      clientRequestId: input.clientRequestId ?? null,
      ownNumber: input.number ?? input.proposedNumber ?? null,
    });
    if (clashes.length === 0) return;

    const involved = clashingInvoices(clashes);
    const numbersById = await this.numbersForDocumentIds([
      ...parentIds,
      ...involved.flatMap((i) => i.parentDocIds),
    ]);
    throw new ParentAlreadyInvoicedError({
      scope: 'sales',
      docType: input.docType,
      ownNumber: input.number ?? input.proposedNumber ?? null,
      ownDocDate: input.docDate,
      ownTotalTtc: input.totalTtc ?? 0,
      ownParentNumbers: parentIds.map((id) => numbersById.get(id) ?? id),
      takenParents: clashes.map((c) => ({
        parentId: c.parentId,
        parentNumber: numbersById.get(c.parentId) ?? c.parentId,
        invoiceId: c.invoice.id,
      })),
      invoices: involved.map<ConflictingInvoiceSummary>((i) => ({
        id: i.id,
        number: i.number,
        docDate: i.docDate,
        totalTtc: i.totalTtc,
        parentDocIds: i.parentDocIds,
        parentNumbers: i.parentDocIds.map((pid) => numbersById.get(pid) ?? pid),
      })),
    });
  }

  /**
   * Un BL qui a déjà cédé ses lignes à une facture ne peut plus recevoir
   * un nouvel avoir direct. Le retour doit partir de la facture existante,
   * sinon cet avoir resterait absent de ses totaux et de son impression.
   *
   * Cette lecture a lieu avant remise et numérotation. La RPC atomique refait
   * la même validation sous verrou pour fermer la course concurrente.
   */
  private async assertCreditSourceMayBeCredited(
    input: CreateSalesDocumentInput,
  ): Promise<void> {
    if (input.docType !== 'credit_note') return;
    const sourceIds = [
      ...new Set([
        ...(input.parentDocIds ?? []),
        ...(input.parentDocId ? [input.parentDocId] : []),
        ...input.lines
          .map((line) => line.sourceDocId)
          .filter((id): id is string => !!id),
      ]),
    ];
    if (sourceIds.length === 0) return;

    const found = new Set<string>();
    for (const ids of chunkIds(sourceIds, IN_FILTER_CHUNK_SIZE)) {
      const { data, error } = await this.supabase
        .from(AUTHORIZED_SALES_DOCUMENTS)
        .select('id, doc_type, is_invoiced')
        .in('id', ids);
      if (error) throw error;
      for (const row of (data ?? []) as Array<Record<string, unknown>>) {
        const id = row['id'] as string;
        found.add(id);
        if (row['doc_type'] === 'delivery' && row['is_invoiced'] === true) {
          throw new CreditNoteSourceInvoicedError(id);
        }
      }
    }
    // Une source masquée/supprimée ne doit jamais être interprétée comme
    // un BL encore créditable.
    if (found.size !== sourceIds.length)
      throw new Error('credit_note_source_unavailable');
  }

  /**
   * Factures citant un des parents donnés, **annulées exclues** : une facture
   * intégralement contre-passée par un avoir ne bloque plus ses sources (c'est
   * exactement ce que produit l'arbitrage « garder ma facture »).
   */
  private async findLiveInvoicesForParents(parentIds: string[]) {
    const rows: Array<Record<string, unknown>> = [];
    for (let i = 0; i < parentIds.length; i += PARENT_FILTER_CHUNK_SIZE) {
      const chunk = parentIds.slice(i, i + PARENT_FILTER_CHUNK_SIZE);
      const { data, error } = await this.supabase
        .from(AUTHORIZED_SALES_DOCUMENTS)
        .select(
          'id, number, doc_date, total_ttc, parent_doc_id, parent_doc_ids, client_request_id',
        )
        .eq('doc_type', 'invoice')
        .or(
          `parent_doc_id.in.(${chunk.join(',')}),parent_doc_ids.ov.{${chunk.join(',')}}`,
        );
      if (error) throw error;
      rows.push(...((data ?? []) as Array<Record<string, unknown>>));
    }
    const byId = new Map<string, Record<string, unknown>>();
    for (const row of rows) byId.set(row['id'] as string, row);
    if (byId.size === 0) return [];

    const cancelled = await this.cancelledInvoiceIds([...byId.keys()]);
    return [...byId.values()]
      .filter((row) => !cancelled.has(row['id'] as string))
      .map((row) => ({
        id: row['id'] as string,
        number: (row['number'] as string) ?? '',
        docDate: (row['doc_date'] as string) ?? '',
        totalTtc: Number(row['total_ttc']) || 0,
        parentDocIds: [
          ...new Set(
            [
              ...((row['parent_doc_ids'] as string[] | null) ?? []),
              ...(row['parent_doc_id'] ? [row['parent_doc_id'] as string] : []),
            ].filter(Boolean),
          ),
        ],
        clientRequestId: (row['client_request_id'] as string | null) ?? null,
      }));
  }

  /**
   * Parmi les factures données, celles intégralement annulées par avoir.
   *
   * Un avoir PARTIEL (retour de quelques lignes) ne libère rien : le critère
   * est la contre-passation complète, au millime près.
   */
  private async cancelledInvoiceIds(
    invoiceIds: string[],
  ): Promise<Set<string>> {
    const cancelled = new Set<string>();
    if (invoiceIds.length === 0) return cancelled;

    const totals = new Map<string, number>();
    for (let i = 0; i < invoiceIds.length; i += IN_FILTER_CHUNK_SIZE) {
      const chunk = invoiceIds.slice(i, i + IN_FILTER_CHUNK_SIZE);
      const { data, error } = await this.supabase
        .from(AUTHORIZED_SALES_DOCUMENTS)
        .select('id, total_ttc')
        .in('id', chunk);
      if (error) throw error;
      for (const row of (data ?? []) as Array<Record<string, unknown>>) {
        totals.set(row['id'] as string, Number(row['total_ttc']) || 0);
      }
    }

    const credited = new Map<string, number>();
    for (let i = 0; i < invoiceIds.length; i += PARENT_FILTER_CHUNK_SIZE) {
      const chunk = invoiceIds.slice(i, i + PARENT_FILTER_CHUNK_SIZE);
      const { data, error } = await (this.supabase as any)
        .from(AUTHORIZED_SALES_DOCUMENTS)
        .select('total_ttc, parent_doc_id, parent_doc_ids')
        .eq('doc_type', 'credit_note')
        .or(
          `parent_doc_id.in.(${chunk.join(',')}),parent_doc_ids.ov.{${chunk.join(',')}}`,
        );
      if (error) throw error;
      for (const row of (data ?? []) as Array<Record<string, unknown>>) {
        const parents = new Set(
          [
            ...((row['parent_doc_ids'] as string[] | null) ?? []),
            ...(row['parent_doc_id'] ? [row['parent_doc_id'] as string] : []),
          ].filter(Boolean),
        );
        for (const pid of parents) {
          if (!totals.has(pid)) continue;
          credited.set(
            pid,
            (credited.get(pid) ?? 0) + (Number(row['total_ttc']) || 0),
          );
        }
      }
    }

    for (const [id, invoiceTotal] of totals) {
      const creditedTotal = credited.get(id) ?? 0;
      // Tolérance d'un millime : les totaux sont arrondis à 3 décimales.
      if (invoiceTotal > 0 && creditedTotal >= invoiceTotal - 0.0005)
        cancelled.add(id);
    }
    return cancelled;
  }

  /**
   * Canal repris des documents parents, à la création d'un document dérivé.
   *
   * UN seul point de passage pour tous les clients (web, mobile, rejeu de la
   * file hors-ligne) : c'est ce qui garantit qu'aucun chemin de transformation
   * ne peut oublier de reporter l'origine de la vente. La lecture est minuscule
   * (une colonne, uniquement quand il y a filiation) et l'arbitrage — un canal
   * n'est repris que si tous les parents s'accordent — vit dans le domaine.
   *
   * Un échec de lecture ne bloque JAMAIS la création : le document sort au
   * canal par défaut plutôt que de laisser un numéro de séquence en l'air.
   */
  private async inheritChannelFromParents(
    input: CreateSalesDocumentInput,
  ): Promise<string> {
    const parentIds = [
      ...new Set(
        [
          ...(input.parentDocIds ?? []),
          ...(input.parentDocId ? [input.parentDocId] : []),
        ].filter(Boolean),
      ),
    ];
    if (parentIds.length === 0) return DEFAULT_SALES_CHANNEL;

    const channels: Array<string | null> = [];
    try {
      for (let i = 0; i < parentIds.length; i += IN_FILTER_CHUNK_SIZE) {
        const chunk = parentIds.slice(i, i + IN_FILTER_CHUNK_SIZE);
        const { data, error } = await (this.supabase as any)
          .from(AUTHORIZED_SALES_DOCUMENTS)
          .select('channel')
          .in('id', chunk);
        if (error) throw error;
        for (const row of (data ?? []) as Array<Record<string, unknown>>) {
          channels.push((row['channel'] as string | null) ?? null);
        }
      }
    } catch {
      return DEFAULT_SALES_CHANNEL;
    }
    return inheritSalesChannel(channels);
  }

  private async inheritOriginStoreFromParents(
    input: CreateSalesDocumentInput,
  ): Promise<string | null> {
    const parentIds = [
      ...new Set(
        [
          ...(input.parentDocIds ?? []),
          ...(input.parentDocId ? [input.parentDocId] : []),
        ].filter(Boolean),
      ),
    ];
    if (parentIds.length === 0) return null;

    const storeIds: Array<string | null> = [];
    try {
      for (let i = 0; i < parentIds.length; i += IN_FILTER_CHUNK_SIZE) {
        const chunk = parentIds.slice(i, i + IN_FILTER_CHUNK_SIZE);
        const { data, error } = await (this.supabase as any)
          .from('authorized_sales_documents')
          .select('origin_store_id')
          .in('id', chunk);
        if (error) throw error;
        for (const row of (data ?? []) as Array<Record<string, unknown>>) {
          storeIds.push((row['origin_store_id'] as string | null) ?? null);
        }
      }
    } catch {
      // Compatibility during a staged rollout: an old schema must still be
      // able to create a document without consuming another legal number.
      return null;
    }
    return inheritSalesOriginStore(storeIds);
  }

  /** Numéros de documents par id — pour rendre un conflit lisible. */
  private async numbersForDocumentIds(
    ids: string[],
  ): Promise<Map<string, string>> {
    const unique = [...new Set(ids.filter(Boolean))];
    const out = new Map<string, string>();
    for (let i = 0; i < unique.length; i += IN_FILTER_CHUNK_SIZE) {
      const chunk = unique.slice(i, i + IN_FILTER_CHUNK_SIZE);
      const { data, error } = await (this.supabase as any)
        .from(AUTHORIZED_SALES_DOCUMENTS)
        .select('id, number')
        .in('id', chunk);
      if (error) throw error;
      for (const row of (data ?? []) as Array<Record<string, unknown>>) {
        out.set(row['id'] as string, (row['number'] as string) ?? '');
      }
    }
    return out;
  }

  /**
   * Supprime une facture évincée par un arbitrage de synchronisation.
   *
   * La suppression elle-même est celle de `deleteDocument` — même nettoyage des
   * dépendances (imputations de paiement, certificats de retenue) et même
   * libération des sources. Ce qui s'ajoute ici, et qui justifie une méthode à
   * part, c'est l'IDEMPOTENCE : le rejeu d'un arbitrage dont la tentative
   * précédente avait abouti côté serveur ne doit pas échouer sur un document
   * déjà disparu.
   */
  async deleteInvoiceForConflict(id: string): Promise<void> {
    const { data: doc, error: fetchErr } = await this.supabase
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select('id, doc_type, number')
      .eq('id', id)
      .maybeSingle();
    if (fetchErr) throw fetchErr;
    if (!doc) return;
    const docType = doc['doc_type'] as SalesDocType;
    if (docType !== 'invoice') {
      throw new Error(
        `deleteInvoiceForConflict: ${doc['number']} is not an invoice (${docType})`,
      );
    }
    await this.deleteDocument(id);
  }

  /**
   * Recalcule `is_invoiced` pour chaque source : vrai si et seulement si une
   * facture vivante la couvre encore. Le drapeau était jusqu'ici posé à la
   * création et retiré à la suppression du document seulement — une facture
   * écartée par arbitrage laissait le BL immobilisé pour toujours.
   */
  private async refreshParentInvoicedFlags(parentIds: string[]): Promise<void> {
    for (const parentId of parentIds) {
      const live = await this.findLiveInvoicesForParents([parentId]);
      const stillInvoiced = live.some((inv) =>
        inv.parentDocIds.includes(parentId),
      );
      await this.supabase
        .from(SALES_DOCUMENTS_WRITE)
        .update({ is_invoiced: stillInvoiced } as never)
        .eq('id', parentId);
    }
  }

  /**
   * Concurrence optimiste : refuse l'écriture si le document a bougé côté
   * serveur depuis la version que l'appelant éditait.
   *
   * Actif UNIQUEMENT quand `baseUpdatedAt` est fourni — c'est-à-dire sur le
   * rejeu hors-ligne, où l'écart peut se compter en heures et où l'écrasement
   * silencieux détruisait réellement le travail de l'autre poste. La saisie en
   * ligne reste en dernier-écrit-gagne : y appliquer le même contrôle
   * fabriquerait des conflits sur deux enregistrements successifs du même
   * utilisateur (le cache porte encore l'ancien `updated_at`).
   */
  private async assertNotConcurrentlyEdited(
    id: string,
    input: UpdateSalesDocumentInput,
  ): Promise<void> {
    const base = input.baseUpdatedAt;
    if (!base) return;
    const { data, error } = await (this.supabase as any)
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select('id, doc_type, number, updated_at, total_ttc')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    // Disparu : ce n'est pas un conflit d'édition, l'update échouera plus loin
    // avec sa propre erreur (traitée par le triage habituel).
    if (!data) return;
    const serverUpdatedAt = (data['updated_at'] as string) ?? '';
    if (!isConcurrentlyEdited(base, serverUpdatedAt)) return;

    const { count } = await (this.supabase as any)
      .from(AUTHORIZED_SALES_DOCUMENT_LINES)
      .select('id', { count: 'exact', head: true })
      .eq('document_id', id);
    throw new ConcurrentEditError({
      scope: 'sales',
      documentId: id,
      docType: (data['doc_type'] as string) ?? '',
      number: (data['number'] as string) ?? '',
      baseUpdatedAt: base,
      serverUpdatedAt,
      mineTotalTtc: input.totalTtc ?? null,
      serverTotalTtc: Number(data['total_ttc']) || 0,
      mineLineCount: input.lines?.length ?? null,
      serverLineCount: count ?? 0,
    });
  }

  async updateDocument(
    id: string,
    input: UpdateSalesDocumentInput,
  ): Promise<SalesDocument> {
    const resumeDraftUpdate = async (): Promise<string | null> => {
      const { data, error } = await this.supabase.rpc('update_sales_document_draft_atomic' as never, {
        p_document_id: id, p_client_request_id: input.clientRequestId,
        p_command: input, p_lookup_only: true,
      } as never);
      if (error) throw error;
      return typeof data === 'string' ? data : null;
    };
    if (input.clientRequestId) {
      const committedId = await resumeDraftUpdate();
      if (committedId) {
        const committed = await this.getDocumentById(committedId);
        if (!committed) throw new Error('sales_document_not_found');
        return committed;
      }
    } else {
      await this.assertNotConcurrentlyEdited(id, input);
    }
    const { data: current, error: currentError } = await this.supabase
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select(SALES_DOCUMENT_WRITE_SNAPSHOT_SELECT)
      .eq('id', id)
      .single();
    if (currentError) throw currentError;
    if (!current) throw new Error('sales_document_not_found');
    // One authorized snapshot supplies the preflight checks, frozen costs and
    // linked-write plan. Re-reading each part serially delayed every save.
    const before = mapSalesDocument(current);
    // Un avoir enregistré a déjà pu remettre le stock en place et/ou
    // être consommé par une facture. Le modifier après coup laisserait ces
    // effets historiques avec d'anciens snapshots. Cette garde serveur reste
    // nécessaire même si les écrans web/mobile masquent le formulaire.
    if (before.docType === 'credit_note') {
      throw new Error('credit_note_saved_immutable');
    }
    if (before.docType === 'delivery' || before.docType === 'invoice') {
      await this.assertNoCreditLineReferences(id, (before.lines ?? []).map((line) => line.id));
    }
    await this.authorizeDiscount({
      docType: before.docType,
      action: 'edit',
      documentId: id,
      requestedPercent: maxRequestedDiscountPercent({
        documentTotalHt: input.totalHt ?? before.totalHt,
        documentDiscount: input.documentDiscount ?? before.documentDiscount,
        lines: input.lines ?? before.lines ?? [],
      }),
      supervisorToken: input.supervisorToken,
    });
    const mapped: TablesUpdate<'sales_documents'> = {};
    if (input.customerSource !== undefined)
      (mapped as Record<string, unknown>)['customer_source'] =
        input.customerSource;
    if (input.clientId !== undefined) mapped['client_id'] = input.clientId;
    if (input.originStoreId !== undefined) {
      (mapped as Record<string, unknown>)['origin_store_id'] =
        input.originStoreId;
    }
    if (input.onlineCustomerId !== undefined)
      (mapped as Record<string, unknown>)['online_customer_id'] =
        input.onlineCustomerId;
    if (input.proCustomerId !== undefined)
      (mapped as Record<string, unknown>)['pro_customer_id'] =
        input.proCustomerId;
    if (input.proClientId !== undefined)
      (mapped as Record<string, unknown>)['pro_client_id'] = input.proClientId;
    if (input.representativeId !== undefined)
      mapped['representative_id'] = input.representativeId;
    if (input.carRegistrationNumber !== undefined)
      (mapped as Record<string, unknown>)['car_registration_number'] =
        input.carRegistrationNumber;
    if (input.docDate !== undefined) mapped['doc_date'] = input.docDate;
    if (input.validityDate !== undefined)
      mapped['validity_date'] = input.validityDate;
    if (input.notes !== undefined) mapped['notes'] = input.notes;
    if (input.customProperties !== undefined)
      (mapped as Record<string, unknown>)['custom_properties'] =
        input.customProperties;
    if (input.vatExempt !== undefined)
      (mapped as Record<string, unknown>)['vat_exempt'] = input.vatExempt;
    if (input.totalHt !== undefined) mapped['total_ht'] = input.totalHt;
    if (input.totalDiscount !== undefined)
      mapped['total_discount'] = input.totalDiscount;
    if (input.documentDiscountMode !== undefined)
      (mapped as Record<string, unknown>)['document_discount_mode'] =
        input.documentDiscountMode;
    if (input.documentDiscountValue !== undefined)
      (mapped as Record<string, unknown>)['document_discount_value'] =
        input.documentDiscountValue;
    if (input.documentDiscount !== undefined)
      (mapped as Record<string, unknown>)['document_discount'] =
        input.documentDiscount;
    if (input.totalVat !== undefined) mapped['total_vat'] = input.totalVat;
    if (input.totalTtc !== undefined) mapped['total_ttc'] = input.totalTtc;
    if (input.withholdingAmount !== undefined)
      mapped['withholding_amount'] = input.withholdingAmount;
    if (input.stampAmount !== undefined)
      mapped['stamp_amount'] = input.stampAmount;
    Object.assign(mapped as Record<string, unknown>, fodecColumns(input));
    if (input.deliveryFee !== undefined)
      (mapped as Record<string, unknown>)['delivery_fee'] = input.deliveryFee;
    if (input.netToPay !== undefined) mapped['net_to_pay'] = input.netToPay;
    if (input.storefrontOrderStatus !== undefined) {
      (mapped as Record<string, unknown>)['storefront_order_status'] =
        input.storefrontOrderStatus;
    }
    if (input.proCommandeCode !== undefined) {
      (mapped as Record<string, unknown>)['pro_commande_code'] =
        input.proCommandeCode;
    }

    let preparedUpdatedLines: Array<Record<string, unknown>> | null = null;
    // Replace lines if provided
    if (input.lines) {
      // ── GEL DU COÛT CÔTÉ SERVEUR (règle métier : le bénéfice d'un document
      // ENREGISTRÉ ne bouge JAMAIS quand le prix d'achat catalogue change) ──
      // Un article déjà présent sur le document garde son snapshot EXISTANT,
      // quoi que le client envoie : aucun client (web, mobile, ancien build)
      // ne peut plus re-figer un coût historique au catalogue du jour lors
      // d'un simple Éditer → Enregistrer. Un article NOUVEAU sur le document
      // (ligne ajoutée / article remplacé pendant l'édition) se fige comme à
      // la création : coût fourni, sinon catalogue net du jour.
      // Une ligne d'un article déjà présent mais SANS coût connu reste à
      // « coût inconnu » (0) — on n'invente pas le coût d'aujourd'hui pour
      // une vente d'hier.
      const frozenCostByArticle = new Map<string, number>();
      const articlesOnDocument = new Set<string>();
      for (const line of before.lines ?? []) {
        const articleId = line.articleId;
        if (!articleId) continue;
        articlesOnDocument.add(articleId);
        const cost = Number(line.purchasePrice);
        if (
          Number.isFinite(cost) &&
          cost > 0 &&
          !frozenCostByArticle.has(articleId)
        ) {
          frozenCostByArticle.set(articleId, round3(cost));
        }
      }

      const catalogCosts = await resolveNetUnitCosts(
        this.supabase,
        // Le repli catalogue ne concerne QUE les articles nouveaux sur le doc.
        input.lines.map((line) =>
          line.articleId && articlesOnDocument.has(line.articleId)
            ? { articleId: null, purchasePrice: line.purchasePrice }
            : line,
        ),
      );
      const netCosts = input.lines.map((line, idx) => {
        if (line.articleId && articlesOnDocument.has(line.articleId)) {
          return frozenCostByArticle.get(line.articleId) ?? 0;
        }
        return catalogCosts[idx] ?? 0;
      });

      preparedUpdatedLines = input.lines.map((line, idx) => ({
        id: line.id || undefined,
        article_id: line.articleId ?? null,
        variant_id: line.variantId ?? null,
        variant_label: line.variantLabel ?? null,
        description: line.description,
        quantity: line.quantity,
        unit_price_ht: line.unitPriceHt,
        unit_price_ttc: line.unitPriceTtc ?? null,
        purchase_price: netCosts[idx] ?? 0,
        discount_pct: line.discountPct ?? 0,
        discount_mode: line.discountMode ?? 'pct',
        discount_amount: line.discountAmount ?? 0,
        vat_rate: line.vatRate,
        total_ht: line.totalHt ?? 0,
        total_ttc: line.totalTtc ?? 0,
        // Reporté par la RPC (mig 20260803100100) : une réécriture des lignes
        // ne doit pas effacer l'origine BL d'une facture groupée.
        source_doc_id: line.sourceDocId ?? null,
        // Même invariant pour un avoir : sa ligne doit continuer à viser la
        // ligne exacte retournée après une édition autorisée.
        source_line_id: line.sourceLineId ?? null,
        unit_label: line.unitLabel ?? null,
        unit_factor: line.unitFactor ?? 1,
      }));
    }

    // Allocate each new row once so a linked BL can reference the same new
    // order line inside the paired transaction. Existing form ids survive
    // reordering; old clients retain the matching positional identity.
    const reservedLineIds = new Set<string>((preparedUpdatedLines ?? [])
      .flatMap((line) => typeof line['id'] === 'string' ? [line['id']] : []));
    for (const [index, line] of (preparedUpdatedLines ?? []).entries()) {
      const existing = before.lines?.[index];
      line['id'] ||= existing
        && !reservedLineIds.has(existing.id)
        && existing.articleId === line['article_id']
        && existing.variantId === line['variant_id']
        ? existing.id : globalThis.crypto.randomUUID();
      reservedLineIds.add(line['id'] as string);
    }
    const next = {
      ...before,
      ...Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined)),
      lines: preparedUpdatedLines
        ? preparedUpdatedLines.map((line, index) => mapSalesLine({
            ...line, document_id: id, line_number: index + 1,
          }))
        : before.lines,
    } as SalesDocument;
    const linkedCandidate = storefrontOrderStatusRemovesLinkedDelivery(input.storefrontOrderStatus)
      ? null
      : this.isStorefrontOrder(before)
        ? await this.getLatestChildDelivery(id)
        : before.docType === 'delivery'
          ? await this.getStorefrontParentOrder(before)
          : null;
    const linked = linkedCandidate && canMirrorStorefrontDocumentLines(
      (before.docType === 'order' ? before : linkedCandidate).lines ?? [],
      (before.docType === 'delivery' ? before : linkedCandidate).lines ?? [],
    ) ? linkedCandidate : null;
    const linkedWrite = linked ? this.buildLinkedStorefrontWrite(linked, next) : null;
    if (before.docType === 'delivery' && linked?.docType === 'order' && linkedWrite) {
      for (const [index, line] of (preparedUpdatedLines ?? []).entries()) {
        line['source_doc_id'] = linked.id;
        line['source_line_id'] = linkedWrite.lines[index]?.['id'] ?? null;
      }
    }
    if (preparedUpdatedLines && before.docType === 'delivery') {
      (mapped as Record<string, unknown>)['stock_replacement'] =
        this.prepareDeliveryStockReplacement(before, next.lines ?? []);
    }
    if (preparedUpdatedLines && linked && linkedWrite && linked.docType === 'delivery') {
      linkedWrite.header['stock_replacement'] = this.prepareDeliveryStockReplacement(
        linked,
        linkedWrite.lines.map((line, index) => mapSalesLine({
          ...line, document_id: linked.id, line_number: index + 1,
        })),
      );
    }

    // Header, garde des dependances et swap de lignes forment UNE transaction.
    // L'ancienne paire `.update()` + `sales_update_document_lines_atomic`
    // pouvait modifier le header avant que la garde concurrente d'un avoir ne
    // refuse les lignes. L'ancien RPC lignes-only est desormais revoque.
    const { data: updateResult, error: updateError } = await this.supabase.rpc(
      (input.clientRequestId ? 'update_sales_document_draft_atomic'
        : linked ? 'update_linked_storefront_documents_atomic' : 'update_sales_document_atomic') as never,
      {
        p_document_id: id,
        p_document: mapped,
        p_lines: preparedUpdatedLines,
        ...(input.clientRequestId ? {
          p_client_request_id: input.clientRequestId, p_command: input,
          p_lookup_only: false,
          ...(linked ? { p_linked_base_updated_at: linked.updatedAt } : {}),
        } : {}),
        ...(linked && linkedWrite ? {
          p_linked_document_id: linked.id,
          p_linked_document: linkedWrite.header,
          p_linked_lines: preparedUpdatedLines ? linkedWrite.lines : null,
        } : {}),
      } as never,
    );
    if (updateError) {
      const databaseError = updateError as { code?: string; message?: string };
      if (input.clientRequestId && databaseError.code !== '23505') {
        // A transport failure can follow commit. The same-key lookup takes the
        // draft lock and distinguishes that durable receipt from a rollback.
        let confirmedAbsent = false;
        try {
          const committedId = await resumeDraftUpdate();
          if (committedId) {
            const committed = await this.getDocumentById(committedId);
            if (!committed) throw new Error('sales_document_not_found');
            return committed;
          }
          confirmedAbsent = true;
        } catch { /* Unknown outcome: retain the frozen command for retry. */ }
        if (confirmedAbsent && databaseError.code && /^[0-9A-Z]{5}$/.test(databaseError.code)) {
          throw new ConfirmedMutationRejectedError(updateError);
        }
      }
      if (
        databaseError.code === '23503' &&
        databaseError.message?.includes(
          'sales_document_has_credit_dependencies',
        )
      ) {
        throw new SalesDocumentCreditLockedError(
          id,
          'update',
          'credit_note_line_reference',
        );
      }
      throw updateError;
    }
    if (input.clientRequestId && updateResult !== id) {
      // An empty response is not evidence that the write committed.
      if (await resumeDraftUpdate() !== id) throw new Error('sales_document_update_result_missing');
    }

    const fresh = (await this.getDocumentById(id))!;
    // Commande boutique passée en Rejetée ou Retournée → son BL disparaît
    // (stock restauré, imputations libérées). AVANT les miroirs : le miroir
    // commande → BL réécrirait sinon un document en cours de suppression. Le
    // déclencheur est le STATUT PORTÉ PAR CET APPEL (la transition), pas
    // l'état stocké : une simple édition d'une commande déjà rejetée ne
    // supprime rien.
    if (
      storefrontOrderStatusRemovesLinkedDelivery(input.storefrontOrderStatus) &&
      this.isStorefrontOrder(fresh)
    ) {
      await this.removeChildDeliveriesForCancelledOrder(id);
    }
    await this.syncLinkedProCommandeFromDelivery(fresh);
    if (before.docType === 'delivery' || linked?.docType === 'delivery') {
      void mirrorStorefrontStockForArticles(this.supabase, [...new Set([
        ...(before.lines ?? []), ...(next.lines ?? []), ...(linked?.lines ?? []),
      ].flatMap((line) => line.articleId ? [line.articleId] : []))]);
    }
    return fresh;
  }

  private prepareDeliveryStockReplacement(
    _before: SalesDocument,
    nextLines: SalesDocumentLine[],
  ): Record<string, unknown> {
    // No stock:view read is needed to edit an authorized BL. The server pins
    // its original warehouse and revalidates tracking under the document lock,
    // just as creation filters the complete line plan against the catalogue.
    return {
      stock_effects: buildSalesDeliveryStockEffects(nextLines).map((effect) => ({
        article_id: effect.articleId, variant_id: effect.variantId, quantity: effect.quantity,
      })),
    };
  }

  async deleteDocument(id: string): Promise<void> {
    // Cette RPC SECURITY DEFINER voit aussi les avoirs masques par le RBAC.
    // Elle doit rester la toute premiere operation : une lecture via la vue
    // autorisee peut sembler vide sans `sales.credit_notes:view`, puis les
    // retours stock/paiements/certificats seraient deja modifies lorsque la FK
    // finale refuserait la suppression.
    const { error: creditDependencyError } = await this.supabase.rpc(
      'preflight_delete_sales_document' as never,
      { p_document_id: id } as never,
    );
    if (creditDependencyError) {
      const databaseError = creditDependencyError as {
        code?: string;
        message?: string;
      };
      if (
        databaseError.code === '23503' &&
        databaseError.message?.includes(
          'sales_document_has_credit_dependencies',
        )
      ) {
        throw new SalesDocumentCreditLockedError(
          id,
          'delete',
          'credit_note_child',
        );
      }
      throw creditDependencyError;
    }

    // Look up the doc being deleted so we can revert parent flags after.
    // `channel` / `online_customer_id` / `number` ne servent qu'à la branche
    // commande boutique plus bas — les lire ici évite une seconde requête.
    const { data: doc, error: fetchErr } = await this.supabase
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select(
        'id, number, doc_type, parent_doc_id, parent_doc_ids, channel, online_customer_id',
      )
      .eq('id', id)
      .single();
    if (fetchErr) throw fetchErr;

    // Un AVOIR reste indestructible : il matérialise l'annulation d'une facture,
    // et le supprimer ferait réapparaître une créance déjà annulée.
    //
    // Les FACTURES, elles, sont supprimables depuis 2026-07-26 (décision
    // explicite de Khalil, qui prime sur la règle 4 de CLAUDE.md). Ce que ça
    // implique, et qui est assumé : la séquence peut se retrouver trouée, ce
    // qu'un contrôle fiscal tunisien peut relever. Le réglage
    // `reuse_deleted_numbers` décide de la suite — quand il est actif, le
    // numéro revient à la création suivante MÊME s'il se retrouve entre-temps
    // au milieu de la série, parce que le trigger transactionnel de suppression
    // le consigne dans `released_document_numbers` sous le verrou de série ;
    // sinon le compteur continue et le trou est définitif. L'écran de
    // confirmation dit lequel des deux s'applique.
    //
    // Côté UI, l'action est réservée à la permission ventes:suppression.
    const docType = doc?.['doc_type'] as SalesDocType | undefined;
    if (docType && !canDeleteSalesDocument({ docType })) {
      throw new Error(
        "Un avoir ne peut pas être supprimé : il matérialise l'annulation d'une facture.",
      );
    }

    // PRE-FLIGHT integral, avant le moindre effet de bord (BL enfant d'une
    // commande boutique, stock, paiements, certificats, numero libere). Une FK
    // CASCADE sur sales_credit_applications ne doit jamais rendre acceptable
    // la disparition silencieuse d'un usage d'avoir.
    await this.assertNoCreditDependencies(id);

    // Commande BOUTIQUE : son BL part AVANT elle. Sans ça, la FK parent_doc_id
    // (NO ACTION) ferait échouer le DELETE sur un message Postgres illisible —
    // et si le BL avait été délié à la main, on laisserait une livraison
    // orpheline, stock sorti, sans plus aucune commande pour l'expliquer. C'est
    // la même règle que « commande rejetée ⇒ BL retiré », appliquée à la
    // disparition définitive de la commande. Refus du BL ⇒ on n'entame RIEN.
    const isStorefrontOrderRow =
      (doc?.['doc_type'] as string | undefined) === 'order' &&
      ((doc?.['channel'] as string | undefined) === 'storefront' ||
        Boolean(doc?.['online_customer_id']));
    // Le panier abandonné que cette commande avait « récupéré » se lit MAINTENANT :
    // sa FK est ON DELETE SET NULL, donc après le DELETE plus rien ne le relie à
    // la commande et il resterait marqué récupéré pour toujours.
    let recoveredCartIds: string[] = [];
    if (isStorefrontOrderRow) {
      await this.removeDeliveriesForDeletedOrder({
        scope: 'storefront',
        orderRef: (doc?.['number'] as string | undefined) ?? id,
        parentOrderId: id,
      });
      recoveredCartIds = await this.findRecoveredCartIds(id);
    }

    // Collect parent IDs whose is_invoiced flag may need resetting.
    // Only invoices flip this flag on parents (deliveries/orders), so only
    // invoice deletion can revert it.
    const parentIds: string[] = [];
    if (doc?.['doc_type'] === 'invoice') {
      const single = doc['parent_doc_id'] as string | null;
      const many = (doc['parent_doc_ids'] as string[] | null) ?? [];
      if (single) parentIds.push(single);
      for (const pid of many)
        if (pid && !parentIds.includes(pid)) parentIds.push(pid);
    }

    // Deleting a delivery (BL) must reverse its side effects, otherwise the
    // stock stays permanently deducted and any auto-payment stays allocated to
    // a ghost document. Stock level is derived from the movement ledger, so
    // deleting the BL's 'out' movements restores it exactly. We remove the
    // dependents BEFORE the doc so the worst case of a mid-way failure is a
    // benign, retryable inconsistency (stock restored, BL still present) rather
    // than the permanent corruption of an orphaned ledger. The payment record
    // itself is preserved (money was received) — only its allocation to this
    // BL is removed, leaving it as unallocated credit the user can review.
    let affectedArticleIds: string[] = [];
    if (docType === 'delivery') {
      const { data: movs } = await this.supabase
        .from(AUTHORIZED_STOCK_MOVEMENTS)
        .select('article_id')
        .eq('reference_type', 'sales_delivery')
        .eq('reference_id', id);
      affectedArticleIds = [
        ...new Set(
          ((movs ?? []) as Array<Record<string, unknown>>)
            .map((m) => m['article_id'] as string)
            .filter(Boolean),
        ),
      ];
      const { error: smErr } = await this.supabase.rpc(
        'delete_document_stock_movements' as never,
        {
          p_reference_type: 'sales_delivery',
          p_reference_id: id,
        } as never,
      );
      if (smErr) throw smErr;
      const { error: paErr } = await this.supabase
        .from('payment_allocations')
        .delete()
        .eq('document_type', 'delivery')
        .eq('document_id', id);
      if (paErr) throw paErr;
    }

    if (docType === 'invoice') {
      // D'abord RENDRE aux sources ce que la facture leur avait repris à la
      // transformation : sans ça, supprimer la facture laissait le BL
      // facturable ET impayé, alors que l'argent était bien rentré.
      await this.returnPaymentsToParents(id, parentIds);

      // Les encaissements survivent à la facture : on retire l'imputation,
      // jamais le paiement — l'argent a bien été reçu. Il redevient un crédit
      // non affecté que l'utilisateur peut ré-imputer. On filtre sur le seul
      // document_id : `document_type` porte le type du document ('invoice',
      // 'delivery', …) et ne distingue PAS vente et achat — l'id, lui, est
      // unique. Ce qui reste ici n'a trouvé sa place chez aucune source.
      const { error: paErr } = await this.supabase
        .from('payment_allocations')
        .delete()
        .eq('document_id', id);
      if (paErr) throw paErr;

      // Les certificats de retenue à la source citent la facture par
      // `source_document_id` (sans clé étrangère) : les laisser orphelins
      // fausserait la déclaration. Ils partent avec la facture qu'ils
      // documentent.
      const { error: wcErr } = await (this.supabase as any)
        .from('withholding_certificates')
        .delete()
        .eq('certificate_type', 'sale')
        .eq('source_document_id', id);
      if (wcErr) throw wcErr;
    }

    const { error: delErr } = await this.supabase
      .from(SALES_DOCUMENTS_WRITE)
      .delete()
      .eq('id', id);
    if (delErr) throw delErr;

    if (isStorefrontOrderRow) {
      await this.cleanUpAfterStorefrontOrderDeleted(
        id,
        (doc?.['online_customer_id'] as string | null | undefined) ?? null,
        recoveredCartIds,
      );
    }

    if (affectedArticleIds.length > 0) {
      await mirrorStorefrontStockForArticles(this.supabase, affectedArticleIds);
    }

    // Les sources que la facture immobilisait redeviennent facturables — sauf
    // celles qu'une autre facture VIVANTE couvre encore. On passe par le
    // recalcul commun plutôt que par un test « existe-t-il une facture ? » :
    // une facture intégralement annulée par avoir ne doit pas continuer à
    // bloquer son BL.
    await this.refreshParentInvoicedFlags(parentIds);
  }

  private async assertNoCreditDependencies(id: string): Promise<void> {
    const { count: childCreditCount, error: childCreditError } =
      await this.supabase
        .from(AUTHORIZED_SALES_DOCUMENTS)
        .select('id', { count: 'exact', head: true })
        .eq('doc_type', 'credit_note')
        .or(`parent_doc_id.eq.${id},parent_doc_ids.cs.{${id}}`);
    if (childCreditError) throw childCreditError;
    if ((childCreditCount ?? 0) > 0) {
      throw new SalesDocumentCreditLockedError(
        id,
        'delete',
        'credit_note_child',
      );
    }

    const { count: applicationCount, error: applicationError } =
      await pendingSalesCreditSchema(this.supabase)
        .from('sales_credit_applications')
        .select('id', { count: 'exact', head: true })
        .eq('invoice_id', id);
    if (applicationError) throw applicationError;
    if ((applicationCount ?? 0) > 0) {
      throw new SalesDocumentCreditLockedError(
        id,
        'delete',
        'credit_application',
      );
    }
  }

  private async assertNoCreditLineReferences(
    id: string,
    sourceLineIds: string[],
  ): Promise<void> {
    if (sourceLineIds.length === 0) return;

    const referringDocumentIdSet = new Set<string>();
    for (const sourceLineChunk of chunkIds(sourceLineIds)) {
      const { data: referringLines, error: referringLinesError } =
        await this.supabase
          .from(AUTHORIZED_SALES_DOCUMENT_LINES)
          .select('document_id')
          .in('source_line_id', sourceLineChunk);
      if (referringLinesError) throw referringLinesError;
      for (const line of (referringLines ?? []) as Array<
        Record<string, unknown>
      >) {
        const documentId = line['document_id'] as string;
        if (documentId) referringDocumentIdSet.add(documentId);
      }
    }
    const referringDocumentIds = [...referringDocumentIdSet];
    if (referringDocumentIds.length === 0) return;

    for (const documentChunk of chunkIds(referringDocumentIds)) {
      const { count: creditCount, error: creditError } = await this.supabase
        .from(AUTHORIZED_SALES_DOCUMENTS)
        .select('id', { count: 'exact', head: true })
        .eq('doc_type', 'credit_note')
        .in('id', documentChunk);
      if (creditError) throw creditError;
      if ((creditCount ?? 0) > 0) {
        throw new SalesDocumentCreditLockedError(
          id,
          'update',
          'credit_note_line_reference',
        );
      }
    }
  }

  // Resolve tenant + numbering config (prefix / padding / reuse mode) plus a
  // helper that reads the numbers already issued this year. Shared by the
  // consuming allocator (getNextNumber) and the non-consuming preview
  // (peekNextNumber) so both build identical, tenant-correct numbers.
  private async resolveNumberingContext(docType: SalesDocType) {
    const meta = SALES_DOC_NUMBERING[docType];
    const seqName = meta.seq;
    const currentYear = new Date().getFullYear();

    // Filtre explicitement par current_user_tenant_id() — pour les admins
    // plateforme, RLS expose >1 tenant ; on veut CELUI de l'appelant, pas le
    // premier renvoyé par Postgres. Le préfixe pilote le scan des numéros
    // existants, il doit donc être connu avant.
    type SbAny = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: string | null; error: { message: string } | null }>;
    };
    const { data: tenantId } = await (this.supabase as unknown as SbAny).rpc(
      'current_user_tenant_id',
      {},
    );
    // Skip the query when the tenant context isn't ready — a '' tenant_id
    // filter throws `invalid input syntax for type uuid: ""` server-side.
    const { data: settingsRow } = tenantId
      ? await this.supabase
          .from('company_settings')
          .select('*')
          .eq('tenant_id', tenantId)
          .maybeSingle()
      : { data: null };
    const sRow = (settingsRow ?? {}) as Record<string, unknown>;
    const prefix =
      (sRow[meta.prefixCol] as string | null) || meta.defaultPrefix;
    const padding =
      Number(sRow[meta.paddingCol] ?? SALES_DOC_DEFAULT_PADDING) ||
      SALES_DOC_DEFAULT_PADDING;
    const reuseDeleted = Boolean(sRow['reuse_deleted_numbers']);
    // Numérotation continue sans année ({PREFIX}-{NNNN}) quand le tenant
    // reprend la numérotation de son ancien système (doc_number_include_year
    // = false). `year` vaut alors null partout en aval (build/parse/scan).
    const year: number | null =
      sRow['doc_number_include_year'] === false ? null : currentYear;

    const readExistingValues = async (): Promise<number[]> => {
      // PostgREST silently caps an un-ranged select at 1,000 rows. Large
      // tenants can have several thousand documents in one series; reading
      // only the first page made max(existing)+1 point at an already-issued
      // number (TUNIELEC: BLC-183883). Page with a total order so preview and
      // save both see the complete legal series.
      const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
        this.supabase
          .from(AUTHORIZED_SALES_DOCUMENTS)
          .select('id, number')
          .eq('doc_type', docType)
          .like('number', year == null ? `${prefix}-%` : `${prefix}-${year}-%`)
          .order('number', { ascending: true })
          .order('id', { ascending: true })
          .range(from, to),
      );

      // p.year === year écarte les numéros de l'autre format : en mode sans
      // année, les anciens {PREFIX}-{YEAR}-{NNNN} parsent avec year ≠ null ;
      // en mode avec année, l'inverse.
      return rows
        .map((r) => parseDocumentNumber(r['number'] as string))
        .filter(
          (p): p is NonNullable<typeof p> =>
            p !== null && p.year === year && p.prefix === prefix,
        )
        .map((p) => p.value);
    };

    // Les numéros que l'app a vus se libérer (une ligne par suppression). Même
    // filtrage de série que readExistingValues : un numéro libéré sous un ancien
    // préfixe, ou d'une autre année, n'appartient pas à la série courante.
    // Cette lecture ne sert QU'au mode réutilisation — d'où l'appel paresseux,
    // le mode strict ne paie pas la requête.
    //
    // Le try/catch vaut promesse de déploiement : tant que la migration
    // 20260818120000 n'est pas appliquée, la table n'existe pas et la lecture
    // échoue. Un registre illisible doit rendre « aucun numéro libéré » —
    // c'est-à-dire l'ancien comportement — jamais casser la numérotation.
    const readReleasedValues = async (): Promise<number[]> => {
      try {
        // `as any` : released_document_numbers n'est pas encore dans
        // database.types.ts — même cast que le reste du fichier, à retirer au
        // prochain `supabase gen types typescript`.
        //
        // Filtre tenant explicite, pour la raison dite plus haut : RLS expose
        // TOUS les tenants à un admin plateforme, qui se verrait alors proposer
        // les numéros libérés d'un autre client.
        const rows = await fetchAllRows<Record<string, unknown>>((from, to) => {
          let query = (this.supabase as any)
            .from('released_document_numbers')
            .select('id, number');
          if (tenantId) query = query.eq('tenant_id', tenantId);
          return query
            .eq('doc_type', docType)
            .like(
              'number',
              year == null ? `${prefix}-%` : `${prefix}-${year}-%`,
            )
            .order('number', { ascending: true })
            .order('id', { ascending: true })
            .range(from, to);
        });

        return rows
          .map((r) => parseDocumentNumber(r['number'] as string))
          .filter(
            (p): p is NonNullable<typeof p> =>
              p !== null && p.year === year && p.prefix === prefix,
          )
          .map((p) => p.value);
      } catch {
        return [];
      }
    };

    return {
      seqName,
      year,
      prefix,
      padding,
      reuseDeleted,
      readExistingValues,
      readReleasedValues,
    };
  }

  /**
   * Le tenant de l'APPELANT, jamais « le premier que RLS laisse voir ».
   * `null` si le contexte n'est pas prêt — les appelants dégradent alors sans
   * filtre plutôt que d'échouer, la policy RLS restant la barrière réelle.
   */
  private async currentTenantId(): Promise<string | null> {
    type SbRpc = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: string | null; error: { message: string } | null }>;
    };
    const { data } = await (this.supabase as unknown as SbRpc).rpc(
      'current_user_tenant_id',
      {},
    );
    return data ?? null;
  }

  /**
   * Retire du registre un numéro qu'un document porte à nouveau. Purement
   * hygiénique : `findNextNumber` écarte déjà les lignes périmées en croisant
   * avec les numéros réellement existants. Best-effort, pour la même raison.
   */
  private async clearReleasedNumber(
    docType: SalesDocType,
    number: string,
  ): Promise<void> {
    if (!number) return;
    try {
      // `as any` : table absente de database.types.ts (voir readReleasedValues).
      // Le filtre tenant n'est PAS décoratif sur un DELETE : RLS laisse un admin
      // plateforme écrire chez tous les tenants, et ce même numéro existe
      // probablement chez plusieurs d'entre eux.
      const tenantId = await this.currentTenantId();
      let query = (this.supabase as any)
        .from('released_document_numbers')
        .delete();
      if (tenantId) query = query.eq('tenant_id', tenantId);
      await query.eq('doc_type', docType).eq('number', number);
    } catch {
      // Ligne périmée laissée en place : sans effet, findNextNumber la réécarte
      // à chaque fois en la croisant avec les numéros existants.
    }
  }

  // Non-consuming preview of the next document number. Forms render this as an
  // estimate BEFORE save; the real number is allocated (and the per-tenant
  // counter advanced) only in createDocument. Calling the consuming
  // getNextNumber here burned a legal sequence value on every form open,
  // leaving gaps — which Tunisian fiscal numbering forbids (CLAUDE.md §4).
  async peekNextNumber(docType: SalesDocType): Promise<string> {
    const {
      seqName,
      year,
      prefix,
      padding,
      reuseDeleted,
      readExistingValues,
      readReleasedValues,
    } = await this.resolveNumberingContext(docType);

    if (reuseDeleted) {
      const [existingValues, releasedValues] = await Promise.all([
        readExistingValues(),
        readReleasedValues(),
      ]);
      const { value } = findNextNumber(existingValues, 0, true, releasedValues);
      return buildDocumentNumber(prefix, year, value, padding);
    }

    // peek_next_sequence_value reads tenant_sequence_state.next_value WITHOUT
    // incrementing it. Reconcile with the highest number actually issued so the
    // preview still matches what getNextNumber would assign for imported docs.
    // Cast like the current_user_tenant_id call above: the RPC exists in the DB
    // but isn't in the generated types yet. If it's ever unavailable the call
    // errors and we fall back to max-existing+1 — never a consuming call.
    type PeekRpc = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{
        data: number | string | null;
        error: { message: string } | null;
      }>;
    };
    const { data, error } = await (this.supabase as unknown as PeekRpc).rpc(
      'peek_next_sequence_value',
      { seq_name: seqName },
    );
    const existingValues = await readExistingValues();
    const maxExisting =
      existingValues.length === 0 ? 0 : Math.max(...existingValues);
    const peeked = !error && data != null ? Number(data) : maxExisting + 1;
    const value = Math.max(peeked, maxExisting + 1);
    return buildDocumentNumber(prefix, year, value, padding);
  }

  async getNextNumber(docType: SalesDocType): Promise<string> {
    const {
      seqName,
      year,
      prefix,
      padding,
      reuseDeleted,
      readExistingValues,
      readReleasedValues,
    } = await this.resolveNumberingContext(docType);

    if (reuseDeleted) {
      // Mode réutilisation : on se base sur les numéros réellement existants,
      // plus ceux qu'une suppression a explicitement libérés — ces derniers
      // permettent de reprendre un numéro au MILIEU de la série, pas seulement
      // le dernier émis (voir findNextNumber).
      const [existingValues, releasedValues] = await Promise.all([
        readExistingValues(),
        readReleasedValues(),
      ]);

      const { value } = findNextNumber(existingValues, 0, true, releasedValues);
      // Maintient la séquence Postgres alignée sur le max observé pour éviter des
      // collisions si l'utilisateur désactive plus tard la réutilisation. C'est
      // bien le MAX, pas `value` : reprendre un trou ne fait pas reculer le
      // compteur en dessous de numéros déjà émis.
      const newMax = Math.max(value, ...existingValues);
      const { error: alignError } = await this.supabase.rpc(
        'set_sequence_value',
        {
          seq_name: seqName,
          new_value: newMax,
        },
      );
      if (
        alignError?.message.includes(
          'protected_sequence_requires_atomic_command',
        )
      ) {
        throw alignError;
      }
      return buildDocumentNumber(prefix, year, value, padding);
    }

    // Mode strict (par défaut) : séquence atomique côté Postgres
    const { data, error } = await this.supabase.rpc('get_next_sequence_value', {
      seq_name: seqName,
    });
    if (error?.message.includes('protected_sequence_requires_atomic_command')) {
      // Fiscal series are minted only by the matching transactional command.
      // Falling back to MAX+1 here would recreate the exact crash/concurrency
      // gap that the protected server-side allocator closes.
      throw error;
    }
    if (!error && data != null) {
      const sequenceValue = Number(data);
      const existingValues = await readExistingValues();
      const maxExisting =
        existingValues.length === 0 ? 0 : Math.max(...existingValues);
      let value = sequenceValue;

      if (sequenceValue <= maxExisting) {
        await this.supabase.rpc('set_sequence_value', {
          seq_name: seqName,
          new_value: maxExisting,
        });
        const { data: syncedData, error: syncedError } =
          await this.supabase.rpc('get_next_sequence_value', {
            seq_name: seqName,
          });
        value =
          !syncedError && syncedData != null
            ? Number(syncedData)
            : maxExisting + 1;
      }

      return buildDocumentNumber(prefix, year, value, padding);
    }

    // Fallback: derive from the highest existing number instead of row count,
    // because deleted rows or imported historical docs can make count+1 collide.
    const existingValues = await readExistingValues();
    const next =
      existingValues.length === 0 ? 1 : Math.max(...existingValues) + 1;
    const { error: fallbackAlignError } = await this.supabase.rpc(
      'set_sequence_value',
      {
        seq_name: seqName,
        new_value: next,
      },
    );
    if (
      fallbackAlignError?.message.includes(
        'protected_sequence_requires_atomic_command',
      )
    ) {
      throw fallbackAlignError;
    }
    return buildDocumentNumber(prefix, year, next, padding);
  }

  /**
   * Atomically claim an offline-proposed number. Strict mode goes through the
   * claim_sequence_value RPC (advance next_value past the proposal iff the
   * proposal is still ahead of the counter — exactly one device can win a
   * given value); a lost claim throws DocumentNumberConflictError with the
   * next-free estimate the conflict UI shows the user. Reuse-deleted mode is
   * server-authoritative instead: the proposal is kept only when it matches
   * "last existing + 1", else the document is silently renumbered to it.
   */
  private async claimProposedNumber(
    docType: SalesDocType,
    proposed: string,
  ): Promise<string> {
    const parsed = parseDocumentNumber(proposed);
    if (!parsed) {
      // Defensive: malformed proposal (never produced by our client) — fall
      // back to a normal server mint rather than rejecting the document.
      return this.getNextNumber(docType);
    }
    const ctx = await this.resolveNumberingContext(docType);

    if (ctx.reuseDeleted) {
      // Reuse mode: the server is authoritative — the next number is whatever
      // findNextNumber picks from the freed-number registry and the live
      // series, never what the client guessed. A stale offline proposal (minted
      // from a base peeked before a deletion or before the setting flipped)
      // would either re-create the very skip this mode exists to close, or
      // resurrect an ancient number. The proposal is only honored when it
      // already IS that pick; otherwise we silently renumber — offline creates
      // are flagged provisional client-side precisely for this.
      const [existingValues, releasedValues] = await Promise.all([
        ctx.readExistingValues(),
        ctx.readReleasedValues(),
      ]);
      const { value: expected } = findNextNumber(
        existingValues,
        0,
        true,
        releasedValues,
      );
      // Keep the sequence aligned on the max observed, same as the
      // reuse-mode mint path, so a later strict-mode switch can't collide.
      const newMax = Math.max(expected, ...existingValues, 0);
      await this.supabase.rpc('set_sequence_value', {
        seq_name: ctx.seqName,
        new_value: newMax,
      });
      const matchesSeries =
        parsed.value === expected &&
        parsed.prefix === ctx.prefix &&
        parsed.year === ctx.year;
      return matchesSeries
        ? proposed
        : buildDocumentNumber(ctx.prefix, ctx.year, expected, ctx.padding);
    }

    type ClaimRpc = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{
        data: Array<{ claimed: boolean; next_value: number | string }> | null;
        error: { message: string } | null;
      }>;
    };
    const { data, error } = await (this.supabase as unknown as ClaimRpc).rpc(
      'claim_sequence_value',
      { seq_name: ctx.seqName, desired: parsed.value },
    );
    if (error) throw error; // network/RPC failure → normal retry path, not a conflict
    const row = data?.[0];
    if (row?.claimed) return proposed;

    const existingValues = await ctx.readExistingValues();
    throw new DocumentNumberConflictError(
      this.buildNumberConflict(
        docType,
        ctx,
        proposed,
        existingValues,
        Number(row?.next_value ?? 0),
      ),
    );
  }

  private async normalizeAtomicNumberError(
    docType: SalesDocType,
    candidate: string,
    error: unknown,
  ): Promise<unknown> {
    if (error instanceof ConfirmedMutationRejectedError) {
      const normalizedCause = await this.normalizeAtomicNumberError(
        docType,
        candidate,
        error.cause,
      );
      return normalizedCause === error.cause
        ? error
        : new ConfirmedMutationRejectedError(normalizedCause);
    }

    const message =
      error && typeof error === 'object' && 'message' in error
        ? String((error as { message?: unknown }).message ?? '')
        : String(error ?? '');
    if (
      !candidate ||
      !/sales_(?:quote|order|delivery|invoice|credit_note)_number_conflict/i.test(
        message,
      )
    ) {
      return error;
    }

    const ctx = await this.resolveNumberingContext(docType);
    const existingValues = await ctx.readExistingValues();
    return new DocumentNumberConflictError(
      this.buildNumberConflict(docType, ctx, candidate, existingValues, null),
    );
  }

  /**
   * Reject an explicitly provided number that this series already issued —
   * inserting it would create a duplicate legal number. Values ahead of the
   * counter also advance the sequence so the next auto mint can't collide
   * with a hand-picked future number.
   */
  private async assertNumberAvailable(
    docType: SalesDocType,
    candidate: string,
  ): Promise<void> {
    const parsed = parseDocumentNumber(candidate);
    if (!parsed) return; // foreign format (migration import) — accept as-is
    const ctx = await this.resolveNumberingContext(docType);
    if (parsed.prefix !== ctx.prefix || parsed.year !== ctx.year) return; // other series
    const existingValues = await ctx.readExistingValues();
    if (existingValues.includes(parsed.value)) {
      throw new DocumentNumberConflictError(
        this.buildNumberConflict(docType, ctx, candidate, existingValues, null),
      );
    }
    // Best-effort forward claim — claimed=false just means the value sits in a
    // gap behind the counter, which is precisely what the user asked for.
    type ClaimRpc = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: { message: string } | null }>;
    };
    await (this.supabase as unknown as ClaimRpc)
      .rpc('claim_sequence_value', {
        seq_name: ctx.seqName,
        desired: parsed.value,
      })
      .catch(() => undefined);
  }

  private buildNumberConflict(
    docType: SalesDocType,
    ctx: { prefix: string; padding: number; year: number | null },
    proposed: string,
    existingValues: number[],
    seqNext: number | null,
  ): DocumentNumberConflictDetails {
    const maxExisting =
      existingValues.length === 0 ? 0 : Math.max(...existingValues);
    const nextFreeValue = Math.max(seqNext ?? 0, maxExisting + 1);
    return {
      docType,
      proposedNumber: proposed,
      nextFreeValue,
      nextFreeNumber: buildDocumentNumber(
        ctx.prefix,
        ctx.year,
        nextFreeValue,
        ctx.padding,
      ),
      prefix: ctx.prefix,
      padding: ctx.padding,
      year: ctx.year,
    };
  }

  async markAsInvoiced(id: string): Promise<void> {
    const { error } = await this.supabase
      .from(SALES_DOCUMENTS_WRITE)
      .update({ is_invoiced: true })
      .eq('id', id);
    if (error) throw error;
  }

  async getChildDocuments(
    parentId: string,
    allowedTypes?: readonly SalesDocType[],
  ): Promise<SalesDocument[]> {
    if (allowedTypes?.length === 0) return [];
    let query = (this.supabase as any)
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select(`*, ${SALES_DOCUMENT_LINES_EMBED}`)
      .or(`parent_doc_id.eq.${parentId},parent_doc_ids.cs.{${parentId}}`);
    if (allowedTypes) query = query.in('doc_type', allowedTypes);
    const { data, error } = await query
      .order('created_at', { ascending: false })
      .order('doc_date', { ascending: false });
    if (error) throw error;
    // ⚠ forme fléchée obligatoire depuis que mapSalesDocument prend un 2e
    // argument : en point-free, `Array.map` passerait l'INDEX comme `opts`.
    const docs = (data ?? []).map((row: Record<string, unknown>) =>
      mapSalesDocument(row),
    );
    return this.enrichDocumentsBatched(docs);
  }

  async getChildDocumentsOfMany(parentIds: string[]): Promise<SalesDocument[]> {
    const ids = [...new Set(parentIds.filter(Boolean))];
    if (ids.length === 0) return [];

    // Deux formes de filiation cohabitent : `parent_doc_id` (transformation
    // 1→1) et `parent_doc_ids` (regroupement). `ov` = les tableaux se
    // croisent — c'est le pendant ensembliste du `cs` de getChildDocuments.
    const byId = new Map<string, SalesDocument>();
    for (let i = 0; i < ids.length; i += PARENT_FILTER_CHUNK_SIZE) {
      const chunk = ids.slice(i, i + PARENT_FILTER_CHUNK_SIZE);
      const { data, error } = await this.supabase
        .from(AUTHORIZED_SALES_DOCUMENTS)
        .select(`*, ${SALES_DOCUMENT_LINES_EMBED}`)
        .or(
          `parent_doc_id.in.(${chunk.join(',')}),parent_doc_ids.ov.{${chunk.join(',')}}`,
        );
      if (error) throw error;
      // Un même enfant peut citer plusieurs parents du lot : dédoublonner.
      for (const row of (data ?? []) as Array<Record<string, unknown>>) {
        const doc = mapSalesDocument(row);
        byId.set(doc.id, doc);
      }
    }

    const docs = [...byId.values()];
    docs.sort(compareDocumentsNewestFirst);
    return this.enrichDocumentsBatched(docs);
  }

  async listCreditCompanions(
    documentIds: string[],
  ): Promise<SalesCreditCompanions> {
    // Dedupliquer sans trier preserve l'ordre stable fourni par le caller.
    const ids = [...new Set(documentIds.filter(Boolean))];
    if (ids.length === 0) return { creditNotes: [], applications: [] };

    const creditNotesById = new Map<string, SalesDocument>();
    const applicationsById = new Map<string, SalesCreditApplication>();
    for (const chunk of chunkIds(ids, CREDIT_COMPANION_CHUNK_SIZE)) {
      const { data, error } = await this.supabase.rpc(
        'list_sales_credit_companions' as never,
        { p_document_ids: chunk } as never,
      );
      if (error) throw error;
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        throw new SalesCreditCompanionsUnavailableError();
      }
      const payload = data as Record<string, unknown>;
      if (
        !Array.isArray(payload['credits']) ||
        !Array.isArray(payload['applications'])
      ) {
        throw new SalesCreditCompanionsUnavailableError();
      }
      // Calcul, application et impression exigent le snapshot financier
      // complet. Le chemin de filiation structurelle passe par la projection
      // de references ci-dessous et ne transforme jamais un masque en zero.
      if (
        payload['financial_payload_complete'] !== true &&
        (payload['credits'].length > 0 || payload['applications'].length > 0)
      ) {
        throw new SalesCreditCompanionsUnavailableError();
      }

      for (const rawCredit of payload['credits']) {
        if (
          !rawCredit ||
          typeof rawCredit !== 'object' ||
          Array.isArray(rawCredit)
        ) {
          throw new SalesCreditCompanionsUnavailableError();
        }
        const row = rawCredit as Record<string, unknown>;
        const id = row['id'];
        if (typeof id !== 'string' || row['doc_type'] !== 'credit_note') {
          throw new SalesCreditCompanionsUnavailableError();
        }
        for (const field of [
          'number',
          'doc_date',
          'customer_source',
          'total_ht',
          'total_discount',
          'document_discount',
          'total_vat',
          'total_ttc',
          'net_to_pay',
          'invoice_applied_ttc',
          'payment_applied_ttc',
        ]) {
          if (row[field] == null)
            throw new SalesCreditCompanionsUnavailableError();
        }
        const rawLines = row['lines'];
        if (!Array.isArray(rawLines))
          throw new SalesCreditCompanionsUnavailableError();
        for (const rawLine of rawLines) {
          if (
            !rawLine ||
            typeof rawLine !== 'object' ||
            Array.isArray(rawLine)
          ) {
            throw new SalesCreditCompanionsUnavailableError();
          }
          const line = rawLine as Record<string, unknown>;
          for (const field of [
            'id',
            'line_number',
            'description',
            'quantity',
            'unit_price_ht',
            'vat_rate',
            'total_ht',
            'total_ttc',
          ]) {
            if (line[field] == null)
              throw new SalesCreditCompanionsUnavailableError();
          }
        }
        const document = mapSalesDocument({
          ...row,
          sales_document_lines: rawLines.map((rawLine) => ({
            ...(rawLine as Record<string, unknown>),
            document_id: id,
          })),
        });
        creditNotesById.set(id, {
          ...document,
          invoiceAppliedTtc: Number(row['invoice_applied_ttc'] ?? 0),
          paymentAppliedTtc: Number(row['payment_applied_ttc'] ?? 0),
          creditCancelledAt:
            typeof row['credit_cancelled_at'] === 'string'
              ? row['credit_cancelled_at']
              : null,
          creditCancelledBy:
            typeof row['credit_cancelled_by'] === 'string'
              ? row['credit_cancelled_by']
              : null,
          creditCancellationReason:
            typeof row['credit_cancellation_reason'] === 'string'
              ? row['credit_cancellation_reason']
              : null,
        });
      }

      for (const rawApplication of payload['applications']) {
        if (
          !rawApplication ||
          typeof rawApplication !== 'object' ||
          Array.isArray(rawApplication)
        ) {
          throw new SalesCreditCompanionsUnavailableError();
        }
        const row = rawApplication as Record<string, unknown>;
        const id = row['id'];
        const rawLines = row['lines'];
        if (typeof id !== 'string' || !Array.isArray(rawLines)) {
          throw new SalesCreditCompanionsUnavailableError();
        }
        for (const field of [
          'credit_note_id',
          'invoice_id',
          'source_delivery_id',
          'credit_total_millimes',
          'consumed_from_millimes',
          'consumed_to_millimes',
          'applied_ht_before_discount',
          'applied_discount',
          'applied_ht',
          'applied_document_discount',
          'applied_net_ht',
          'applied_vat',
          'applied_ttc',
        ]) {
          if (row[field] == null)
            throw new SalesCreditCompanionsUnavailableError();
        }
        for (const rawLine of rawLines) {
          if (
            !rawLine ||
            typeof rawLine !== 'object' ||
            Array.isArray(rawLine)
          ) {
            throw new SalesCreditCompanionsUnavailableError();
          }
          const line = rawLine as Record<string, unknown>;
          for (const field of [
            'id',
            'credit_note_line_id',
            'line_number',
            'description',
            'applied_quantity',
            'unit_price_ht',
            'vat_rate',
            'applied_ht_before_discount',
            'applied_discount',
            'applied_ht',
            'applied_document_discount',
            'applied_net_ht',
            'applied_vat',
            'applied_ttc',
          ]) {
            if (line[field] == null)
              throw new SalesCreditCompanionsUnavailableError();
          }
        }
        applicationsById.set(
          id,
          mapSalesCreditApplication(
            {
              ...row,
              sales_credit_application_lines: rawLines.map((rawLine) => ({
                ...(rawLine as Record<string, unknown>),
                application_id: id,
              })),
            },
            (row['credit_note_number'] as string | null) ?? null,
          ),
        );
      }
    }

    const applications = [...applicationsById.values()];
    const applicationsByCreditId = new Map<string, SalesCreditApplication[]>();
    for (const application of applications) {
      const list = applicationsByCreditId.get(application.creditNoteId);
      if (list) list.push(application);
      else applicationsByCreditId.set(application.creditNoteId, [application]);
    }
    return {
      creditNotes: [...creditNotesById.values()].map((credit) => ({
        ...credit,
        creditApplications: applicationsByCreditId.get(credit.id) ?? [],
      })),
      applications,
    };
  }

  async listCreditCompanionReferences(
    documentIds: string[],
  ): Promise<SalesCreditCompanionReference[]> {
    const ids = [...new Set(documentIds.filter(Boolean))];
    if (ids.length === 0) return [];

    const byId = new Map<string, SalesCreditCompanionReference>();
    for (const chunk of chunkIds(ids, CREDIT_COMPANION_CHUNK_SIZE)) {
      const { data, error } = await this.supabase.rpc(
        'list_sales_credit_companion_references' as never,
        { p_document_ids: chunk } as never,
      );
      if (error) throw error;
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        throw new SalesCreditCompanionsUnavailableError();
      }
      const references = (data as Record<string, unknown>)['references'];
      if (!Array.isArray(references))
        throw new SalesCreditCompanionsUnavailableError();

      for (const rawCredit of references) {
        if (
          !rawCredit ||
          typeof rawCredit !== 'object' ||
          Array.isArray(rawCredit)
        ) {
          throw new SalesCreditCompanionsUnavailableError();
        }
        const row = rawCredit as Record<string, unknown>;
        if (
          typeof row['credit_note_id'] !== 'string' ||
          typeof row['number'] !== 'string'
        ) {
          throw new SalesCreditCompanionsUnavailableError();
        }
        const linkedDocumentIds = row['linked_document_ids'];
        if (!Array.isArray(linkedDocumentIds)) {
          throw new SalesCreditCompanionsUnavailableError();
        }
        const creditNoteId = row['credit_note_id'];
        const existing = byId.get(creditNoteId);
        byId.set(creditNoteId, {
          id: creditNoteId,
          docType: 'credit_note',
          number: row['number'],
          docDate: null,
          parentDocId: null,
          parentDocIds: [],
          linkedDocumentIds: [
            ...new Set([
              ...(existing?.linkedDocumentIds ?? []),
              ...linkedDocumentIds.filter(
                (linkedId): linkedId is string => typeof linkedId === 'string',
              ),
            ]),
          ],
          totalTtc: null,
        });
      }
    }
    return [...byId.values()];
  }

  async listCreditApplicationStatuses(
    creditNoteIds: string[],
  ): Promise<SalesCreditApplicationStatusSnapshot[]> {
    const ids = [...new Set(creditNoteIds.filter(Boolean))];
    if (ids.length === 0) return [];

    const byId = new Map<string, SalesCreditApplicationStatusSnapshot>();
    for (const chunk of chunkIds(ids, CREDIT_COMPANION_CHUNK_SIZE)) {
      const { data, error } = await this.supabase.rpc(
        'list_sales_credit_application_status' as never,
        { p_credit_note_ids: chunk } as never,
      );
      if (error) throw error;
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        throw new Error('sales_credit_status_unavailable');
      }
      const statuses = (data as Record<string, unknown>)['statuses'];
      if (!Array.isArray(statuses))
        throw new Error('sales_credit_status_unavailable');
      for (const rawStatus of statuses) {
        if (
          !rawStatus ||
          typeof rawStatus !== 'object' ||
          Array.isArray(rawStatus)
        ) {
          throw new Error('sales_credit_status_unavailable');
        }
        const row = rawStatus as Record<string, unknown>;
        const id = row['credit_note_id'];
        const status = row['status'];
        if (
          typeof id !== 'string' ||
          (status !== 'not_applied' &&
            status !== 'partially_applied' &&
            status !== 'fully_applied' &&
            status !== 'cancelled')
        ) {
          throw new Error('sales_credit_status_unavailable');
        }
        const money = (field: string): number | null => {
          if (row[field] == null) return null;
          const value = Number(row[field]);
          if (!Number.isFinite(value))
            throw new Error('sales_credit_status_unavailable');
          return value;
        };
        byId.set(id, {
          creditNoteId: id,
          totalTtc: money('total_ttc'),
          invoiceAppliedTtc: money('invoice_applied_ttc'),
          paymentAppliedTtc: money('payment_applied_ttc'),
          appliedTtc: money('applied_ttc'),
          remainingTtc: money('remaining_ttc'),
          status,
          cancelledAt:
            typeof row['cancelled_at'] === 'string'
              ? row['cancelled_at']
              : null,
          cancelledBy:
            typeof row['cancelled_by'] === 'string'
              ? row['cancelled_by']
              : null,
          cancellationReason:
            typeof row['cancellation_reason'] === 'string'
              ? row['cancellation_reason']
              : null,
        });
      }
    }
    return ids.map((id) => {
      const status = byId.get(id);
      if (!status) throw new Error('sales_credit_status_unavailable');
      return status;
    });
  }

  async cancelCreditNote(
    input: CancelSalesCreditNoteInput,
  ): Promise<SalesCreditCancellation> {
    const { data, error } = await this.supabase.rpc(
      'cancel_sales_credit_note_atomic' as never,
      {
        p_credit_note_id: input.creditNoteId,
        p_reason: input.reason?.trim() || null,
      } as never,
    );
    if (error) throw normalizeCreditCancellationError(error);
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new Error('sales_credit_cancellation_unavailable');
    }
    const row = data as Record<string, unknown>;
    if (
      typeof row['credit_note_id'] !== 'string' ||
      typeof row['cancelled_at'] !== 'string' ||
      typeof row['cancelled_by'] !== 'string' ||
      !Array.isArray(row['affected_article_ids'])
    ) {
      throw new Error('sales_credit_cancellation_unavailable');
    }
    const affectedArticleIds = row['affected_article_ids'].filter(
      (id): id is string => typeof id === 'string',
    );
    if (affectedArticleIds.length > 0) {
      await mirrorStorefrontStockForArticles(this.supabase, affectedArticleIds);
    }
    return {
      creditNoteId: row['credit_note_id'],
      cancelledAt: row['cancelled_at'],
      cancelledBy: row['cancelled_by'],
      reason: typeof row['reason'] === 'string' ? row['reason'] : null,
      affectedArticleIds,
    };
  }

  /**
   * `options.includeLines` est indispensable au calcul du BÉNÉFICE : sans les
   * lignes (donc sans les coûts d'achat figés dessus), le bénéfice d'un
   * document vaut structurellement 0. La statistique de caisse s'en sert pour
   * ne charger les lignes QUE des documents réellement encaissés sur la
   * session — jamais tout le catalogue de ventes.
   */
  async listDocumentsByIds(
    ids: string[],
    options?: { includeLines?: boolean },
  ): Promise<SalesDocument[]> {
    const unique = [...new Set(ids.filter(Boolean))];
    if (unique.length === 0) return [];

    const docs: SalesDocument[] = [];
    for (let i = 0; i < unique.length; i += IN_FILTER_CHUNK_SIZE) {
      const chunk = unique.slice(i, i + IN_FILTER_CHUNK_SIZE);
      const { data, error } = await this.supabase
        .from(AUTHORIZED_SALES_DOCUMENTS)
        .select(SALES_DOCUMENT_LIST_SELECT)
        .in('id', chunk);
      if (error) throw error;
      for (const row of (data ?? []) as Array<Record<string, unknown>>)
        docs.push(mapSalesDocument(row));
    }

    docs.sort(compareDocumentsNewestFirst);
    const enriched = await this.enrichDocumentsBatched(docs, {
      includeCreditApplications: options?.includeLines === true,
    });
    return this.attachLinesIfRequested(enriched, {
      includeLines: options?.includeLines,
    });
  }

  /**
   * Remises accordées à un client sur un article, les plus récentes d'abord.
   *
   * Requête unique : on part des DOCUMENTS (filtrés client + type + date, tous
   * indexés) et on embarque les lignes de l'article en jointure INTERNE — un
   * document sans ligne pour cet article disparaît du résultat. Partir des
   * lignes obligerait à trier sur une colonne de la table jointe, que PostgREST
   * n'ordonne pas au niveau racine.
   */
  async listClientArticleDiscounts(
    query: ClientArticleDiscountQuery,
  ): Promise<ClientArticleDiscount[]> {
    const { clientId, articleId } = query;
    if (!clientId || !articleId) return [];

    let request = (this.supabase as any)
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select(
        `
        id, number, doc_type, doc_date,
        sales_document_lines:authorized_sales_document_lines!inner(
          article_id, quantity, unit_price_ht, unit_price_ttc, vat_rate,
          discount_mode, discount_pct, discount_amount
        )
      `,
      )
      .eq('client_id', clientId)
      .eq('sales_document_lines.article_id', articleId)
      .in('doc_type', DISCOUNT_HISTORY_DOC_TYPES);
    if (query.since) request = request.gte('doc_date', query.since);
    const { data, error } = await request
      .order('doc_date', { ascending: false })
      .order('number', { ascending: false })
      .limit(query.limit && query.limit > 0 ? query.limit : 10);
    if (error) throw error;

    const entries: ClientArticleDiscount[] = [];
    for (const row of (data ?? []) as Array<Record<string, unknown>>) {
      const lines =
        (row['sales_document_lines'] as
          | Array<Record<string, unknown>>
          | undefined) ?? [];
      for (const line of lines) {
        entries.push({
          documentId: row['id'] as string,
          documentNumber: (row['number'] as string) ?? '',
          docType: row['doc_type'] as ClientArticleDiscount['docType'],
          docDate: (row['doc_date'] as string) ?? '',
          quantity: Number(line['quantity']) || 0,
          unitPriceHt: Number(line['unit_price_ht']) || 0,
          unitPriceTtc:
            line['unit_price_ttc'] == null
              ? null
              : Number(line['unit_price_ttc']),
          vatRate: Number(line['vat_rate']) || 0,
          discountMode:
            (line['discount_mode'] as ClientArticleDiscount['discountMode']) ??
            'pct',
          discountPct: Number(line['discount_pct']) || 0,
          discountAmount: Number(line['discount_amount']) || 0,
        });
      }
    }
    return entries;
  }

  /**
   * Attache en une passe les applications aux factures et aux avoirs présents
   * dans le lot. Les autres types ne déclenchent aucune requête.
   */
  private async attachCreditApplications(
    docs: SalesDocument[],
  ): Promise<SalesDocument[]> {
    const invoiceIds = docs
      .filter((doc) => doc.docType === 'invoice')
      .map((doc) => doc.id)
      .filter(Boolean);
    const relevantIds = docs
      .filter(
        (doc) => doc.docType === 'invoice' || doc.docType === 'credit_note',
      )
      .map((doc) => doc.id)
      .filter(Boolean);
    if (relevantIds.length === 0) return docs;
    const visibleCreditIds = docs
      .filter((doc) => doc.docType === 'credit_note')
      .map((doc) => doc.id)
      .filter(Boolean);

    // Autoritaire pour la filiation : contrairement a la vue generale des
    // avoirs, cette projection SECURITY DEFINER ne transforme pas un avoir
    // masque par `credit_notes:view` en absence. La lecture directe plus bas
    // reste utile aux roles autorises car elle porte le snapshot de benefice.
    const [safeCompanions, visibleCreditStatuses] = await Promise.all([
      invoiceIds.length > 0
        ? this.listCreditCompanions(invoiceIds).catch((error: unknown) => {
            // Les lectures ordinaires de liste/detail ne doivent pas disparaitre
            // parce qu'un role voit le lien mais pas ses montants. Les chemins
            // explicites de planification/impression appellent la methode
            // publique directement et restent, eux, fail-closed.
            if (error instanceof SalesCreditCompanionsUnavailableError) {
              return { creditNotes: [], applications: [] };
            }
            throw error;
          })
        : Promise.resolve({ creditNotes: [], applications: [] }),
      visibleCreditIds.length > 0
        ? this.listCreditApplicationStatuses(visibleCreditIds)
        : Promise.resolve([]),
    ]);
    const statusByCreditId = new Map(
      visibleCreditStatuses.map((status) => [status.creditNoteId, status]),
    );

    const rows: Array<Record<string, unknown>> = [];
    for (const chunk of chunkIds(
      [...new Set(relevantIds)],
      PARENT_FILTER_CHUNK_SIZE,
    )) {
      const { data, error } = await pendingSalesCreditSchema(this.supabase)
        .from('sales_credit_applications')
        .select('*, sales_credit_application_lines(*)')
        .or(
          `invoice_id.in.(${chunk.join(',')}),credit_note_id.in.(${chunk.join(',')})`,
        )
        .order('created_at', { ascending: true });
      if (error) throw error;
      rows.push(...((data ?? []) as Array<Record<string, unknown>>));
    }
    const creditIds = [
      ...new Set([
        ...rows.map((row) => row['credit_note_id'] as string).filter(Boolean),
        ...docs
          .filter((doc) => doc.docType === 'credit_note')
          .map((doc) => doc.id),
      ]),
    ];
    const creditNumberById = new Map<string, string>();
    for (const credit of safeCompanions.creditNotes) {
      creditNumberById.set(credit.id, credit.number);
    }
    const paymentAppliedByCredit = new Map<string, number>();
    for (const chunk of chunkIds(creditIds, IN_FILTER_CHUNK_SIZE)) {
      const { data, error } = await this.supabase
        .from(AUTHORIZED_SALES_DOCUMENTS)
        .select('id, number')
        .in('id', chunk);
      if (error) throw error;
      for (const row of (data ?? []) as Array<Record<string, unknown>>) {
        creditNumberById.set(row['id'] as string, row['number'] as string);
      }
    }
    for (const chunk of chunkIds(creditIds, IN_FILTER_CHUNK_SIZE)) {
      const { data, error } = await this.supabase
        .from('payment_allocations')
        .select('document_id, allocated_amount')
        .eq('document_type', 'credit_note')
        .in('document_id', chunk)
        .lt('allocated_amount', 0);
      if (error) throw error;
      for (const row of (data ?? []) as Array<Record<string, unknown>>) {
        const creditId = row['document_id'] as string;
        paymentAppliedByCredit.set(
          creditId,
          Math.round(
            ((paymentAppliedByCredit.get(creditId) ?? 0) +
              Math.abs(Number(row['allocated_amount'] ?? 0))) *
              1000,
          ) / 1000,
        );
      }
    }

    const applicationById = new Map(
      safeCompanions.applications.map((application) => [
        application.id,
        application,
      ]),
    );
    for (const row of rows) {
      const application = mapSalesCreditApplication(
        row,
        creditNumberById.get(row['credit_note_id'] as string) ?? null,
      );
      // La ligne autorisee porte appliedBenefit; elle remplace donc le
      // snapshot least-privilege du meme usage quand les deux sont visibles.
      applicationById.set(application.id, application);
    }
    const applications = [...applicationById.values()];
    const byDocumentId = new Map<string, SalesCreditApplication[]>();
    for (const application of applications) {
      for (const id of [application.invoiceId, application.creditNoteId]) {
        const list = byDocumentId.get(id);
        if (list) list.push(application);
        else byDocumentId.set(id, [application]);
      }
    }

    return docs.map((doc) => {
      if (doc.docType !== 'invoice' && doc.docType !== 'credit_note')
        return doc;
      const creditApplications = byDocumentId.get(doc.id) ?? [];
      const invoiceAppliedTtc =
        doc.docType === 'credit_note'
          ? Math.round(
              creditApplications.reduce(
                (sum, application) =>
                  application.reversedAt ? sum : sum + application.appliedTtc,
                0,
              ) * 1000,
            ) / 1000
          : undefined;
      const aggregateStatus =
        doc.docType === 'credit_note'
          ? statusByCreditId.get(doc.id)
          : undefined;
      return {
        ...doc,
        creditApplications,
        invoiceAppliedTtc: aggregateStatus
          ? (aggregateStatus.invoiceAppliedTtc ?? undefined)
          : invoiceAppliedTtc,
        paymentAppliedTtc:
          doc.docType === 'credit_note'
            ? aggregateStatus
              ? (aggregateStatus.paymentAppliedTtc ?? undefined)
              : (paymentAppliedByCredit.get(doc.id) ?? 0)
            : undefined,
        creditApplicationStatus: aggregateStatus?.status,
        creditCancelledAt:
          aggregateStatus?.cancelledAt ?? doc.creditCancelledAt ?? null,
        creditCancelledBy:
          aggregateStatus?.cancelledBy ?? doc.creditCancelledBy ?? null,
        creditCancellationReason:
          aggregateStatus?.cancellationReason ??
          doc.creditCancellationReason ??
          null,
      };
    });
  }

  /**
   * List screens need the derived status but not application line snapshots.
   * Keeping this path separate avoids loading financial companion payloads for
   * every invoice while preventing a used credit from falling back to
   * "not_applied" merely because the list query omitted lines.
   */
  private async attachCreditApplicationStatuses(
    docs: SalesDocument[],
  ): Promise<SalesDocument[]> {
    const creditIds = docs
      .filter((doc) => doc.docType === 'credit_note')
      .map((doc) => doc.id)
      .filter(Boolean);
    if (creditIds.length === 0) return docs;

    const statuses = await this.listCreditApplicationStatuses(creditIds);
    const byCreditId = new Map(
      statuses.map((status) => [status.creditNoteId, status]),
    );
    return docs.map((doc) => {
      if (doc.docType !== 'credit_note') return doc;
      const status = byCreditId.get(doc.id);
      if (!status) throw new Error('sales_credit_status_unavailable');
      return {
        ...doc,
        invoiceAppliedTtc: status.invoiceAppliedTtc ?? undefined,
        paymentAppliedTtc: status.paymentAppliedTtc ?? undefined,
        creditApplicationStatus: status.status,
        creditCancelledAt: status.cancelledAt,
        creditCancelledBy: status.cancelledBy,
        creditCancellationReason: status.cancellationReason,
      };
    });
  }

  // Single-doc enrichment fires 1–2 sub-queries. Mapping it over a list of N
  // docs via Promise.all (the old pattern) issued up to 2N parallel Supabase
  // requests per call — and the prewarm tick calls listDocuments every 5 min
  // with up to 500 rows per page. That stampede was overwhelming PostgREST
  // and surfacing as intermittent 500s on /rest/v1/sales_documents.
  // Collect every distinct customer id across the batch and resolve all
  // related rows in at most four `.in('id', [...])` queries.
  // ⚠ `clients` is read through `authorized_clients`, like every other client
  // read: the base table carries no column masking, so enriching from it handed
  // a restricted member the full client row (phone, email, notes) on every list
  // line. It is also what broke when 20260825100000 revoked base-table SELECT —
  // the throw below rejected the whole list query.
  private async enrichDocumentsBatched(
    docs: SalesDocument[],
    options?: {
      includeCreditApplications?: boolean;
      includeCustomers?: boolean;
    },
  ): Promise<SalesDocument[]> {
    if (docs.length === 0) return docs;

    const collect = (
      pick: (d: SalesDocument) => string | null | undefined,
    ): string[] => {
      const ids = new Set<string>();
      for (const d of docs) {
        const v = pick(d);
        if (v) ids.add(v);
      }
      return [...ids];
    };

    const includeCustomers = options?.includeCustomers !== false;
    const clientIds = includeCustomers ? collect((d) => d.clientId) : [];
    const onlineCustomerIds = includeCustomers
      ? collect((d) => d.onlineCustomerId ?? null)
      : [];
    const proCustomerIds = includeCustomers
      ? collect((d) => d.proCustomerId ?? null)
      : [];
    const proClientIds = includeCustomers
      ? collect((d) => d.proClientId ?? null)
      : [];

    type IdRows = {
      data: Array<Record<string, unknown>> | null;
      error: { message: string } | null;
    };
    const empty: IdRows = { data: [], error: null };
    const fetchIn = (table: string, ids: string[]): Promise<IdRows> => {
      if (ids.length === 0) return Promise.resolve(empty);
      return (this.supabase as any)
        .from(table)
        .select('*')
        .in('id', ids) as Promise<IdRows>;
    };

    const [clientsRes, onlineRes, proCustomerRes, proClientRes] =
      await Promise.all([
        fetchIn(AUTHORIZED_CLIENTS, clientIds),
        fetchIn('online_customers', onlineCustomerIds),
        fetchIn('pro_customers', proCustomerIds),
        fetchIn('pro_clients', proClientIds),
      ]);

    if (clientsRes.error) throw clientsRes.error;
    if (onlineRes.error) throw onlineRes.error;
    if (proCustomerRes.error) throw proCustomerRes.error;
    if (proClientRes.error) throw proClientRes.error;

    const toMap = <T>(
      rows: Array<Record<string, unknown>> | null,
      mapFn: (r: Record<string, unknown>) => T,
    ): Map<string, T> => {
      const m = new Map<string, T>();
      for (const r of rows ?? []) m.set(r['id'] as string, mapFn(r));
      return m;
    };
    const clientMap = toMap(clientsRes.data, mapClient);
    const onlineMap = toMap(onlineRes.data, mapOnlineCustomer);
    const proCustomerMap = toMap(proCustomerRes.data, mapProCustomer);
    const proClientMap = toMap(proClientRes.data, mapProClient);

    const enriched = includeCustomers
      ? docs.map((doc) => {
          if (doc.customerSource === 'pro' || doc.proCustomerId) {
            return {
              ...doc,
              proCustomer: doc.proCustomerId
                ? (proCustomerMap.get(doc.proCustomerId) ?? null)
                : null,
              proClient: doc.proClientId
                ? (proClientMap.get(doc.proClientId) ?? null)
                : null,
            };
          }
          if (doc.customerSource === 'online' || doc.onlineCustomerId) {
            return {
              ...doc,
              onlineCustomer: doc.onlineCustomerId
                ? (onlineMap.get(doc.onlineCustomerId) ?? null)
                : null,
            };
          }
          if (doc.clientId) {
            return { ...doc, client: clientMap.get(doc.clientId) };
          }
          return doc;
        })
      : docs;
    return options?.includeCreditApplications === false
      ? this.attachCreditApplicationStatuses(enriched)
      : this.attachCreditApplications(enriched);
  }

  private async enrichDocumentCustomer(
    doc: SalesDocument,
  ): Promise<SalesDocument> {
    if (doc.customerSource === 'pro' || doc.proCustomerId) {
      const [proCustomer, proClient] = await Promise.all([
        doc.proCustomerId
          ? (this.supabase as any)
              .from('pro_customers')
              .select('*')
              .eq('id', doc.proCustomerId)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
        doc.proClientId
          ? (this.supabase as any)
              .from('pro_clients')
              .select('*')
              .eq('id', doc.proClientId)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
      ]);
      if (proCustomer.error) throw proCustomer.error;
      if (proClient.error) throw proClient.error;
      return {
        ...doc,
        proCustomer: proCustomer.data ? mapProCustomer(proCustomer.data) : null,
        proClient: proClient.data ? mapProClient(proClient.data) : null,
      };
    }

    if (doc.customerSource === 'online' || doc.onlineCustomerId) {
      const { data, error } = doc.onlineCustomerId
        ? await (this.supabase as any)
            .from('online_customers')
            .select('*')
            .eq('id', doc.onlineCustomerId)
            .maybeSingle()
        : { data: null, error: null };
      if (error) throw error;
      return { ...doc, onlineCustomer: data ? mapOnlineCustomer(data) : null };
    }

    if (doc.clientId) {
      const { data, error } = await this.supabase
        .from(AUTHORIZED_CLIENTS)
        .select('*')
        .eq('id', doc.clientId)
        .maybeSingle();
      if (error) throw error;
      return { ...doc, client: data ? mapClient(data) : undefined };
    }

    return doc;
  }

  private isStorefrontOrder(doc: SalesDocument): boolean {
    return (
      doc.docType === 'order' &&
      (doc.channel === 'storefront' || Boolean(doc.onlineCustomerId))
    );
  }

  private async getLatestChildDelivery(
    orderId: string,
  ): Promise<SalesDocument | null> {
    const { data, error } = await this.supabase
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select(SALES_DOCUMENT_WRITE_SNAPSHOT_SELECT)
      .eq('doc_type', 'delivery')
      .or(`parent_doc_id.eq.${orderId},parent_doc_ids.cs.{${orderId}}`)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data ? mapSalesDocument(data) : null;
  }

  private async getStorefrontParentOrder(
    delivery: SalesDocument,
  ): Promise<SalesDocument | null> {
    const parentIds = delivery.parentDocIds?.length
      ? delivery.parentDocIds
      : delivery.parentDocId
        ? [delivery.parentDocId]
        : [];
    if (parentIds.length === 0) return null;

    const { data, error } = await this.supabase
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select(SALES_DOCUMENT_WRITE_SNAPSHOT_SELECT)
      .eq('doc_type', 'order')
      .in('id', parentIds);
    if (error) throw error;

    for (const row of data ?? []) {
      const order = mapSalesDocument(row);
      if (this.isStorefrontOrder(order)) return order;
    }
    return null;
  }

  /**
   * Commande boutique rejetée ou retournée : supprime ses BL enfants (en
   * pratique un seul — la création depuis la commande est bloquée dès qu'un
   * BL existe). Chaque BL passe par le garde-fou de
   * deleteDeliveryForCancelledOrder.
   */
  private async removeChildDeliveriesForCancelledOrder(
    orderId: string,
  ): Promise<void> {
    const { data, error } = await this.supabase
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select('id')
      .eq('doc_type', 'delivery')
      .or(`parent_doc_id.eq.${orderId},parent_doc_ids.cs.{${orderId}}`);
    if (error) throw error;
    for (const row of (data ?? []) as Array<Record<string, unknown>>) {
      await this.deleteDeliveryForCancelledOrder(row['id'] as string, orderId);
    }
  }

  /**
   * Règle « commande annulée ⇒ BL retiré » (boutique et Espace Pro, statuts
   * rejeté ET retourné) : supprime le BL d'une commande qui ne se réalisera
   * pas.
   *
   * La suppression passe par deleteDocument, qui inverse les effets de bord du
   * BL : mouvements de stock effacés (stock restauré), imputations
   * d'encaissement libérées (l'argent reçu reste, en crédit non affecté),
   * miroir de stock boutique rafraîchi.
   *
   * Refuse (retour false, sans erreur — l'annulation de la commande, elle,
   * doit aboutir) quand le BL est déjà engagé en aval :
   * - facturé (`is_invoiced`, y compris par facture groupée via
   *   parent_doc_ids) : la facture gouverne désormais ce BL ;
   * - référencé par un document enfant (facture/avoir) : la FK parent_doc_id
   *   (NO ACTION) ferait de toute façon échouer le DELETE, et un avoir
   *   perdrait sa source ;
   * - rattaché à un AUTRE parent que la commande annulée (BL groupé) : les
   *   autres commandes gardent leur BL.
   */
  async deleteDeliveryForCancelledOrder(
    deliveryId: string,
    cancelledParentId?: string,
  ): Promise<boolean> {
    return (
      await this.removeDeliveryForRemovedOrder(deliveryId, cancelledParentId)
    ).removed;
  }

  /**
   * Retire les BL d'une commande qu'on SUPPRIME, et refuse l'opération entière
   * si l'un d'eux est engagé en aval (facturé, porteur d'un avoir, ou partagé
   * avec une autre commande).
   *
   * Le refus est une ERREUR, pas un silence : contrairement à l'annulation par
   * statut — où la commande subsiste, affiche « Ouvrir le BL » et laisse
   * l'utilisateur trancher —, une suppression qui aboutirait à moitié ne
   * laisserait plus rien à l'écran pour signaler le BL resté derrière.
   *
   * Deux façons de désigner les BL, selon le côté :
   * - BOUTIQUE : `parentOrderId` — la commande est un `sales_documents`, ses BL
   *   sont ses enfants, et c'est aussi le parent à retirer pour arbitrer le cas
   *   « BL groupé ».
   * - ESPACE PRO : `deliveryIds` — la commande vit dans `pro_commandes` et n'est
   *   parente d'aucun document de vente. Sans `parentOrderId`, l'arbitrage
   *   « BL groupé » ne s'applique pas, exactement comme sur le chemin du rejet.
   */
  async removeDeliveriesForDeletedOrder(context: {
    scope: 'storefront' | 'pro';
    orderRef: string;
    /** Commande au sens `sales_documents` (boutique). */
    parentOrderId?: string;
    /** BL déjà connus de l'appelant (Espace Pro). */
    deliveryIds?: string[];
  }): Promise<void> {
    let deliveryIds = context.deliveryIds ?? null;
    if (!deliveryIds) {
      if (!context.parentOrderId) return;
      const { data, error } = await this.supabase
        .from(AUTHORIZED_SALES_DOCUMENTS)
        .select('id')
        .eq('doc_type', 'delivery')
        .or(
          `parent_doc_id.eq.${context.parentOrderId},parent_doc_ids.cs.{${context.parentOrderId}}`,
        );
      if (error) throw error;
      deliveryIds = ((data ?? []) as Array<Record<string, unknown>>).map(
        (r) => r['id'] as string,
      );
    }

    for (const deliveryId of deliveryIds) {
      const outcome = await this.removeDeliveryForRemovedOrder(
        deliveryId,
        context.parentOrderId,
      );
      // Un BL déjà disparu (`not_a_delivery`) ne bloque rien : il n'y a plus de
      // livraison à laisser orpheline. Les trois autres motifs, si.
      if (!outcome.removed && outcome.reason !== 'not_a_delivery') {
        throw new OrderDeletionBlockedError({
          scope: context.scope,
          orderRef: context.orderRef,
          deliveryNumber: outcome.number,
          reason: outcome.reason,
        });
      }
    }
  }

  /**
   * Ce qui, faute de clé étrangère, survivrait à une commande boutique
   * supprimée : le compteur de commandes du client en ligne, la notification
   * « nouvelle commande » (dont le lien mènerait à une page morte) et le panier
   * abandonné marqué récupéré par une commande qui n'existe plus.
   *
   * Aucun de ces trois nettoyages ne doit faire échouer la suppression : la
   * commande est DÉJÀ partie quand on arrive ici, et l'échec ne laisserait qu'un
   * compteur faux — pas une incohérence de stock ou de CA.
   */
  /** Paniers abandonnés que cette commande avait fait passer en « récupéré ». */
  private async findRecoveredCartIds(orderId: string): Promise<string[]> {
    try {
      const { data } = await (this.supabase as any)
        .from('abandoned_carts')
        .select('id')
        .eq('recovered_sales_document_id', orderId);
      return ((data ?? []) as Array<Record<string, unknown>>).map(
        (r) => r['id'] as string,
      );
    } catch {
      return [];
    }
  }

  private async cleanUpAfterStorefrontOrderDeleted(
    orderId: string,
    onlineCustomerId: string | null,
    recoveredCartIds: string[],
  ): Promise<void> {
    try {
      if (onlineCustomerId) {
        await new SupabaseOnlineCustomerRepository(
          this.supabase,
        ).recomputeAggregates(onlineCustomerId);
      }
    } catch {
      // Compteur non recalculé — sans effet sur la comptabilité.
    }

    try {
      await (this.supabase as any)
        .from('admin_notifications')
        .delete()
        .eq('entity_type', 'sales_document')
        .eq('entity_id', orderId);
    } catch {
      // Notification laissée en place : son lien tombera sur « introuvable ».
    }

    // La FK a déjà remis `recovered_sales_document_id` à NULL ; il reste le
    // statut, qui garderait le panier hors des relances pour une commande
    // effacée.
    if (recoveredCartIds.length > 0) {
      try {
        await (this.supabase as any)
          .from('abandoned_carts')
          .update({ status: 'active' })
          .in('id', recoveredCartIds);
      } catch {
        // Panier laissé « récupéré » : la relance ne le reproposera pas.
      }
    }
  }

  /**
   * Même opération que `deleteDeliveryForCancelledOrder`, mais qui RAPPORTE le
   * motif du refus au lieu de le taire.
   *
   * Les deux appelants n'attendent pas la même chose : l'annulation par statut
   * doit aboutir quoi qu'il arrive (le BL épargné reste visible et supprimable à
   * la main), tandis que la SUPPRESSION d'une commande doit s'interrompre et
   * dire pourquoi — sans quoi elle laisserait un BL orphelin, stock sorti, sans
   * plus rien pour l'expliquer.
   */
  private async removeDeliveryForRemovedOrder(
    deliveryId: string,
    removedParentId?: string,
  ): Promise<
    | { removed: true }
    | { removed: false; reason: DeliveryRemovalBlocker; number: string | null }
  > {
    const { data: doc, error } = await this.supabase
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select(
        'id, number, doc_type, is_invoiced, parent_doc_id, parent_doc_ids',
      )
      .eq('id', deliveryId)
      .maybeSingle();
    if (error) throw error;
    const row = (doc ?? null) as Record<string, unknown> | null;
    const number = (row?.['number'] as string | undefined) ?? null;

    // Le nombre d'enfants n'est lu que si les contrôles locaux passent : c'est
    // une requête de plus, inutile quand le BL est déjà refusé sur pièce.
    const localBlocker = deliveryRemovalBlocker({
      docType: (row?.['doc_type'] as string | undefined) ?? null,
      isInvoiced: (row?.['is_invoiced'] as boolean | undefined) ?? null,
      parentDocId:
        (row?.['parent_doc_id'] as string | null | undefined) ?? null,
      parentDocIds:
        (row?.['parent_doc_ids'] as string[] | null | undefined) ?? null,
      childCount: 0,
      removedParentId: removedParentId ?? null,
    });
    if (localBlocker) return { removed: false, reason: localBlocker, number };

    const { count, error: childError } = await this.supabase
      .from(AUTHORIZED_SALES_DOCUMENTS)
      .select('id', { count: 'exact', head: true })
      .or(`parent_doc_id.eq.${deliveryId},parent_doc_ids.cs.{${deliveryId}}`);
    if (childError) throw childError;
    if ((count ?? 0) > 0)
      return { removed: false, reason: 'has_children', number };

    await this.deleteDocument(deliveryId);
    return { removed: true };
  }

  private buildLinkedStorefrontWrite(
    target: SalesDocument,
    source: SalesDocument,
  ): { header: Record<string, unknown>; lines: Array<Record<string, unknown>> } {
    const payload: Record<string, unknown> = {
      customer_source: source.customerSource,
      client_id: source.clientId || null,
      online_customer_id: source.onlineCustomerId ?? null,
      pro_customer_id: source.proCustomerId ?? null,
      pro_client_id: source.proClientId ?? null,
      representative_id: source.representativeId ?? null,
      car_registration_number: source.carRegistrationNumber ?? null,
      // PAS de doc_date / validity_date ici : depuis que le BL est daté du jour
      // du TRANSFERT (et non du jour de la commande), les deux documents ont
      // légitimement des dates différentes. Les miroiter reviendrait à redater
      // la commande storefront au jour où on crée son BL — et inversement, à
      // écraser la date de transfert du BL à la première édition de la commande.
      notes: source.notes,
      vat_exempt: source.vatExempt ?? false,
      total_ht: source.totalHt,
      total_discount: source.totalDiscount,
      document_discount_mode: source.documentDiscountMode ?? 'amount',
      document_discount_value: source.documentDiscountValue ?? 0,
      document_discount: source.documentDiscount ?? 0,
      total_vat: source.totalVat,
      total_ttc: source.totalTtc,
      withholding_amount: source.withholdingAmount,
      stamp_amount: source.stampAmount,
      ...fodecColumns(source),
      delivery_fee: source.deliveryFee,
      net_to_pay: source.netToPay,
    };

    if (target.docType === 'delivery' && source.channel) {
      payload['channel'] = source.channel;
    }

    // Swap atomique (RPC sales_overwrite_document_lines_atomic, mig
    // 20260719130000) : l'UPDATE du header + le DELETE/INSERT des lignes
    // commitent ou échouent ENSEMBLE. L'ancien enchaînement non transactionnel
    // (trois allers-retours supabase-js) pouvait laisser la cible avec ZÉRO
    // ligne si le ré-INSERT échouait après le DELETE — la fragilité qui a wipé
    // les lignes des commandes Pro (voir 20260707100000). line_number est
    // attribué par la RPC dans l'ordre du tableau.
    const relatedLines = (source.lines ?? []).map((line) => source.docType === 'order'
        ? target.lines?.find((candidate) => candidate.sourceLineId === line.id)
        : target.lines?.find((candidate) => candidate.id === line.sourceLineId));
    const reservedTargetIds = new Set(relatedLines.flatMap((line) => line ? [line.id] : []));
    const lines = (source.lines ?? []).map((line, index) => {
      const related = relatedLines[index];
      const positional = target.lines?.[index];
      const existing = related ?? (positional && !reservedTargetIds.has(positional.id)
        && positional.articleId === line.articleId
        && positional.variantId === line.variantId
        // A newly allocated order row also needs a new derived BL identity.
        // Reusing a sourced child would rewrite its frozen consumption link.
        && !(source.docType === 'order' && positional.sourceLineId
          && positional.sourceLineId !== line.id) ? positional : undefined);
      const targetId = existing?.id ?? globalThis.crypto.randomUUID();
      reservedTargetIds.add(targetId);
      return {
      id: targetId,
      article_id: line.articleId,
      variant_id: line.variantId ?? null,
      variant_label: line.variantLabel ?? null,
      description: line.description,
      quantity: line.quantity,
      unit_price_ht: line.unitPriceHt,
      unit_price_ttc: line.unitPriceTtc ?? null,
      purchase_price: line.purchasePrice ?? 0,
      discount_pct: line.discountPct ?? 0,
      // Sans eux, une remise en DT (mode 'amount') redevenait silencieusement
      // une remise % lors du miroir commande ↔ BL storefront.
      discount_mode: line.discountMode ?? 'pct',
      discount_amount: line.discountAmount ?? 0,
      vat_rate: line.vatRate,
      total_ht: line.totalHt ?? 0,
      total_ttc: line.totalTtc ?? 0,
      unit_label: line.unitLabel ?? null,
      unit_factor: line.unitFactor ?? 1,
      source_doc_id: target.docType === 'delivery' && source.docType === 'order'
        ? source.id : existing?.sourceDocId ?? null,
      source_line_id: target.docType === 'delivery' && source.docType === 'order'
        ? line.id : existing?.sourceLineId ?? null,
    };
    });
    return { header: payload, lines };
  }

  private async syncLinkedProCommandeFromDelivery(
    doc: SalesDocument,
  ): Promise<void> {
    if (doc.docType !== 'delivery' || !doc.proCommandeCode) return;

    const { data: commande, error: commandeError } = await (
      this.supabase as any
    )
      .from('pro_commandes')
      .select('code, tenant_id, pro_customer_id, total_used')
      .eq('code', doc.proCommandeCode)
      .maybeSingle();
    if (commandeError) throw commandeError;
    if (!commande) return;

    const lines = doc.lines ?? [];
    const missingArticle = lines.find((line) => !line.articleId);
    if (missingArticle) {
      throw new Error(
        'Les lignes du BL lie a une commande Pro doivent garder un article.',
      );
    }

    const articleIds = [
      ...new Set(lines.map((line) => line.articleId).filter(Boolean)),
    ] as string[];
    const { data: articles, error: articlesError } =
      articleIds.length > 0
        ? await (this.supabase as any)
            .from(AUTHORIZED_ARTICLES)
            .select('id, purchase_price_ht, supplier_discount')
            .in('id', articleIds)
        : { data: [], error: null };
    if (articlesError) throw articlesError;
    const netCostByArticle = new Map<string, number>();
    for (const row of (articles ?? []) as Array<Record<string, unknown>>) {
      const net = netUnitCostHt({
        purchasePriceHt: Number(row['purchase_price_ht'] ?? 0),
        supplierDiscount: Number(row['supplier_discount'] ?? 0),
      });
      if (net != null) netCostByArticle.set(row['id'] as string, net);
    }

    const { data: proCustomer, error: proCustomerError } = await (
      this.supabase as any
    )
      .from('pro_customers')
      .select('commission_rate')
      .eq('id', commande.pro_customer_id)
      .maybeSingle();
    if (proCustomerError) throw proCustomerError;
    const commissionRate = Number(proCustomer?.commission_rate ?? 0);

    // Commission & reste-à-payer : calcul métier pur dans @erp/domain/sales. Le
    // repository ne fait que résoudre le coût net par ligne (snapshot figé de la
    // ligne, sinon catalogue net de remise fournisseur). Le coût résolu est
    // AUSSI re-figé sur les lignes de la commande Pro plus bas, pour que tout
    // recalcul ultérieur reparte du même coût.
    const resolvedCostByLine = new Map<SalesDocumentLine, number | null>();
    for (const line of lines) {
      if (!line.articleId) continue;
      const snapshot = Number(line.purchasePrice);
      resolvedCostByLine.set(
        line,
        snapshot > 0
          ? snapshot
          : (netCostByArticle.get(line.articleId) ?? null),
      );
    }
    const commissionLines: SalesBenefitLineInput[] = lines
      .filter((line) => line.articleId)
      .map((line) => ({
        quantity: line.quantity,
        unitPriceHt: line.unitPriceHt,
        // Ancre TTC : sans elle (et son taux), le revenu d'une ligne au prix
        // catalogue TTC serait recalculé côté HT arrondi → commission ≠
        // bénéfice affiché sur le document.
        unitPriceTtc: line.unitPriceTtc,
        vatRate: line.vatRate,
        discountPct: line.discountPct,
        discountMode: line.discountMode ?? 'pct',
        discountAmount: line.discountAmount ?? 0,
        unitCostHt: resolvedCostByLine.get(line),
      }));
    const totalCommission = calculateProCommission(
      commissionLines,
      commissionRate,
    );
    const totalUsed = Number(commande.total_used ?? 0);
    const totalAPayer = computeProAmountDue(
      doc.totalTtc,
      doc.deliveryFee,
      totalUsed,
    );

    // Swap atomique : l'UPDATE du header + le DELETE/INSERT des lignes tournent
    // dans UNE transaction (RPC pro_sync_commande_lines_atomic, mig
    // 20260719130000). Avant, c'étaient trois appels supabase-js séparés : si
    // l'INSERT était rejeté APRÈS le DELETE commité (price guard, FK sur un
    // article supprimé, RLS, réseau), la commande restait avec ZÉRO ligne —
    // c'est ce qui a wipé CMD-2026-0003 / CMD-2026-0013 (voir 20260707100000).
    // Le calcul métier (commission, reste-à-payer, montant_remise, coût net
    // figé) reste ici / dans @erp/domain ; la RPC ne fait qu'écrire les valeurs
    // déjà calculées. line_number est attribué par la RPC dans l'ordre du
    // tableau.
    const header = {
      pht: doc.totalHt,
      pttc: doc.totalTtc,
      tva: doc.totalVat,
      total_commission: round3(totalCommission),
      delivery_fee: doc.deliveryFee,
      total_a_payer: round3(totalAPayer),
      notes: doc.notes,
      delivery_sales_document_id: doc.id,
    };
    const rpcLines = lines.map((line) => {
      const grossHt = line.unitPriceHt * line.quantity;
      return {
        article_id: line.articleId,
        quantite: line.quantity,
        pht: line.totalHt,
        pttc: line.totalTtc,
        tva: line.vatRate,
        remise: line.discountPct,
        montant_remise: round3(Math.max(0, grossHt - line.totalHt)),
        // Coût net figé (snapshot de la ligne, sinon catalogue net) — sans lui
        // le swap effacerait le gel du coût et re-créerait le bug de la
        // commission remise à zéro (voir 20260713100000).
        unit_cost_ht: resolvedCostByLine.get(line) ?? null,
      };
    });
    const { error: syncError } = await (this.supabase as any).rpc(
      'pro_sync_commande_lines_atomic',
      {
        p_code: doc.proCommandeCode,
        p_header: header,
        p_lines: rpcLines,
      },
    );
    if (syncError) throw syncError;

    // La commission PENDING du portefeuille suit le montant recalculé (c'est le
    // montant que l'admin approuvera). Une commission déjà approuvée — argent
    // crédité — n'est jamais retouchée.
    const { error: movementError } = await (this.supabase as any)
      .from('pro_wallet_movements')
      .update({ amount: round3(totalCommission) })
      .eq('reference_code', doc.proCommandeCode)
      .eq('type', 'commission')
      .eq('status', 'pending');
    if (movementError) throw movementError;
  }
}

function round3(value: number): number {
  return Math.round((value + Number.EPSILON) * 1000) / 1000;
}

/**
 * Coût unitaire net HT (snapshot) à figer sur chaque ligne au moment de
 * l'enregistrement.
 *
 * - Une ligne qui porte déjà un snapshot (> 0) le conserve → le bénéfice est
 *   GELÉ à la date de vente, il ne bougera pas si le prix d'achat de l'article
 *   change ensuite.
 * - Sinon on complète depuis le catalogue, net de la remise fournisseur, pour
 *   que le bénéfice soit correct quel que soit le client qui a créé le document
 *   (web, mobile, POS…). Le calcul du coût net vit dans @erp/domain/sales.
 */
async function resolveNetUnitCosts(
  supabase: TypedSupabaseClient,
  lines: ReadonlyArray<{
    articleId?: string | null;
    purchasePrice?: number | null;
  }>,
): Promise<number[]> {
  const needArticleIds = [
    ...new Set(
      lines
        .filter((l) => l.articleId && !(Number(l.purchasePrice) > 0))
        .map((l) => l.articleId as string),
    ),
  ];

  const netByArticle = new Map<string, number>();
  if (needArticleIds.length > 0) {
    const { data, error } = await (supabase as any)
      .from(AUTHORIZED_ARTICLES)
      .select('id, purchase_price_ht, supplier_discount')
      .in('id', needArticleIds);
    if (error) throw error;
    for (const row of (data ?? []) as Array<Record<string, unknown>>) {
      const net = netUnitCostHt({
        purchasePriceHt: Number(row['purchase_price_ht'] ?? 0),
        supplierDiscount: Number(row['supplier_discount'] ?? 0),
      });
      if (net != null) netByArticle.set(row['id'] as string, net);
    }
  }

  return lines.map((l) => {
    const snapshot = Number(l.purchasePrice);
    if (Number.isFinite(snapshot) && snapshot > 0) return round3(snapshot);
    if (l.articleId) return netByArticle.get(l.articleId) ?? 0;
    return 0;
  });
}

function applySalesDocumentFilters(
  query: any,
  filters?: SalesDocumentFilters,
): any {
  // Delta du miroir hors-ligne — voir `updatedSince` dans les filtres.
  if (filters?.updatedSince)
    query = query.gt('updated_at', filters.updatedSince);
  if (filters?.docType) query = query.eq('doc_type', filters.docType);
  if (filters?.search?.trim())
    query = query.or(orContainsAnyColumn(['number'], filters.search.trim()));
  if (filters?.clientIds?.length)
    query = query.in('client_id', filters.clientIds);
  else if (filters?.clientId) query = query.eq('client_id', filters.clientId);
  if (filters?.representativeId)
    query = query.eq('representative_id', filters.representativeId);
  if (filters?.onlineCustomerId)
    query = query.eq('online_customer_id', filters.onlineCustomerId);
  if (filters?.storefrontOnly) {
    query = query.or('channel.eq.storefront,online_customer_id.not.is.null');
  }
  if (filters?.dateFrom) query = query.gte('doc_date', filters.dateFrom);
  if (filters?.dateTo) query = query.lte('doc_date', filters.dateTo);
  if (filters?.originStoreId !== undefined) {
    if (filters.originStoreId === null) {
      query = query.is('origin_store_id', null);
    } else if (filters.includeGlobalOrigin) {
      query = query.or(
        `${orEqualsAnyColumn(['origin_store_id'], filters.originStoreId)},origin_store_id.is.null`,
      );
    } else {
      query = query.eq('origin_store_id', filters.originStoreId);
    }
  }
  if (filters?.isInvoiced !== undefined)
    query = query.eq('is_invoiced', filters.isInvoiced);
  // `parent_doc_ids` porte TOUJOURS la filiation (l'écriture y recopie
  // `parent_doc_id`), donc un simple chevauchement de tableaux suffit — pas
  // besoin d'un `or()` sur les deux colonnes.
  if (filters?.parentDocIdIn?.length) {
    query = query.overlaps('parent_doc_ids', filters.parentDocIdIn);
  }
  // Une liste d'ids VIDE demande zéro document, elle ne dit pas « pas de
  // filtre » : `in.()` est justement ce que PostgREST comprend comme « aucun ».
  // (Même contrat que `ArticleFilters.ids`.)
  if (filters?.ids) query = query.in('id', filters.ids);
  return query;
}

/**
 * Colonnes FODEC d'un payload de document — seulement quand le document en
 * porte un (taux > 0). Un document sans FODEC laisse ses colonnes à leur
 * défaut (0), et le payload reste accepté par une base pas encore migrée :
 * la mise à jour atomique (private.update_sales_document_snapshot) refuse
 * toute clé qu'elle ne connaît pas, et le bundle peut être déployé avant la
 * migration 20260907140000_fodec_sales_documents.sql sans casser les
 * éditions de documents. Une fois figé, le taux ne revient jamais à 0 par une
 * mise à jour (voir SalesDocumentForm : un document enregistré garde son taux).
 */
function fodecColumns(
  source: { fodecRate?: number | null; fodecAmount?: number | null },
): { fodec_rate?: number; fodec_amount?: number } {
  const rate = Number(source.fodecRate);
  if (!Number.isFinite(rate) || rate <= 0) return {};
  const amount = Number(source.fodecAmount);
  return {
    fodec_rate: rate,
    fodec_amount: Number.isFinite(amount) && amount > 0 ? amount : 0,
  };
}

// UI column key → sales_documents DB column, for server-side ordering.
// Computed/joined columns (client name, benefit, payment status, parent doc)
// are intentionally absent — those stay unsorted (default creation order).
const SALES_SORT_COLUMNS: Record<string, string> = {
  number: 'number',
  docDate: 'doc_date',
  validityDate: 'validity_date',
  totalHt: 'total_ht',
  totalDiscount: 'total_discount',
  totalVat: 'total_vat',
  totalTtc: 'total_ttc',
  withholdingAmount: 'withholding_amount',
  stampAmount: 'stamp_amount',
  deliveryFee: 'delivery_fee',
  netToPay: 'net_to_pay',
  isInvoiced: 'is_invoiced',
  channel: 'channel',
  proCommandeCode: 'pro_commande_code',
  carRegistrationNumber: 'car_registration_number',
  notes: 'notes',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
  // Hour-only display columns — they sort by the timestamp they render.
  createdTime: 'created_at',
  updatedTime: 'updated_at',
};

function applySalesDocumentSort(
  query: any,
  sort?: { field: string; direction: 'asc' | 'desc' },
): any {
  const column = sort ? SALES_SORT_COLUMNS[sort.field] : undefined;
  if (column) {
    query = query.order(column, { ascending: sort!.direction === 'asc' });
    // Deterministic tiebreaker so equal values keep a stable page order.
    if (column !== 'created_at')
      query = query.order('created_at', { ascending: false });
    // …but `created_at` is NOT unique either (legacy imports share a
    // transaction timestamp) — see withStableOrder.
    return withStableOrder(query);
  }
  // Default: newest CREATED first, `doc_date` en retombée — voir
  // compareDocumentsNewestFirst pour les deux raisons.
  return withStableOrder(
    query
      .order('created_at', { ascending: false })
      .order('doc_date', { ascending: false }),
  );
}

export function mapSalesDocumentSummary(
  row: Record<string, unknown>,
): SalesDocumentSummary {
  return {
    id: row['id'] as string,
    number: (row['number'] as string) ?? '',
    docType: row['doc_type'] as SalesDocumentSummary['docType'],
    docDate: row['doc_date'] as string,
    clientId: (row['client_id'] as string) ?? '',
    originStoreId: (row['origin_store_id'] as string | null) ?? null,
    channel: (row['channel'] as string | null) ?? null,
    isInvoiced: (row['is_invoiced'] as boolean) ?? false,
    parentDocId: (row['parent_doc_id'] as string | null) ?? null,
    parentDocIds: (row['parent_doc_ids'] as string[] | null) ?? [],
    totalTtc: Number(row['total_ttc']) || 0,
    createdAt: (row['created_at'] as string) ?? '',
  };
}

function mapSalesDocument(
  row: Record<string, unknown>,
  opts?: { supabase: TypedSupabaseClient },
): SalesDocument {
  const lines =
    (row['sales_document_lines'] as Record<string, unknown>[] | undefined) ??
    [];
  // Le résolveur d'images a besoin du tenant : il vit sur la ligne parente
  // (`select('*')`), pas sur l'embed article.
  const imageOpts = opts
    ? {
        supabase: opts.supabase,
        tenantId: (row['tenant_id'] as string | null) ?? null,
      }
    : undefined;
  return {
    id: row['id'] as string,
    docType: row['doc_type'] as SalesDocument['docType'],
    number: row['number'] as string,
    clientId: (row['client_id'] as string) ?? '',
    originStoreId: (row['origin_store_id'] as string | null) ?? null,
    stockWarehouseId: (row['stock_warehouse_id'] as string | null) ?? null,
    cashRegisterId: (row['cash_register_id'] as string | null) ?? null,
    customerSource:
      (row['customer_source'] as SalesDocument['customerSource']) ??
      inferCustomerSource(row),
    onlineCustomerId: (row['online_customer_id'] as string) ?? null,
    proCustomerId: (row['pro_customer_id'] as string) ?? null,
    proClientId: (row['pro_client_id'] as string) ?? null,
    channel: (row['channel'] as string) ?? 'local',
    representativeId: (row['representative_id'] as string) ?? null,
    carRegistrationNumber: (row['car_registration_number'] as string) ?? null,
    docDate: row['doc_date'] as string,
    validityDate: (row['validity_date'] as string) ?? null,
    parentDocId: (row['parent_doc_id'] as string) ?? null,
    parentDocIds: (row['parent_doc_ids'] as string[]) ?? [],
    isInvoiced: (row['is_invoiced'] as boolean) ?? false,
    storefrontOrderStatus:
      (row[
        'storefront_order_status'
      ] as SalesDocument['storefrontOrderStatus']) ?? null,
    paymentMethod:
      (row['payment_method'] as SalesDocument['paymentMethod']) ?? null,
    paymentStatus:
      (row['payment_status'] as SalesDocument['paymentStatus']) ?? null,
    proCommandeCode: (row['pro_commande_code'] as string) ?? null,
    vatExempt: (row['vat_exempt'] as boolean) ?? false,
    totalHt: (row['total_ht'] as number) ?? 0,
    totalDiscount: (row['total_discount'] as number) ?? 0,
    documentDiscountMode:
      (row[
        'document_discount_mode'
      ] as SalesDocument['documentDiscountMode']) ?? 'amount',
    documentDiscountValue: Number(row['document_discount_value'] ?? 0),
    documentDiscount: Number(row['document_discount'] ?? 0),
    totalVat: (row['total_vat'] as number) ?? 0,
    totalTtc: (row['total_ttc'] as number) ?? 0,
    withholdingAmount: (row['withholding_amount'] as number) ?? 0,
    stampAmount: (row['stamp_amount'] as number) ?? 0,
    fodecRate: Number(row['fodec_rate'] ?? 0),
    fodecAmount: Number(row['fodec_amount'] ?? 0),
    deliveryFee: Number(row['delivery_fee'] ?? 0),
    netToPay: (row['net_to_pay'] as number) ?? 0,
    notes: (row['notes'] as string) ?? null,
    customProperties:
      (row['custom_properties'] as SalesDocument['customProperties']) ?? {},
    createdBy: (row['created_by'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    creditCancelledAt: (row['credit_cancelled_at'] as string | null) ?? null,
    creditCancelledBy: (row['credit_cancelled_by'] as string | null) ?? null,
    creditCancellationReason:
      (row['credit_cancellation_reason'] as string | null) ?? null,
    // ⚠ forme fléchée obligatoire : en point-free, `Array.map` passerait
    // l'INDEX en second argument, donc comme `opts`.
    // L'embed PostgREST n'a pas d'ORDER BY — voir sortByLineNumber.
    lines: sortByLineNumber(lines.map((line) => mapSalesLine(line, imageOpts))),
    client: row['client']
      ? mapClient(row['client'] as Record<string, unknown>)
      : undefined,
    onlineCustomer: row['online_customer']
      ? mapOnlineCustomer(row['online_customer'] as Record<string, unknown>)
      : null,
    proCustomer: row['pro_customer']
      ? mapProCustomer(row['pro_customer'] as Record<string, unknown>)
      : null,
    proClient: row['pro_client']
      ? mapProClient(row['pro_client'] as Record<string, unknown>)
      : null,
  };
}

function mapSalesCreditApplication(
  row: Record<string, unknown>,
  creditNoteNumber: string | null,
): SalesCreditApplication {
  const lineRows =
    (row['sales_credit_application_lines'] as
      | Array<Record<string, unknown>>
      | undefined) ?? [];
  return {
    id: row['id'] as string,
    creditNoteId: row['credit_note_id'] as string,
    creditNoteNumber,
    invoiceId: row['invoice_id'] as string,
    sourceDeliveryId: row['source_delivery_id'] as string,
    clientRequestId: row['client_request_id'] as string,
    consumedFromTtc: Number(row['consumed_from_millimes'] ?? 0) / 1000,
    consumedToTtc: Number(row['consumed_to_millimes'] ?? 0) / 1000,
    creditTotalTtc: Number(row['credit_total_millimes'] ?? 0) / 1000,
    appliedHtBeforeDiscount: Number(row['applied_ht_before_discount'] ?? 0),
    appliedDiscount: Number(row['applied_discount'] ?? 0),
    appliedHt: Number(row['applied_ht'] ?? 0),
    appliedDocumentDiscount: Number(row['applied_document_discount'] ?? 0),
    appliedNetHt: Number(row['applied_net_ht'] ?? 0),
    appliedVat: Number(row['applied_vat'] ?? 0),
    appliedTtc: Number(row['applied_ttc'] ?? 0),
    appliedBenefit:
      row['applied_benefit'] == null ? null : Number(row['applied_benefit']),
    reversedAt: (row['reversed_at'] as string | null) ?? null,
    createdAt: row['created_at'] as string,
    lines: lineRows
      .map(mapSalesCreditApplicationLine)
      .sort((a, b) => a.lineNumber - b.lineNumber),
  };
}

function mapSalesCreditApplicationLine(
  row: Record<string, unknown>,
): SalesCreditApplicationLine {
  return {
    id: row['id'] as string,
    applicationId: row['application_id'] as string,
    creditNoteLineId: row['credit_note_line_id'] as string,
    sourceLineId: (row['source_line_id'] as string | null) ?? null,
    lineNumber: Number(row['line_number'] ?? 0),
    articleId: (row['article_id'] as string | null) ?? null,
    description: (row['description'] as string) ?? '',
    appliedQuantity: Number(row['applied_quantity'] ?? 0),
    unitPriceHt: Number(row['unit_price_ht'] ?? 0),
    unitPriceTtc:
      row['unit_price_ttc'] == null ? null : Number(row['unit_price_ttc']),
    vatRate: Number(row['vat_rate'] ?? 0),
    appliedHtBeforeDiscount: Number(row['applied_ht_before_discount'] ?? 0),
    appliedDiscount: Number(row['applied_discount'] ?? 0),
    appliedHt: Number(row['applied_ht'] ?? 0),
    appliedDocumentDiscount: Number(row['applied_document_discount'] ?? 0),
    appliedNetHt: Number(row['applied_net_ht'] ?? 0),
    appliedVat: Number(row['applied_vat'] ?? 0),
    appliedTtc: Number(row['applied_ttc'] ?? 0),
    appliedBenefit:
      row['applied_benefit'] == null ? null : Number(row['applied_benefit']),
  };
}

function inferCustomerSource(
  row: Record<string, unknown>,
): SalesDocument['customerSource'] {
  if (row['pro_customer_id'] || row['pro_commande_code']) return 'pro';
  if (row['online_customer_id']) return 'online';
  return 'local';
}

function mapClient(row: Record<string, unknown>): SalesDocument['client'] {
  return {
    id: row['id'] as string,
    code: row['code'] as string,
    name: row['name'] as string,
    type: row['type'] as 'company' | 'individual',
    taxId: (row['tax_id'] as string) ?? null,
    address: (row['address'] as string) ?? null,
    city: (row['city'] as string) ?? null,
    phone: (row['phone'] as string) ?? null,
    fax: (row['fax'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    defaultDiscountPct: Number(row['default_discount_pct'] ?? 0),
    paymentTermsDays: Number(row['payment_terms_days'] ?? 0),
    notes: (row['notes'] as string) ?? null,
    isDefault: Boolean(row['is_default']),
    carRegistrationNumber: (row['car_registration_number'] as string) ?? null,
    proClientId: (row['pro_client_id'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapOnlineCustomer(
  row: Record<string, unknown>,
): NonNullable<SalesDocument['onlineCustomer']> {
  return {
    id: row['id'] as string,
    phone: row['phone'] as string,
    email: (row['email'] as string) ?? null,
    firstName: (row['first_name'] as string) ?? null,
    lastName: (row['last_name'] as string) ?? null,
    fullName: (row['full_name'] as string) ?? null,
    governorate: (row['governorate'] as string) ?? null,
    city: (row['city'] as string) ?? null,
    address: (row['address'] as string) ?? null,
    primaryCity: (row['primary_city'] as string) ?? null,
    preferredLanguage: (row['preferred_language'] as string) ?? 'fr',
    totalOrders: Number(row['total_orders'] ?? 0),
    totalSpent: Number(row['total_spent'] ?? 0),
    firstOrderAt: (row['first_order_at'] as string) ?? null,
    lastOrderAt: (row['last_order_at'] as string) ?? null,
    isBlocked: Boolean(row['is_blocked']),
    blockReason: (row['block_reason'] as string) ?? null,
    blockedAt: (row['blocked_at'] as string) ?? null,
    linkedCustomerId: (row['linked_customer_id'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    firstUtmSource: (row['first_utm_source'] as string) ?? null,
    firstUtmCampaign: (row['first_utm_campaign'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapProCustomer(
  row: Record<string, unknown>,
): NonNullable<SalesDocument['proCustomer']> {
  return {
    id: row['id'] as string,
    tenantId: row['tenant_id'] as string,
    userId: row['user_id'] as string,
    firstName: row['first_name'] as string,
    lastName: row['last_name'] as string,
    email: row['email'] as string,
    phone: (row['phone'] as string) ?? null,
    whatsapp: (row['whatsapp'] as string) ?? null,
    countryCode: (row['country_code'] as string) ?? 'TN',
    status: row['status'] as NonNullable<
      SalesDocument['proCustomer']
    >['status'],
    profilePictureUrl: (row['profile_picture_url'] as string) ?? null,
    registrationData:
      (row['registration_data'] as Record<string, unknown>) ?? null,
    commissionRate: Number(row['commission_rate'] ?? 0),
    commissionRateOverride:
      row['commission_rate_override'] != null
        ? Number(row['commission_rate_override'])
        : null,
    defaultRemiseTier:
      row['default_remise_tier'] === 'min' ||
      row['default_remise_tier'] === 'max'
        ? row['default_remise_tier']
        : 'moy',
    // Ce mapper sert la fiche d'un document de vente : il ne SELECT pas les
    // colonnes d'email, d'où les valeurs neutres plutôt qu'un cast menteur.
    language:
      row['language'] === 'ar' || row['language'] === 'en'
        ? row['language']
        : 'fr',
    emailOptout: Array.isArray(row['email_optout'])
      ? (row['email_optout'] as NonNullable<
          SalesDocument['proCustomer']
        >['emailOptout'])
      : [],
    approvedAt: (row['approved_at'] as string) ?? null,
    approvedBy: (row['approved_by'] as string) ?? null,
    rejectedReason: (row['rejected_reason'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapProClient(
  row: Record<string, unknown>,
): NonNullable<SalesDocument['proClient']> {
  return {
    id: row['id'] as string,
    tenantId: row['tenant_id'] as string,
    proCustomerId: row['pro_customer_id'] as string,
    nom: row['nom'] as string,
    prenom: (row['prenom'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    telephone: (row['telephone'] as string) ?? null,
    adresse: (row['adresse'] as string) ?? null,
    ville: (row['ville'] as string) ?? null,
    cp: (row['cp'] as string) ?? null,
    complement: (row['complement'] as string) ?? null,
    depense: Number(row['depense'] ?? 0),
    commission: Number(row['commission'] ?? 0),
    soldeCumule: Number(row['solde_cumule'] ?? 0),
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

/**
 * Image de l'article portée par la ligne. Trois pièges, tous couverts ici :
 *  - chemin nu hérité (`234aaaf9-….png`) → résolu en URL publique `erp-images`,
 *    exactement comme `mapArticle` ;
 *  - chaîne VIDE : `?? null` la laisserait passer et produirait `<img src="">`,
 *    d'où le garde explicite au lieu de l'idiome des enrichissements voisins ;
 *  - `image_url` nul mais galerie remplie → on retombe sur `images[0]`.
 * Renvoie `null` sur les chemins de lecture allégés qui ne joignent pas l'image.
 */
function readArticleImageUrl(
  article: Record<string, unknown> | null | undefined,
  opts?: { supabase: TypedSupabaseClient; tenantId: string | null },
): string | null {
  if (!article) return null;
  const resolve = (value: unknown): string | null => {
    if (typeof value !== 'string' || !value.trim()) return null;
    return opts
      ? resolveStorageImageUrl(opts.supabase, value, {
          tenantId: opts.tenantId,
          folder: 'articles',
        })
      : value.trim();
  };
  const primary = resolve(article['image_url']);
  if (primary) return primary;
  for (const entry of (article['images'] as unknown[] | null | undefined) ??
    []) {
    const url = resolve(entry);
    if (url) return url;
  }
  return null;
}

function mapSalesLine(
  row: Record<string, unknown>,
  opts?: { supabase: TypedSupabaseClient; tenantId: string | null },
): SalesDocumentLine {
  const article = row['article'] as Record<string, unknown> | null | undefined;
  return {
    id: row['id'] as string,
    documentId: row['document_id'] as string,
    lineNumber: row['line_number'] as number,
    articleId: (row['article_id'] as string) ?? null,
    variantId: (row['variant_id'] as string) ?? null,
    variantLabel: (row['variant_label'] as string) ?? null,
    unitLabel: (row['unit_label'] as string) ?? null,
    unitFactor: Number(row['unit_factor'] ?? 1) || 1,
    description: row['description'] as string,
    quantity: row['quantity'] as number,
    unitPriceHt: row['unit_price_ht'] as number,
    unitPriceTtc: (row['unit_price_ttc'] as number) ?? null,
    purchasePrice: (row['purchase_price'] as number) ?? 0,
    discountPct: (row['discount_pct'] as number) ?? 0,
    discountMode:
      (row['discount_mode'] as SalesDocumentLine['discountMode']) ?? 'pct',
    discountAmount: Number(row['discount_amount'] ?? 0),
    vatRate: row['vat_rate'] as number,
    totalHt: row['total_ht'] as number,
    totalTtc: row['total_ttc'] as number,
    sourceDocId: (row['source_doc_id'] as string) ?? null,
    sourceLineId: (row['source_line_id'] as string) ?? null,
    articleCode: (article?.['code'] as string) ?? null,
    articleName: (article?.['name'] as string) ?? null,
    articleIsInvoiceable: article
      ? ((article['is_invoiceable'] as boolean | null) ?? true)
      : null,
    articleUnit: (article?.['unit'] as string | null) ?? null,
    articleImageUrl: readArticleImageUrl(article, opts),
  };
}
